import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Clock,
  Truck,
  Box,
  CheckCircle
} from 'lucide-react';
import { 
  supabase, 
  authHeaders,
  fetchAllReceipts,
  fetchListReceiptById,
  verifyDrawerFieldsParity,
  sendMessage,
  fetchReceiptById, 
  deleteReceipt, 
  fetchAllStores, 
  markAllItemStatusesDelivered,
  deliverPartialItems, 
  checkAllItemsDelivered, 
  createPartialDeliveryOrder,
  fetchManufacturers,
  createManufacturer
} from '../supabaseClient';
import { sanitizeReceiptData } from '../utils/sanitize';
import { ITEM_TERMINAL_STATUSES, UNIT_TERMINAL_STATUSES, RECEIPT_TERMINAL_STATUSES, ITEM_ARRIVED_STATUSES } from '../utils/statusConstants';
import { getDashboardTabVisibility } from '../utils/dashboardTabRestrictions';
import { isPhoneAgentEnabledForStore } from '../utils/phoneAgentAccess';
// Deliveries: back for the Testing Store only (per-store allowlist).
import { isDeliveriesEnabledForStore } from '../utils/deliveriesAccess';
import { isChatEnabledForStore } from '../utils/chatAccess';
import {
  clearSiteStorageAndCaches,
  removePushSubscriptionOnLogout,
} from '../utils/logoutCleanup';
// Sales pipeline (Exitium's own selling tool): Testing Store + owner login only.
import { isSalesPipelineEnabled } from '../config/salesPipelineAccess';

// ============================================
// DEV-ONLY LOGGING UTILITY
// Prevents sensitive data from leaking in production logs
// ============================================
const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
const devLog = isDev ? console.log.bind(console) : () => {};
const devError = isDev ? console.error.bind(console) : () => {};
const devWarn = isDev ? console.warn.bind(console) : () => {};

// ============================================
// PUSH NOTIFICATIONS HOOK (embedded)
// ============================================
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

// Owner/testing accounts that keep the retired Marketing Studio (AI video
// generator) — internal use only. This is the single switch: the `campaigns`
// key inside tabVisibility reads it, and nothing in dashboardTabRestrictions
// (JSON or plan config) can widen it. byars.jp5 = Testing Store login.
// Remove the email here to kill access again.
const MARKETING_OWNER_EMAILS = ['byars.jp5@gmail.com'];

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
  
const usePushNotifications = (storeId, userId) => {
  // Initialize from localStorage to persist across reloads
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscription, setPushSubscription] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(() => {
    try {
      return localStorage.getItem('pushEnabled') === 'true';
    } catch { return false; }
  });
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);

  // Check support
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setPushSupported(supported);
    devLog('[Push] Supported:', supported);
  }, []);

  useEffect(() => {
    if (!storeId && !userId) {
      setPushSubscription(null);
      setPushEnabled(false);
    }
  }, [storeId, userId]);

  // Register service worker and check existing subscription
  useEffect(() => {
    if (!pushSupported) return;
    
    const initServiceWorker = async () => {
      try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw-push.js', { scope: '/' });
        devLog('[Push] Service worker registered');
        
        // Wait for it to be ready
        const swReg = await navigator.serviceWorker.ready;
        devLog('[Push] Service worker ready');
        setServiceWorkerReady(true);
        
        // Check for existing subscription
        const existingSubscription = await swReg.pushManager.getSubscription();
        devLog('[Push] Existing subscription:', existingSubscription ? 'found' : 'none');
        
        if (existingSubscription) {
          setPushSubscription(existingSubscription);
          setPushEnabled(true);
          localStorage.setItem('pushEnabled', 'true');
        } else {
          // No subscription found - check if we thought we had one
          const wasEnabled = localStorage.getItem('pushEnabled') === 'true';
          if (wasEnabled) {
            devLog('[Push] Was enabled but subscription lost, resetting state');
            localStorage.setItem('pushEnabled', 'false');
            setPushEnabled(false);
          }
        }
      } catch (error) {
        devError('[Push] SW registration failed:', error);
        setPushError(error.message);
      }
    };
    
    initServiceWorker();
  }, [pushSupported]);

  // Listen for service worker messages
  useEffect(() => {
    if (!serviceWorkerReady) return;
    const handleMessage = (event) => {
      if (event.data?.type === 'OPEN_RECEIPT') {
        window.dispatchEvent(new CustomEvent('openReceiptFromPush', {
          detail: { receiptId: event.data.receiptId, isEscalation: event.data.isEscalation }
        }));
      }
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [serviceWorkerReady]);

  const saveSubscriptionToDb = async (subscription, storeId, userId) => {
    const subJson = subscription.toJSON();
    const { error } = await supabase.from('push_subscriptions').upsert({
      endpoint: subJson.endpoint, 
      p256dh: subJson.keys.p256dh, 
      auth: subJson.keys.auth,
      store_id: storeId, 
      user_id: userId, 
      user_agent: navigator.userAgent, 
      updated_at: new Date().toISOString()
    }, { onConflict: 'endpoint' });
    if (error) devError('[Push] DB save error:', error);
  };

  const subscribeToPush = useCallback(async () => {
    if (!pushSupported || !serviceWorkerReady) {
      devLog('[Push] Cannot subscribe - not ready');
      return false;
    }
    if (!VAPID_PUBLIC_KEY) {
      setPushError('VAPID key not configured');
      return false;
    }
    
    setPushLoading(true);
    setPushError(null);
    
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      devLog('[Push] Permission:', permission);
      
      if (permission !== 'granted') { 
        setPushError('Permission denied'); 
        setPushLoading(false); 
        return false; 
      }
      
      // Get service worker and subscribe
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true, 
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      
      devLog('[Push] Subscribed successfully');
      
      // Save to database
      await saveSubscriptionToDb(subscription, storeId, userId);
      
      // Update state
      setPushSubscription(subscription);
      setPushEnabled(true);
      localStorage.setItem('pushEnabled', 'true');
      setPushLoading(false);
      
      return true;
    } catch (error) {
      devError('[Push] Subscribe error:', error);
      setPushError(error.message);
      setPushLoading(false);
      return false;
    }
  }, [pushSupported, serviceWorkerReady, storeId, userId]);

  const unsubscribeFromPush = useCallback(async () => {
    setPushLoading(true);
    
    try {
      // Get current subscription
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();
        
        // Remove from database
        const subJson = subscription.toJSON();
        await supabase.from('push_subscriptions').delete().eq('endpoint', subJson.endpoint);
      }
      
      // Update state
      setPushSubscription(null);
      setPushEnabled(false);
      localStorage.setItem('pushEnabled', 'false');
      setPushLoading(false);
      
      devLog('[Push] Unsubscribed');
      return true;
    } catch (error) {
      devError('[Push] Unsubscribe error:', error);
      setPushError(error.message);
      setPushLoading(false);
      return false;
    }
  }, []);

  const togglePush = useCallback(async () => {
    return pushEnabled ? await unsubscribeFromPush() : await subscribeToPush();
  }, [pushEnabled, subscribeToPush, unsubscribeFromPush]);

  const testPush = useCallback(async () => {
    if (!pushSubscription) return false;
    try {
      const res = await fetch('/api/push/test', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ endpoint: pushSubscription.endpoint }) });
      return (await res.json()).success;
    } catch { return false; }
  }, [pushSubscription]);

  return { pushSupported, pushEnabled, pushLoading, pushError, togglePush, testPush };
};

// PWA utilities
export const isPWAInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true;
};

export const installPWA = async () => {
  if (!window.deferredPrompt) return false;
  window.deferredPrompt.prompt();
  const { outcome } = await window.deferredPrompt.userChoice;
  if (outcome === 'accepted') window.deferredPrompt = null;
  return outcome === 'accepted';
};

export const isOnline = () => navigator.onLine;

/** @param {object} receipt */
function getReceiptCreatedAt(receipt) {
  const raw = receipt?.createdAt ?? receipt?.created_at;
  if (raw == null) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** @param {string} yyyymm `YYYY-MM` from `<input type="month" />` */
function isDateInCalendarMonthYear(d, yyyymm) {
  if (!yyyymm || !/^\d{4}-\d{2}$/.test(yyyymm)) return false;
  const [ys, ms] = yyyymm.split('-');
  const year = Number(ys);
  const monthIndex = Number(ms) - 1;
  if (monthIndex < 0 || monthIndex > 11) return false;
  return d.getFullYear() === year && d.getMonth() === monthIndex;
}

/** Sunday–Saturday week in local time that contains "now". */
function isDateInCurrentCalendarWeek(d) {
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  return d >= startOfWeek && d <= endOfWeek;
}

/**
 * Custom hook containing all state and logic for the Receipt Dashboard
 * Used by both ReceiptDashboardMobile and ReceiptDashboardDesktop
 */
export const useReceiptDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Real data loaded from Supabase - no demo data in production
  const [receipts, setReceipts] = useState([]);
  const receiptsRef = useRef([]);

  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [expandedSkuGroups, setExpandedSkuGroups] = useState({}); // Track which SKU groups are expanded in receipt view
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [isEditingInModal, setIsEditingInModal] = useState(false); // Edit mode toggle in receipt detail
  // Ref mirror of isEditingInModal so async continuations (e.g. the receipt
  // hydration in handleOpenReceipt) can check the CURRENT edit state instead
  // of a stale closure value — prevents clobbering in-progress edits.
  const isEditingInModalRef = useRef(false);
  useEffect(() => {
    isEditingInModalRef.current = isEditingInModal;
  }, [isEditingInModal]);
  const [editFormData, setEditFormData] = useState(null); // Form data for editing in detail modal
  // PER-ITEM status-write busy map: { [receiptItemId]: statusKey }. Set while
  // a status pill's write (ready / waiting / out_for_delivery / at_store /
  // delivered / picked_up) is in flight so THAT pill can show a spinner and
  // the row's pill group can lock. Complements (does not replace) the global
  // markingItemReady flag other UI still consumes.
  const [itemStatusBusy, setItemStatusBusy] = useState({});
  const setItemBusy = (itemId, statusKey) => {
    if (!itemId) return;
    setItemStatusBusy(prev => ({ ...prev, [itemId]: statusKey }));
  };
  const clearItemBusy = (itemId) => {
    if (!itemId) return;
    setItemStatusBusy(prev => {
      if (!(itemId in prev)) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };
  // True from the instant the receipt drawer opens until the full receipt
  // (messages, inventory units, set fields) has been fetched — the drawer
  // shows its frame immediately and spinners fill the still-loading areas.
  const [isLoadingReceiptDetails, setIsLoadingReceiptDetails] = useState(false);
  // Monotonic token guarding handleOpenReceipt's async hydration: if the user
  // opens receipt B (or closes the drawer) while receipt A is still fetching,
  // A's late response must not overwrite B's state.
  const openReceiptTokenRef = useRef(0);
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false); // Warning dialog when closing with unsaved changes
  const [showEditModalUnsavedWarning, setShowEditModalUnsavedWarning] = useState(false); // Warning dialog for full edit modal
  const [showDeleteWarning, setShowDeleteWarning] = useState(false); // Warning dialog for delete confirmation
  const [deleteTargetId, setDeleteTargetId] = useState(null); // ID of receipt to delete
  const [staffMessage, setStaffMessage] = useState('');
  const [staffImage, setStaffImage] = useState(null); // For MMS image attachments
  const [isStaffMode, setIsStaffMode] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return (tab === 'new' || tab === 'completed') ? tab : 'new';
  }); // 'new' or 'completed'
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(''); // Search for receipts
  const [receiptPeriodFilter, setReceiptPeriodFilter] = useState(
    /** @type {'all' | 'week' | 'month'} */ ('all'),
  );
  const [receiptSelectedMonth, setReceiptSelectedMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });

  // === DOUBLE-CLICK PREVENTION STATES ===
  const [isSavingReceipt, setIsSavingReceipt] = useState(false);
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [sendingSMSReceiptId, setSendingSMSReceiptId] = useState(null); // Track which specific receipt is sending
  const [smsSentReceipts, setSmsSentReceipts] = useState({}); // Track receipts showing "Sent ✓" state { receiptId: true }
  const [isDeletingReceipt, setIsDeletingReceipt] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const [isTakingOver, setIsTakingOver] = useState(false);
  const [isReturningToAI, setIsReturningToAI] = useState(false);
  const [isMobileEditingDetails, setIsMobileEditingDetails] = useState(false); // Mobile inline editing for customer details
  const [togglingChatModeId, setTogglingChatModeId] = useState(null); // Track which receipt's chat mode is being toggled

  // PWA state
  const [online, setOnline] = useState(true);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // ==========================================
  // INVENTORY MANAGEMENT STATE - NEW
  // ==========================================
  const [showScanner, setShowScanner] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [activeView, setActiveView] = useState(() => {
    const view = searchParams.get('view');
    // The three live surfaces + 'campaigns' (owner-only Marketing Studio —
    // the hidden-tab bounce effect below kicks anyone not on
    // MARKETING_OWNER_EMAILS back to Orders). 'deliveries' is back for the
    // Testing Store only (deliveriesAccess.js); the hidden-tab bounce below
    // sends any other store's ?view=deliveries to Orders. 'inventory'
    // remains removed (July 2026); stale deep links carrying it land on
    // Orders.
    return (view === 'orders' || view === 'reviews' || view === 'phoneAgent' || view === 'campaigns' || view === 'contacts' || view === 'sales' || view === 'deliveries' || view === 'chat') ? view : 'orders';
  }); // 'orders' | 'reviews' | 'phoneAgent' | 'campaigns' | 'contacts' | 'sales' | 'deliveries'
  const [inventoryRefreshTrigger, setInventoryRefreshTrigger] = useState(0); // Triggers InventoryTab refresh when incremented
  const [selectedMobileInvoice, setSelectedMobileInvoice] = useState(null);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
  
  // ==========================================
  // PARTIAL DELIVERY STATE - NEW
  // ==========================================
  const [showPartialDeliveryModal, setShowPartialDeliveryModal] = useState(false);
  const [partialDeliveryReceipt, setPartialDeliveryReceipt] = useState(null);
  const [selectedDeliveryItems, setSelectedDeliveryItems] = useState({}); // { sku: boolean }
  const [isProcessingPartialDelivery, setIsProcessingPartialDelivery] = useState(false);
  const [isUndoingDelivery, setIsUndoingDelivery] = useState(null); // Track which item is being undone (item.id)
  
  // ==========================================
  // RECEIPT DETAIL - ITEM SELECTION FOR DELIVERY ORDER
  // ==========================================
  const [selectedItemsForDelivery, setSelectedItemsForDelivery] = useState({}); // { itemId: boolean } - FIX: use item.id not sku
  const [isCreatingDeliveryFromReceipt, setIsCreatingDeliveryFromReceipt] = useState(false);
  
  // ==========================================
  // MULTI-STORE STATE - NEW
  // ==========================================
  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  // Store-level emergency SMS-AI kill switch. When true, the SMS responder
  // must NOT auto-reply to ANY customer (current or future conversations),
  // regardless of each receipt's own is_ai_handling flag. Distinct from the
  // per-receipt "take over" toggle: this is the global "stop all AI replies
  // now" switch. Mirrored from stores.sms_ai_paused; toggled via
  // toggleSmsAiPaused below.
  const [smsAiPaused, setSmsAiPaused] = useState(false);
  const [togglingSmsAi, setTogglingSmsAi] = useState(false);
  const [storeSettings, setStoreSettings] = useState({
    tax_rate: 8.00 // Default 8% tax rate
  });
  const [storeAddresses, setStoreAddresses] = useState([]); // Store addresses for receipts
  const [manufacturers, setManufacturers] = useState([]); // Manufacturer lookup for current store

  // ==========================================
  // PHONE AGENT SETTINGS (per current store)
  // ==========================================
  const [phoneAgentSettings, setPhoneAgentSettings] = useState({
    bland_phone_number: '',
    tool_auth_token: '',
    sms_forward_number: '',
  });

  // ==========================================
  // SIGNATURES DRAWER STATE - NEW
  // ==========================================
  const [showSignaturesDrawer, setShowSignaturesDrawer] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  
  // ==========================================
  // GLOBAL INVENTORY STATE (for readiness checks)
  // ==========================================
  const [allInventoryUnits, setAllInventoryUnits] = useState([]);
  
  // ==========================================
  // RECEIPT SCANNER STATE
  // ==========================================
  
  // ==========================================
  // NOTIFICATION SYSTEM STATE
  // ==========================================
  const [notifications, setNotifications] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState({});  // { receiptId: count }
  const [totalUnread, setTotalUnread] = useState(0);
  const [notificationSound, setNotificationSound] = useState(true);
  const [browserNotifications, setBrowserNotifications] = useState(false);
  const [showStoreSettings, setShowStoreSettings] = useState(false);
  const [showSalesTaxExportModal, setShowSalesTaxExportModal] = useState(false);
  
  // ESCALATION STATE - urgent notifications that need human attention
  const [escalations, setEscalations] = useState({});  // { receiptId: { reason, timestamp, acknowledged } }
  
  // Audio ref for notification sound
  const notificationAudioRef = useRef(null);
  const chatContainerRef = useRef(null);
  const mobileChatContainerRef = useRef(null);

  // --- Refs mirroring volatile values so realtime subscriptions can read the
  //     latest value WITHOUT being torn down + re-created. Re-creating them was
  //     re-running initializeData() and re-fetching all receipts repeatedly. ---
  const selectedReceiptRef = useRef(null);
  const notificationSoundRef = useRef(true);
  const browserNotificationsRef = useRef(false);
  const currentStoreRef = useRef(null);
  const refreshDebounceRef = useRef(null);
  // Receipt-list load bookkeeping (see loadReceipts / refreshReceiptsInBackground)
  const listLoadSeqRef = useRef(0);        // bumps on every load; only the newest may apply
  const listLoadsInFlightRef = useRef(0);  // loads currently running
  const lastListLoadRef = useRef(0);       // when a load last applied its result
  const lastListLoadStartRef = useRef(0);  // when the most recent load started
  useEffect(() => { selectedReceiptRef.current = selectedReceipt; });
  useEffect(() => { notificationSoundRef.current = notificationSound; });
  useEffect(() => { browserNotificationsRef.current = browserNotifications; });
  useEffect(() => { currentStoreRef.current = currentStore; });

  // Keep the SMS-AI kill switch in sync with the loaded store. currentStore is
  // the full stores row (fetchAllStores selects *), so the column rides along.
  // Treat only an explicit true as paused.
  useEffect(() => {
    setSmsAiPaused(currentStore?.sms_ai_paused === true);
  }, [currentStore?.id, currentStore?.sms_ai_paused]);

  // Flip stores.sms_ai_paused. Optimistic with rollback on failure, mirroring
  // the phone-agent kill switch. The SMS responder backend reads this column
  // before auto-replying — see note where it's enforced. Returns nothing;
  // throws on failure so a caller (e.g. a UI toggle) can surface an error.
  const toggleSmsAiPaused = async (nextPaused) => {
    const store = currentStoreRef.current;
    if (!store?.id) throw new Error('No store selected');
    if (togglingSmsAi) return;
    setTogglingSmsAi(true);
    setSmsAiPaused(nextPaused); // optimistic
    try {
      const { error } = await supabase
        .from('stores')
        .update({ sms_ai_paused: nextPaused })
        .eq('id', store.id);
      if (error) throw error;
      // Reflect on the in-memory store objects so a re-select stays correct.
      setCurrentStore(prev => prev ? { ...prev, sms_ai_paused: nextPaused } : prev);
      setStores(prev => Array.isArray(prev)
        ? prev.map(s => s.id === store.id ? { ...s, sms_ai_paused: nextPaused } : s)
        : prev);
      devLog(`✅ SMS-AI ${nextPaused ? 'PAUSED' : 'resumed'} for store ${store.id}`);
    } catch (err) {
      setSmsAiPaused(!nextPaused); // rollback
      devError('Error toggling SMS-AI kill switch:', err);
      throw err;
    } finally {
      setTogglingSmsAi(false);
    }
  };
  
  // Supabase Auth state
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'


  // ==========================================
  // LOAD PHONE AGENT SETTINGS (per store)
  // bland_phone_number, tool_auth_token, sms_forward_number
  // ==========================================
  const loadPhoneAgentSettings = useCallback(async (storeId) => {
    if (!storeId) {
      setPhoneAgentSettings({
        bland_phone_number: '',
        tool_auth_token: '',
        sms_forward_number: '',
      });
      return;
    }
    try {
      const { data, error } = await supabase
        .from('phone_agent_settings')
        .select('bland_phone_number, tool_auth_token, sms_forward_number, is_active, agent_tier, voice')
        .eq('store_id', storeId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows; anything else is a real error worth logging
        devError('Error loading phone_agent_settings:', error);
      }

      setPhoneAgentSettings({
        bland_phone_number: data?.bland_phone_number || '',
        tool_auth_token: data?.tool_auth_token || '',
        sms_forward_number: data?.sms_forward_number || '',
        voice: data?.voice || 'june',
      });
    } catch (err) {
      devError('Error loading phone_agent_settings:', err);
      setPhoneAgentSettings({
        bland_phone_number: '',
        tool_auth_token: '',
        sms_forward_number: '',
      });
    }
  }, []);
  // ============================================================
  // PhoneAgentTab wiring bundle — spread this onto <PhoneAgentTab>:
  //     <PhoneAgentTab {...phoneAgentTabProps} ...existingProps />
  // One spread wires everything that was pending: voice save, visit
  // status (Confirm / Came in / Dismiss), lead status (Contacted /
  // Came in), and older-calls pagination. Uses this hook's own session
  // bearer + currentStore conventions (same pattern as /api/push/dismiss).
  // ============================================================
  const _paAuthHeaders = () => ({
    'Content-Type': 'application/json',
    ...(session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : {}),
  });

  const _paPatchCall = async (body, label) => {
    const res = await fetch('/api/phone-agents-2/calls', {
      method: 'PATCH',
      headers: _paAuthHeaders(),
      body: JSON.stringify({ store_id: currentStore?.id, ...body }),
    });
    if (!res.ok) {
      let msg = `${label} failed`;
      try { msg = (await res.json()).error || msg; } catch { /* keep msg */ }
      throw new Error(msg);
    }
  };

  const phoneAgentTabProps = {
    initialVoice: phoneAgentSettings.voice || 'june',
    onSaveVoice: async (voiceId) => {
      const res = await fetch('/api/phone-agents-2/config', {
        method: 'POST',
        headers: _paAuthHeaders(),
        body: JSON.stringify({ store_id: currentStore?.id, voice: voiceId }),
      });
      if (!res.ok) {
        let msg = 'Voice save failed';
        try { msg = (await res.json()).error || msg; } catch { /* keep msg */ }
        throw new Error(msg);
      }
      setPhoneAgentSettings((prev) => ({ ...prev, voice: voiceId }));
    },
    onUpdateVisit: (callId, { status }) =>
      _paPatchCall({ call_id: callId, visit_status: status }, 'Visit update'),
    onUpdateLead: (callId, { status }) =>
      _paPatchCall({ call_id: callId, lead_status: status }, 'Lead update'),
    // These two were never passed, so "Done" on a promised callback and
    // "Confirm window" on a delivery only changed local state and came
    // back on the next refresh. calls.py already accepts both fields.
    onUpdateCallback: (callId, { done }) =>
      _paPatchCall({ call_id: callId, callback_done: !!done }, 'Callback update'),
    onUpdateDelivery: (callId, { status }) =>
      _paPatchCall({ call_id: callId, delivery_status: status }, 'Delivery update'),
    onFetchOlderCalls: async (beforeIso) => {
      const res = await fetch(
        `/api/phone-agents-2/calls?store_id=${currentStore?.id}` +
          `&limit=50&before=${encodeURIComponent(beforeIso)}`,
        { headers: _paAuthHeaders() }
      );
      if (!res.ok) return [];
      return (await res.json()).calls || [];
    },
  };

  const tabVisibility = useMemo(
    () => ({
      ...getDashboardTabVisibility(session?.user, {
        plan_number: currentStore?.plan_number,
        payment_status: currentStore?.payment_status,
      }),
      // KILLED SURFACES — July 2026. Inventory is out of the product.
      // Hard-pinned false AFTER the spread so no plan/user combination from
      // dashboardTabRestrictions can resurface it — nav items, view routes,
      // deep links, and the canRoute / campaignsTabEnabled consumers all key
      // off this map.
      // The AI Flyer Builder now lives inside PhoneAgentTab (Current Deals).
      inventory: false,
      // Deliveries — back for the Testing Store ONLY (deliveriesAccess.js).
      // Per-store allowlist set AFTER the spread, so the JSON overrides and
      // plan numbers that still say deliveries: true for live stores are
      // ignored. A code commit is the only way to widen it.
      deliveries: isDeliveriesEnabledForStore(currentStore?.id),
      // Chat — one inbox per person (contacts). New 2026-11-01, Testing
      // Store only while it is checked (chatAccess.js). Same strict rule.
      chat: isChatEnabledForStore(currentStore?.id),
      // Marketing Studio: retired for customers, kept ONLY for the
      // owner/testing login via the hardcoded MARKETING_OWNER_EMAILS
      // allowlist above. Still ignores the JSON/plan config on purpose —
      // a code commit is the only way to widen access.
      campaigns: MARKETING_OWNER_EMAILS.includes(
        (session?.user?.email || '').trim().toLowerCase(),
      ),
      phoneAgent: isPhoneAgentEnabledForStore(currentStore?.id),
      // Contacts — the store's text list (opted in, leads, opted out). Every
      // store: the website's sales desk writes to it as shoppers are seated.
      contacts: true,
      // Sales — Exitium's pipeline for selling Exitium to furniture stores.
      // Strict per-store AND per-user gate (src/config/salesPipelineAccess.js):
      // only the Testing Store on the owner login. Live stores are always
      // false here, so the nav item, the view and ?view=sales deep links
      // never render for them. A code commit is the only way to widen it.
      sales: isSalesPipelineEnabled(currentStore?.id, session?.user?.email),
    }),
    [
      session?.user?.id,
      session?.user?.email,
      currentStore?.id,
      currentStore?.plan_number,
      currentStore?.payment_status,
    ],
  );

  const visibleTabCount = useMemo(
    () =>
      Object.values(tabVisibility).filter(Boolean).length,
    [tabVisibility],
  );

  // ==========================================
  // PUSH NOTIFICATIONS
  // ==========================================
  const pushNotifications = usePushNotifications(currentStore?.id, session?.user?.id);

  // Listen for push notification clicks to open receipts
  useEffect(() => {
    const handleOpenReceiptFromPush = (event) => {
      const { receiptId } = event.detail;
      const receipt = receipts.find(r => r.id === receiptId);
      if (receipt) {
        handleViewReceipt(receipt);
      }
    };
    window.addEventListener('openReceiptFromPush', handleOpenReceiptFromPush);
    return () => window.removeEventListener('openReceiptFromPush', handleOpenReceiptFromPush);
  }, [receipts]);

  // Keep receiptsRef in sync for realtime callbacks (avoids stale closure)
  useEffect(() => { receiptsRef.current = receipts; }, [receipts]);

  // Sync URL with current view and tab state (for refresh persistence)
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', activeView);
    newParams.set('tab', activeTab);
    setSearchParams(newParams, { replace: true }); // replace: true prevents adding to history on every change
  }, [activeView, activeTab]);

  // Hidden tabs cannot be active (URL deep-links included)
  useEffect(() => {
    if (activeView === 'phoneAgent' && tabVisibility.phoneAgent !== false) return;
    if (tabVisibility[activeView]) return;
    const priority = ['orders', 'reviews', 'phoneAgent'];
    const next = priority.find((v) => tabVisibility[v]);
    if (next) setActiveView(next);
  }, [tabVisibility, activeView, setActiveView]);

  // PWA setup
  useEffect(() => {
    setIsInstalled(isPWAInstalled());
    setOnline(isOnline());

    // Set theme color for PWA status bar
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', '#0a0a0f');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = '#0a0a0f';
      document.head.appendChild(meta);
    }
    
    // Also set apple status bar style
    const appleStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (!appleStatusBar) {
      const meta = document.createElement('meta');
      meta.name = 'apple-mobile-web-app-status-bar-style';
      meta.content = 'black-translucent';
      document.head.appendChild(meta);
    }

    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setShowInstallPrompt(true);
    };
    
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      window.deferredPrompt = null;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // ==========================================
  // SUPABASE AUTH SESSION CHECK
  // ==========================================
  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setAuthLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // ==========================================
  // NOTIFICATION FUNCTIONS
  // ==========================================
  const playNotificationSound = () => {
    if (notificationSoundRef.current && notificationAudioRef.current) {
      notificationAudioRef.current.currentTime = 0;
      notificationAudioRef.current.play().catch(e => devLog('Audio play failed:', e));
    }
  };

  const showBrowserNotification = (title, body, receiptId) => {
    if (browserNotifications && 'Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: `receipt-${receiptId}`,
        requireInteraction: true
      });
      
      notification.onclick = () => {
        window.focus();
        // Open the receipt
        const receipt = receipts.find(r => r.id === receiptId);
        if (receipt) {
          handleViewReceipt(receipt);
        }
        notification.close();
      };
    }
  };

  const addNotification = (notification) => {
    const id = Date.now();
    const newNotification = { ...notification, id, timestamp: new Date() };
    setNotifications(prev => [newNotification, ...prev].slice(0, 10)); // Keep last 10
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const markReceiptAsRead = (receiptId) => {
    setUnreadMessages(prev => {
      const newUnread = { ...prev };
      delete newUnread[receiptId];
      return newUnread;
    });
    updateTotalUnread();
  };

  const updateTotalUnread = () => {
    setTotalUnread(Object.values(unreadMessages).reduce((sum, count) => sum + count, 0));
  };

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setBrowserNotifications(permission === 'granted');
      return permission === 'granted';
    }
    return false;
  };

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'granted') {
      setBrowserNotifications(true);
    }
  }, []);

  // Update total unread when unreadMessages changes
  useEffect(() => {
    const total = Object.values(unreadMessages).reduce((sum, count) => sum + count, 0);
    setTotalUnread(total);
  }, [unreadMessages]);

  // Track if user is near bottom of chat (to know if we should auto-scroll)
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const lastMessageCountRef = useRef(0);
  const initialScrollDoneRef = useRef(false);

  // Handle chat scroll to detect if user scrolled up
  const handleChatScroll = (e) => {
    const container = e.target;
    const threshold = 100; // pixels from bottom
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    setUserScrolledUp(!isNearBottom);
  };

  // Auto-scroll chat to newest message - only on initial load or new messages when at bottom
  useEffect(() => {
    const messages = selectedReceipt?.messages || [];
    const messageCount = messages.length;
    
    const scrollToBottom = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
      if (mobileChatContainerRef.current) {
        mobileChatContainerRef.current.scrollTop = mobileChatContainerRef.current.scrollHeight;
      }
    };
    
    // Scroll to bottom on initial open
    if ((showReceiptModal || isMobileChatOpen) && !initialScrollDoneRef.current) {
      const timeoutId = setTimeout(() => {
        scrollToBottom();
        initialScrollDoneRef.current = true;
        lastMessageCountRef.current = messageCount;
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    
    // Scroll to bottom when new message arrives AND user is not scrolled up
    if (messageCount > lastMessageCountRef.current && !userScrolledUp) {
      const timeoutId = setTimeout(scrollToBottom, 100);
      lastMessageCountRef.current = messageCount;
      return () => clearTimeout(timeoutId);
    }
    
    lastMessageCountRef.current = messageCount;
  }, [selectedReceipt?.messages, showReceiptModal, isMobileChatOpen, userScrolledUp]);

  // Reset scroll tracking when modal closes
  useEffect(() => {
    if (!showReceiptModal && !isMobileChatOpen) {
      initialScrollDoneRef.current = false;
      setUserScrolledUp(false);
    }
  }, [showReceiptModal, isMobileChatOpen]);

  // LOAD STORES AND RECEIPTS FROM SUPABASE ON MOUNT OR USER CHANGE
  // ---- DATA INIT: runs ONCE per logged-in user. Critically, this no longer
  //      depends on selectedReceipt / notificationSound / browserNotifications,
  //      which were causing the whole boot sequence (and a full 145-receipt
  //      refetch) to re-run on every receipt open/close and setting toggle. ----
  useEffect(() => {
    if (!session?.user?.id) {
      devLog('\u23f3 Waiting for session before loading data...');
      return;
    }

    devLog('\ud83d\udd04 Initializing data for user:', session.user.email);

    const initializeData = async () => {
      // Load stores first (RLS will filter to user's stores only)
      const storesData = await fetchAllStores();
      setStores(storesData);

      // Check localStorage for previously selected store
      if (storesData.length > 0) {
        const savedStoreId = localStorage.getItem('selectedStoreId');
        const savedStore = savedStoreId
          ? storesData.find(s => s.id === savedStoreId)
          : null;

        // Use saved store or fall back to first store
        const storeToUse = savedStore || storesData[0];
        setCurrentStore(storeToUse);
        // Load receipts, inventory, and store settings for selected store
        await loadReceipts(storeToUse.id);
        await loadInventoryUnits(storeToUse.id);
        await loadStoreSettings(storeToUse.id);
        await loadStoreAddresses(storeToUse.id);
        await loadPhoneAgentSettings(storeToUse.id);
        // Load manufacturer lookup for item forms
        const mfrs = await fetchManufacturers(storeToUse.id);
        setManufacturers(mfrs);
      } else {
        // No stores, load all receipts
        await loadReceipts();
        await loadInventoryUnits();
      }

      loadInvoices();
    };

    initializeData();
  }, [session?.user?.id]);

  // ---- REAL-TIME MESSAGE SUBSCRIPTION: set up ONCE per user. Reads volatile
  //      values via refs so it never has to tear down + re-subscribe (which
  //      previously re-triggered initializeData). ----
  useEffect(() => {
    if (!session?.user?.id) return;

    // Coalesce bursts of message events into at most one silent refetch,
    // skipped if the list was refreshed in the last 30s (the new message
    // itself is already patched into the list below).
    const debouncedReload = () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      refreshDebounceRef.current = setTimeout(() => {
        refreshReceiptsInBackground(30000);
      }, 1500);
    };

    // Reflect a new chat message on its receipt in the list right away,
    // the same way fetchAllReceipts counts them: its .neq('message_type',
    // 'survey') is SQL <>, which also drops rows whose message_type is null.
    const addMessageToList = (msg) => {
      if (!msg?.receipt_id || msg.message_type == null || msg.message_type === 'survey') return;
      setReceipts(prev => prev.map(r => {
        if (r.id !== msg.receipt_id) return r;
        const messages = r.messages || [];
        if (messages.some(m => m.id === msg.id)) return r;
        return { ...r, messages: [...messages, { id: msg.id }], hasActiveChat: true };
      }));
    };

    const messagesChannel = supabase
      .channel('dashboard-messages')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        async (payload) => {
          devLog('\ud83d\udd14 NEW MESSAGE:', payload);
          const newMessage = payload.new;

          // Check if this is an escalation (AI flagged for human attention)
          if (newMessage.escalation_needed && newMessage.role === 'assistant') {
            devLog('\ud83d\udea8 ESCALATION DETECTED:', newMessage.escalation_reason);

            // Get receipt info for the notification
            const { data: receipt } = await supabase
              .from('receipts')
              .select('customer_name, customer_phone')
              .eq('id', newMessage.receipt_id)
              .single();

            const customerName = receipt?.customer_name || 'Customer';

            // Add to escalations state
            setEscalations(prev => ({
              ...prev,
              [newMessage.receipt_id]: {
                reason: newMessage.escalation_reason || 'Needs human attention',
                timestamp: new Date(),
                acknowledged: false,
                customerName
              }
            }));

            // Play urgent sound (double beep)
            playNotificationSound();
            setTimeout(() => playNotificationSound(), 300);

            // Show urgent toast notification
            addNotification({
              type: 'escalation',
              title: '\ud83d\udea8 ESCALATION NEEDED',
              message: `${customerName}: ${newMessage.escalation_reason || 'Needs attention'}`,
              receiptId: newMessage.receipt_id,
              customerName,
              isEscalation: true
            });

            // Browser notification with urgency
            if (browserNotificationsRef.current && Notification.permission === 'granted') {
              new Notification('\ud83d\udea8 URGENT: Customer Escalation', {
                body: `${customerName}: ${newMessage.escalation_reason || 'Needs human attention'}`,
                icon: '/favicon.ico',
                requireInteraction: true,
                tag: `escalation-${newMessage.receipt_id}`
              });
            }
          }

          // Only notify for customer messages (role === 'user')
          if (newMessage.role === 'user') {
            // Get receipt info for the notification
            const { data: receipt } = await supabase
              .from('receipts')
              .select('customer_name, customer_phone')
              .eq('id', newMessage.receipt_id)
              .single();

            const customerName = receipt?.customer_name || 'Customer';

            // Add to unread count
            setUnreadMessages(prev => ({
              ...prev,
              [newMessage.receipt_id]: (prev[newMessage.receipt_id] || 0) + 1
            }));

            // Show toast notification
            addNotification({
              type: 'message',
              title: `New message from ${customerName}`,
              message: newMessage.content?.substring(0, 50) + (newMessage.content?.length > 50 ? '...' : ''),
              receiptId: newMessage.receipt_id,
              customerName
            });

            // Play sound
            playNotificationSound();

            // Show browser notification
            showBrowserNotification(
              `New message from ${customerName}`,
              newMessage.content?.substring(0, 100),
              newMessage.receipt_id
            );
          }

          // Was: loadReceipts() on every message (full 145-row refetch each time).
          // Now: patch this receipt in place, then at most one throttled reload.
          addMessageToList(newMessage);
          debouncedReload();

          // If modal is open for this receipt, reload it
          const sel = selectedReceiptRef.current;
          if (sel && sel.id === newMessage.receipt_id) {
            loadReceiptDetails(sel.id);
            // If viewing this receipt, mark as read
            markReceiptAsRead(newMessage.receipt_id);
          }
        }
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages' },
        () => {
          // Intentionally NOT refetching all receipts on every message update.
          const sel = selectedReceiptRef.current;
          if (sel) {
            loadReceiptDetails(sel.id);
          }
        }
      )
      .subscribe((status) => {
        devLog('Subscription status:', status);
      });

    return () => {
      devLog('Unsubscribing from messages channel');
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      supabase.removeChannel(messagesChannel);
    };
  }, [session?.user?.id]);

  // REAL-TIME RECEIPT STATUS SUBSCRIPTION
  // Auto-refresh when deliveries are completed from DriverView
  useEffect(() => {
    devLog('🔌 Setting up receipts realtime subscription...');
    
    const receiptsChannel = supabase
      .channel('dashboard-receipts')
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'receipts' },
        (payload) => {
          devLog('📦 Receipt updated via realtime:', payload);
          const updatedReceipt = payload.new;
          
          // Check if delivery_status changed to 'delivered'
          if (updatedReceipt.delivery_status === 'delivered') {
            devLog('✅ Receipt marked as delivered');
            // Play notification sound if enabled
            if (notificationSound) {
              try {
                const audio = new Audio('/notification.mp3');
                audio.volume = 0.5;
                audio.play().catch(() => {});
              } catch (e) {}
            }
          }
          
          // ===== SURVEY COMPLETION NOTIFICATION + ESCALATION =====
          // Check if a customer satisfaction score was just set
          if (updatedReceipt.customer_satisfaction_score) {
            // Compare against current state to avoid re-notifying on every receipt update
            const existingReceipt = receiptsRef.current.find(r => r.id === updatedReceipt.id);
            const isNewScore = !existingReceipt?.customer_satisfaction_score && updatedReceipt.customer_satisfaction_score;
            const scoreChanged = existingReceipt?.customer_satisfaction_score !== updatedReceipt.customer_satisfaction_score;
            
            if (isNewScore || scoreChanged) {
              const score = updatedReceipt.customer_satisfaction_score;
              const customerName = updatedReceipt.customer_name || existingReceipt?.customer_name || 'Customer';
              const feedbackText = updatedReceipt.customer_feedback_text || '';
              
              devLog(`⭐ SURVEY COMPLETED: ${customerName} rated ${score}/5`);
              
              // BAD RATING ESCALATION (score <= 2)
              if (score <= 2) {
                devLog('🚨 LOW RATING ESCALATION:', customerName, score);
                
                const escalationReason = `Low satisfaction rating: ${score}/5${feedbackText ? ` - "${feedbackText}"` : ''}`;
                
                // Add to escalations state
                setEscalations(prev => ({
                  ...prev,
                  [updatedReceipt.id]: {
                    reason: escalationReason,
                    timestamp: new Date(),
                    acknowledged: false,
                    customerName,
                    type: 'low_rating'
                  }
                }));
                
                // Play urgent sound (double beep)
                playNotificationSound();
                setTimeout(() => playNotificationSound(), 300);
                
                // Show urgent toast notification
                addNotification({
                  type: 'escalation',
                  title: '🚨 LOW RATING - ACTION NEEDED',
                  message: `${customerName} rated ${score}/5${feedbackText ? `: "${feedbackText}"` : ''}`,
                  receiptId: updatedReceipt.id,
                  customerName,
                  isEscalation: true
                });
                
                // Browser notification with urgency
                if (browserNotifications && Notification.permission === 'granted') {
                  new Notification(`🚨 Low Rating: ${customerName} - ${score}/5`, {
                    body: feedbackText || 'Customer had a poor experience. Action needed!',
                    icon: '/favicon.ico',
                    requireInteraction: true,
                    tag: `low-rating-${updatedReceipt.id}`
                  });
                }
              } 
              // MEDIOCRE RATING (score === 3) - notify but don't escalate
              else if (score === 3) {
                playNotificationSound();
                addNotification({
                  type: 'warning',
                  title: '⚠️ Mediocre Rating',
                  message: `${customerName} rated ${score}/5${feedbackText ? `: "${feedbackText}"` : ''}`,
                  receiptId: updatedReceipt.id,
                  customerName
                });
                
                showBrowserNotification(
                  `⚠️ ${customerName} rated ${score}/5`,
                  feedbackText || 'Customer had an average experience',
                  updatedReceipt.id
                );
              }
              // GOOD RATING (score >= 4) - simple notification
              else {
                playNotificationSound();
                addNotification({
                  type: 'success',
                  title: '⭐ Great Review!',
                  message: `${customerName} rated ${score}/5${feedbackText ? `: "${feedbackText}"` : ''}`,
                  receiptId: updatedReceipt.id,
                  customerName
                });
                
                showBrowserNotification(
                  `⭐ ${customerName} rated ${score}/5`,
                  feedbackText || 'Customer was satisfied!',
                  updatedReceipt.id
                );
              }
            }
          }
          // ===== END SURVEY NOTIFICATION =====
          
          // Update receipt in state directly instead of refetching all
          setReceipts(prev => prev.map(r => 
            r.id === updatedReceipt.id ? { ...r, ...updatedReceipt } : r
          ));
          
          // If modal is open for this receipt, reload details (isRefresh=true to preserve expanded groups)
          if (selectedReceipt?.id === updatedReceipt.id) {
            loadReceiptDetails(updatedReceipt.id, true);
          }
        }
      )
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'receipts' },
        (payload) => {
          devLog('📝 New receipt created via realtime');
          // Only fetch this one new receipt if it belongs to current store
          const newReceipt = payload.new;
          if (!currentStore?.id || newReceipt.store_id === currentStore.id) {
            // Prepend new receipt to list
            setReceipts(prev => [newReceipt, ...prev]);
          }
        }
      )
      .on('postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'receipts' },
        (payload) => {
          devLog('🗑️ Receipt deleted via realtime');
          // Remove from state directly
          setReceipts(prev => prev.filter(r => r.id !== payload.old.id));
        }
      )
      .subscribe((status) => {
        devLog('📡 Receipts subscription status:', status);
      });

    return () => {
      devLog('Unsubscribing from receipts channel');
      supabase.removeChannel(receiptsChannel);
    };
  }, [currentStore?.id]);

  // REAL-TIME INVENTORY UNITS SUBSCRIPTION
  // Auto-refresh when inventory changes (deliveries completed, items assigned, etc.)
  useEffect(() => {
    devLog('🔌 Setting up inventory_units realtime subscription...');
    
    const inventoryChannel = supabase
      .channel('dashboard-inventory')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'inventory_units' },
        (payload) => {
          devLog('📦 Inventory unit changed:', payload.eventType, payload.new?.sku || payload.old?.sku);
          // Trigger inventory tab refresh (this is efficient - component handles its own fetch)
          setInventoryRefreshTrigger(prev => prev + 1);
          
          // Update allInventoryUnits state directly based on event type
          if (payload.eventType === 'INSERT') {
            setAllInventoryUnits(prev => [...prev, payload.new]);
          } else if (payload.eventType === 'UPDATE') {
            setAllInventoryUnits(prev => prev.map(u => 
              u.id === payload.new.id ? payload.new : u
            ));
          } else if (payload.eventType === 'DELETE') {
            setAllInventoryUnits(prev => prev.filter(u => u.id !== payload.old.id));
          }
        }
      )
      .subscribe((status) => {
        devLog('📡 Inventory subscription status:', status);
      });

    return () => {
      devLog('Unsubscribing from inventory channel');
      supabase.removeChannel(inventoryChannel);
    };
  }, [currentStore?.id]);

  // REAL-TIME DELIVERY ROUTES SUBSCRIPTION
  // Auto-refresh when delivery routes or stops change
  useEffect(() => {
    devLog('🔌 Setting up delivery routes realtime subscription...');
    
    const deliveryChannel = supabase
      .channel('dashboard-deliveries')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'delivery_routes' },
        (payload) => {
          devLog('🚚 Delivery route changed:', payload.eventType);
          // Trigger deliveries tab refresh - let it handle its own data
          setInventoryRefreshTrigger(prev => prev + 1);
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'delivery_route_stops' },
        (payload) => {
          devLog('📍 Delivery stop changed:', payload.eventType);
          // Trigger deliveries tab refresh
          setInventoryRefreshTrigger(prev => prev + 1);
        }
      )
      .subscribe((status) => {
        devLog('📡 Delivery subscription status:', status);
      });

    return () => {
      devLog('Unsubscribing from delivery channel');
      supabase.removeChannel(deliveryChannel);
    };
  }, [currentStore?.id]);

  // REMOVED: Global polling - Realtime subscriptions handle all updates
  // This was causing 72k+ REST requests per day
  // If realtime ever fails, user can manually refresh with the refresh button
  
  // FALLBACK POLLING: Conservative 30-second backup for Safari/unreliable realtime
  // Safari often drops websocket connections silently
  useEffect(() => {
    // Only poll when tab is visible to save resources
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        devLog('👁️ Tab visible - refreshing data');
        refreshReceiptsInBackground(30000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Detect Safari - it needs polling fallback
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    let fallbackPoll = null;
    if (isSafari) {
      devLog('🍎 Safari detected - enabling 30s fallback polling');
      fallbackPoll = setInterval(() => {
        if (document.visibilityState === 'visible') {
          // 25s, not 30: a load that finished just under one interval ago
          // shouldn't make this tick skip, which would double the gap.
          refreshReceiptsInBackground(25000);
        }
      }, 30000); // 30 seconds - much lighter than 10s
    }
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (fallbackPoll) clearInterval(fallbackPoll);
    };
  }, [currentStore?.id]);

  // RELOAD INVOICES WHEN STORE CHANGES
  useEffect(() => {
    if (currentStore?.id) {
      loadInvoices();
    }
  }, [currentStore?.id]);

  // RELOAD PHONE AGENT SETTINGS WHEN STORE CHANGES
  useEffect(() => {
    if (currentStore?.id) {
      loadPhoneAgentSettings(currentStore.id);
    }
  }, [currentStore?.id, loadPhoneAgentSettings]);

  // SMART MESSAGE POLLING: Only when modal is open, and less aggressive
  // Realtime should handle this, but Safari/network issues may miss events
  useEffect(() => {
    if (!showReceiptModal || !selectedReceipt) return;
    
    // Track last message count to detect if realtime is working
    let lastMessageCount = selectedReceipt.messages?.length || 0;
    let realtimeWorking = true;
    
    // Poll every 10 seconds (not 3) - but only if realtime seems broken
    const pollInterval = setInterval(() => {
      // Only poll if tab is visible
      if (document.visibilityState !== 'visible') return;
      
      // Check if we've received realtime updates (message count changed without poll)
      const currentCount = selectedReceipt.messages?.length || 0;
      if (currentCount > lastMessageCount) {
        realtimeWorking = true;
        lastMessageCount = currentCount;
        return; // Realtime is working, skip poll
      }
      
      // Safari always needs polling, others only if realtime seems broken
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari || !realtimeWorking) {
        devLog('🔄 Fallback poll for messages');
        loadReceiptDetails(selectedReceipt.id, true);
      }
    }, 10000); // 10 seconds instead of 3
    
    return () => clearInterval(pollInterval);
  }, [showReceiptModal, selectedReceipt?.id]);

  // =====================================================
  // AUTO-FIX: Reconcile stuck receipts
  // If all items are delivered/picked_up but delivery_status is still pending,
  // fix it via backend and update local state immediately.
  // Used by the full list load AND the single-card refresh, so a card always
  // shows the same status a full reload would give it.
  // =====================================================
  const reconcileStuckReceipt = (receipt) => {
    const status = receipt.delivery_status || receipt.deliveryStatus;
    
    // Skip if already marked complete
    if (ITEM_TERMINAL_STATUSES.includes(status)) return receipt;
    
    // Check if all items are actually done
    const validItems = receipt.items || [];
    const itemStatuses = receipt.itemStatuses || [];
    
    if (validItems.length === 0) return receipt; // No items, skip
    
    // Count completed items by matching receipt_item_id
    const completedItemIds = new Set(
      itemStatuses
        .filter(s => ITEM_TERMINAL_STATUSES.includes(s.status))
        .map(s => s.receipt_item_id)
    );
    
    const allDone = validItems.every(item => completedItemIds.has(item.id));
    
    if (!allDone) return receipt; // Not all done, skip
    
    // ALL items are done but receipt is stuck! Fix it.
    console.log(`🔧 AUTO-FIX: Receipt ${receipt.id} has all ${validItems.length} items complete but delivery_status="${status}". Fixing...`);
    
    // Determine final status (picked_up if any item was picked up, otherwise delivered)
    const hasPickedUp = itemStatuses.some(s => s.status === 'picked_up');
    const finalStatus = hasPickedUp ? 'picked_up' : 'delivered';
    
    // Fire backend call to fix the DB (don't await — let it happen in background)
    authHeaders().then(headers => fetch('/api/update-receipt-status', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        receipt_id: receipt.id,
        delivery_status: finalStatus,
        survey_status: receipt.survey_status || 'pending'
      })
    })).then(r => r.json()).then(result => {
      console.log(`✅ AUTO-FIX: Receipt ${receipt.id} → ${finalStatus}`, result);
    }).catch(err => {
      console.error(`❌ AUTO-FIX failed for ${receipt.id}:`, err);
    });
    
    // Update local state immediately so it shows in Completed tab right now
    return {
      ...receipt,
      delivery_status: finalStatus,
      deliveryStatus: finalStatus
    };
  };

  // Load receipts - silent=true skips loading spinner (for background polling)
  const loadReceipts = async (storeId = null, silent = false) => {
    const seq = ++listLoadSeqRef.current;
    listLoadsInFlightRef.current += 1;
    lastListLoadStartRef.current = Date.now();
    try {
      if (!silent) setLoading(true);
      // Use provided storeId or fall back to currentStore
      const effectiveStoreId = storeId || currentStore?.id || null;
      const data = await fetchAllReceipts(effectiveStoreId);
      
      // AUTO-FIX stuck receipts (see reconcileStuckReceipt)
      const fixedData = data.map(reconcileStuckReceipt);
      
      // A newer load started while this one ran (e.g. a store switch, or a
      // reload after a staff action). Its data is fresher, so this older
      // result must not overwrite it.
      if (seq !== listLoadSeqRef.current) return;

      setReceipts(fixedData);
      lastListLoadRef.current = Date.now();

      // Also load any active escalations from the messages table
      await loadEscalations(effectiveStoreId);
    } catch (error) {
      devError('Error loading receipts:', error);
    } finally {
      listLoadsInFlightRef.current -= 1;
      // The newest load clears the spinner. A superseded one leaves it up,
      // so the list never flashes "no orders" before the fresher data lands.
      if (seq === listLoadSeqRef.current) setLoading(false);
    }
  };

  // Automatic refreshes (tab return, Safari fallback poll, incoming texts)
  // go through here: skipped while a load is already running or when the
  // list was refreshed less than minAgeMs ago. Staff actions call
  // loadReceipts directly and always get fresh data. A load running for
  // over 60s is treated as hung (e.g. a phone woke from sleep mid-request)
  // so it can't switch background refreshes off for the rest of the session.
  const refreshReceiptsInBackground = (minAgeMs = 30000) => {
    const loadRunning = listLoadsInFlightRef.current > 0 &&
      Date.now() - lastListLoadStartRef.current < 60000;
    if (loadRunning) return;
    if (Date.now() - lastListLoadRef.current < minAgeMs) return;
    loadReceipts(currentStoreRef.current?.id, true);
  };

  // Re-read ONE receipt and swap it into the list (e.g. so it moves between
  // Active and Completed right after its items are marked), instead of
  // waiting for a full reload.
  const refreshReceiptInList = async (receiptId) => {
    try {
      const row = await fetchListReceiptById(receiptId);
      if (!row) return;
      const fresh = reconcileStuckReceipt(row);
      setReceipts(prev => prev.map(r => (r.id === receiptId ? fresh : r)));
    } catch (error) {
      devError('Error refreshing receipt in list:', error);
    }
  };

  // Load store settings (tax rate, etc.)
  const loadStoreSettings = async (storeId) => {
    if (!storeId) return;
    
    try {
      const { data, error } = await supabase
        .from('store_settings')
        .select('tax_rate')
        .eq('store_id', storeId)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        devError('Error loading store settings:', error);
        return;
      }
      
      if (data) {
        setStoreSettings({
          tax_rate: data.tax_rate !== undefined && data.tax_rate !== null ? data.tax_rate : 8.00
        });
      } else {
        // No settings found, use defaults
        setStoreSettings({ tax_rate: 8.00 });
      }
    } catch (err) {
      devError('Error loading store settings:', err);
    }
  };

  // ==========================================
  // LOAD STORE ADDRESSES (for receipts)
  // ==========================================
  const loadStoreAddresses = async (storeId) => {
    if (!storeId) return;
    
    try {
      const { data, error } = await supabase
        .from('store_addresses')
        .select('*')
        .eq('store_id', storeId)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: true });
      
      if (error && error.code !== 'PGRST116' && !error.message?.includes('does not exist')) {
        devError('Error loading store addresses:', error);
        return;
      }
      
      setStoreAddresses(data || []);
    } catch (err) {
      // Table might not exist yet
      if (!err.message?.includes('does not exist')) {
        devError('Error loading store addresses:', err);
      }
      setStoreAddresses([]);
    }
  };

  // Load manufacturers for the current store
  const loadManufacturers = async (storeId) => {
    if (!storeId) {
      setManufacturers([]);
      return;
    }
    const mfrs = await fetchManufacturers(storeId);
    setManufacturers(mfrs);
  };

  // ==========================================
  // LOAD ACTIVE ESCALATIONS FROM MESSAGES
  // ==========================================
  const loadEscalations = async (storeId = null) => {
    try {
      // Get messages with escalation_needed=true that haven't been acknowledged
      // We join with receipts to get customer name and filter by store
      let query = supabase
        .from('messages')
        .select(`
          receipt_id,
          escalation_reason,
          escalation_acknowledged,
          created_at,
          receipts!inner(customer_name, store_id)
        `)
        .eq('escalation_needed', true)
        .or('escalation_acknowledged.is.null,escalation_acknowledged.eq.false')
        .order('created_at', { ascending: false });
      
      if (storeId) {
        query = query.eq('receipts.store_id', storeId);
      }
      
      const { data, error } = await query;
      
      const escalationMap = {};
      
      if (!error && data) {
        // Build escalations object, only keeping the most recent per receipt
        data.forEach(msg => {
          // Only add if we don't already have one for this receipt (most recent first)
          // and it hasn't been acknowledged
          if (!escalationMap[msg.receipt_id] && !msg.escalation_acknowledged) {
            escalationMap[msg.receipt_id] = {
              reason: msg.escalation_reason || 'Needs attention',
              timestamp: new Date(msg.created_at),
              acknowledged: false,
              customerName: msg.receipts?.customer_name || 'Customer'
            };
          }
        });
      }
      
      // ALSO load low-rating receipts as escalations
      let ratingQuery = supabase
        .from('receipts')
        .select('id, customer_name, customer_satisfaction_score, customer_feedback_text, survey_completed_at')
        .lte('customer_satisfaction_score', 2)
        .not('customer_satisfaction_score', 'is', null);
      
      if (storeId) {
        ratingQuery = ratingQuery.eq('store_id', storeId);
      }
      
      const { data: lowRatingReceipts, error: ratingError } = await ratingQuery;
      
      if (!ratingError && lowRatingReceipts) {
        // Get previously acknowledged low-rating escalations from localStorage
        let dismissedRatings = {};
        try {
          dismissedRatings = JSON.parse(localStorage.getItem('dismissedLowRatingEscalations') || '{}');
        } catch (e) { /* ignore parse errors */ }
        
        lowRatingReceipts.forEach(receipt => {
          // Don't overwrite message-based escalations (they're more specific)
          if (!escalationMap[receipt.id]) {
            const alreadyDismissed = !!dismissedRatings[receipt.id];
            escalationMap[receipt.id] = {
              reason: `Low satisfaction rating: ${receipt.customer_satisfaction_score}/5${receipt.customer_feedback_text ? ` - "${receipt.customer_feedback_text}"` : ''}`,
              timestamp: receipt.survey_completed_at ? new Date(receipt.survey_completed_at) : new Date(),
              acknowledged: alreadyDismissed,
              customerName: receipt.customer_name || 'Customer',
              type: 'low_rating'
            };
          }
        });
      }
      
      // Merge with existing escalations (preserve acknowledged state)
      setEscalations(prev => {
        const merged = { ...escalationMap };
        // If already acknowledged locally, keep it acknowledged
        Object.keys(prev).forEach(receiptId => {
          if (prev[receiptId]?.acknowledged && merged[receiptId]) {
            merged[receiptId].acknowledged = true;
          }
        });
        return merged;
      });
      
      devLog(`🚨 Loaded ${Object.keys(escalationMap).length} active escalations (incl. low ratings)`);
    } catch (err) {
      devError('Error loading escalations:', err);
    }
  };

  // ==========================================
  // LOAD ALL INVENTORY UNITS (for readiness checks)
  // ==========================================
  const loadInventoryUnits = async (storeId = null) => {
    try {
      const effectiveStoreId = storeId || currentStore?.id || null;
      let query = supabase
        .from('inventory_units')
        .select('*, manufacturer:manufacturers(id, name)')
        .order('created_at', { ascending: false });
      
      if (effectiveStoreId) {
        query = query.eq('store_id', effectiveStoreId);
      }
      
      const { data, error } = await query;
      if (!error) {
        setAllInventoryUnits(data || []);
        devLog(`📦 Loaded ${data?.length || 0} inventory units for readiness checks`);
      }
    } catch (err) {
      devError('Error loading inventory units:', err);
    }
  };

  // ==========================================
  // LOAD INVOICES - WITH STORE FILTER
  // ==========================================
  const loadInvoices = async () => {
    try {
      let query = supabase
        .from('supplier_invoices')
        .select('*, supplier_invoice_items (*)')
        .order('scanned_at', { ascending: false })
        .limit(50);
      
      // Filter by store if one is selected
      if (currentStore?.id) {
        query = query.eq('store_id', currentStore.id);
      }
      
      const { data, error } = await query;
      if (!error) {
        setInvoices(data || []);
      }
    } catch (err) {
      devError('Error loading invoices:', err);
    }
  };

  // The drawer used to re-fetch three things right after fetchReceiptById
  // (inventory units twice and the items' set fields), each one more
  // sequential round trip. fetchReceiptById already loaded the same rows
  // with the same columns, so derive them instead. Results match the old
  // re-fetches exactly:
  //  - inventory: units reserved for this receipt, plus units linked to its
  //    VALID items (the re-fetch used receipt.items, which excludes blank
  //    rows), in the same order;
  //  - set fields: the old merge reduced to `x || null` on both columns.
  const deriveDrawerFields = (receipt, receiptId) => {
    const validItemIds = new Set((receipt.items || []).map(item => item.id).filter(Boolean));
    const inventoryUnits = (receipt.inventoryUnits || []).filter(u =>
      String(u.reserved_for_receipt_id) === String(receiptId) ||
      validItemIds.has(u.receipt_item_id)
    );
    const items = (receipt.items || []).map(item => ({
      ...item,
      set_id: item.set_id || null,
      set_name: item.set_name || null
    }));
    return { inventoryUnits, items };
  };

  const loadReceiptDetails = async (receiptId, isRefresh = false) => {
    try {
      const receipt = await fetchReceiptById(receiptId);
      if (receipt) {
        // Inventory units + set fields: derived from what fetchReceiptById
        // already loaded (was three more sequential re-fetches).
        const drawerFields = deriveDrawerFields(receipt, receiptId);
        receipt.inventoryUnits = drawerFields.inventoryUnits;
        receipt.items = drawerFields.items;
        devLog(`📦 Loaded ${receipt.inventoryUnits.length} inventory units for receipt ${receiptId}`);

        // item_statuses for this receipt. Required so items marked ready
        // WITHOUT an inventory unit (no-SKU / no-stock items) display their
        // real status — the unit-based status logic can't see them, and
        // falls back to these rows by receipt_item_id. fetchReceiptById
        // already read these rows (select *); keep the same six columns the
        // old dedicated re-fetch selected, so the objects are unchanged.
        receipt.itemStatuses = (receipt.itemStatuses || []).map(s => ({
          receipt_item_id: s.receipt_item_id,
          item_name: s.item_name,
          sku: s.sku,
          status: s.status,
          notes: s.notes,
          updated_at: s.updated_at
        }));

        if (import.meta.env.DEV) verifyDrawerFieldsParity(receiptId, receipt, { withStatuses: true });

        setSelectedReceipt(receipt);
        // Only reset expanded groups when viewing a NEW receipt, not on refresh/poll
        if (!isRefresh) {
          setExpandedSkuGroups({});
        }
        setIsStaffMode(!receipt.is_ai_handling);
      }
    } catch (error) {
      devError('Error loading receipt details:', error);
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [bulkSkuInput, setBulkSkuInput] = useState('');
  const [bulkAddTarget, setBulkAddTarget] = useState('create'); // 'create' or 'edit'
  const [showBulkSetModal, setShowBulkSetModal] = useState(false);
  const [setName, setSetName] = useState('');
  const [setPrice, setSetPrice] = useState('');
  const [groupedItems, setGroupedItems] = useState([]); // Items being added to the group
  const [editingSetId, setEditingSetId] = useState(null); // Track if editing an existing set
  const [expandedSets, setExpandedSets] = useState({}); // Track which sets are expanded in the form
  
  // Single Item Modal state
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null); // null = new item, number = editing existing
  const [itemFormData, setItemFormData] = useState({
    name: '',
    sku: '',
    color: '',
    quantity: 1,
    price: '',
    manufacturerId: null
  });
  const [itemModalTarget, setItemModalTarget] = useState('create'); // 'create' or 'edit' (which receipt form)
  const [markingItemReady, setMarkingItemReady] = useState(false);
  
  // Grouped Items - item being added/edited within the group
  const [showGroupItemForm, setShowGroupItemForm] = useState(false);
  const [editingGroupItemIndex, setEditingGroupItemIndex] = useState(null);
  const [groupItemFormData, setGroupItemFormData] = useState({
    name: '',
    sku: '',
    color: '',
    quantity: 1,
    manufacturerId: null
  });
  
  // Payment History - for adding new payments
  // Local date helper (avoids UTC rollover that pushed dates to the next day)
  const getLocalDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    date: getLocalDate(),
    note: '',
    paymentType: 'cash' // cash, card, check, financing
  });
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    deliveryAddress: '',
    // Separate address fields for form input
    streetAddress: '',
    apartmentNumber: '',
    city: '',
    state: '',
    zipCode: '',
    items: [],
    deliveryStatus: 'pending',
    deliveryDate: '',
    internalNotes: '', // AI Customer Care Notes
    itemStatuses: [],
    customerPreferences: {
      preferredDeliveryDay: '',
      preferredTimeWindow: '',
      notes: '' // Delivery Instructions
    },
    // Payment tracking
    amountPaid: 0,
    paymentHistory: [], // Array of {amount, date, note}
    // Sales tracking
    salesperson: '',
    // Delivery services
    deliveryServices: {
      delivery: { enabled: false, price: '' },
      setup: { enabled: false, price: '' },
      wastePickup: { enabled: false, price: '' },
      pickup: { enabled: false, price: '' }
    },
    // SMS consent (TCPA compliance)
    smsConsent: false
  });

  // Simplified delivery statuses - matches the actual system flow
  // pending → out_for_delivery → delivered (full delivery)
  // pending → partial_out_for_delivery → partially_delivered → delivered (partial delivery)
  // 
  // BACKWARDS COMPATIBILITY: Legacy statuses (processing, in_transit, partial_scheduled)
  // are treated as 'pending' since they represent pre-dispatch states
  const deliveryStatuses = [
    { value: 'pending', label: 'Order Received', icon: Clock, color: 'blue' },
    { value: 'out_for_delivery', label: 'Out for Delivery', icon: Truck, color: 'cyan' },
    { value: 'partial_out_for_delivery', label: 'Partial Out', icon: Truck, color: 'indigo' },
    { value: 'partially_delivered', label: 'Partial Delivery', icon: Box, color: 'amber' },
    { value: 'delivered', label: 'Delivered', icon: CheckCircle, color: 'emerald' },
    { value: 'picked_up', label: 'Picked Up', icon: Box, color: 'emerald' }
  ];

  // Normalize legacy statuses to the simplified set
  const normalizeStatus = (status) => {
    // Legacy statuses that should be treated as 'pending'
    const legacyPendingStatuses = ['processing', 'in_transit', 'partial_scheduled', 'ready_for_pickup'];
    if (legacyPendingStatuses.includes(status)) {
      return 'pending';
    }
    // partial_delivery was renamed to partially_delivered
    if (status === 'partial_delivery') {
      return 'partially_delivered';
    }
    return status || 'pending';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-blue-100 border-blue-400 text-blue-700',
      out_for_delivery: 'bg-cyan-100 border-cyan-400 text-cyan-700',
      partial_out_for_delivery: 'bg-indigo-100 border-indigo-400 text-indigo-700',
      partially_delivered: 'bg-amber-100 border-amber-400 text-amber-700',
      delivered: 'bg-emerald-100 border-emerald-400 text-emerald-700',
      picked_up: 'bg-emerald-100 border-emerald-400 text-emerald-700'
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status) => {
    const statusObj = deliveryStatuses.find(s => s.value === status);
    const Icon = statusObj?.icon || Clock;
    return <Icon className="w-4 h-4" />;
  };

  // Helper: Calculate receipt readiness from item_statuses (accounts for quantities).
  // Inventory is retired (Jul 2026) — item_statuses is the sole fulfillment
  // source, so inventory_units are no longer consulted here.
  //
  // PERF: This function is called many times per render in the Orders list.
  // The hot inner work lives in __computeReceiptReadiness (see
  // readinessByReceiptId memo below). Return shape of
  // getItemReadinessByQuantity is unchanged.
  const getItemReadinessByQuantity = (receipt, globalInventory = []) => {
    // globalInventory is accepted for call-site compatibility but no longer
    // consulted — readiness comes exclusively from item_statuses rows.
    return __computeReceiptReadiness(receipt);
  };

  // Pure inner readiness computation. Status comes exclusively from
  // item_statuses rows (inventory is retired — see statusConstants.js).
  // Rows are matched receipt_item_id-first, then SKU among rows with no
  // receipt_item_id, one row consumed per item so two items sharing a SKU
  // can't both claim the same row — the same rules as chat.py and
  // receipt_context.py. Name-only items (no SKU) can only match by
  // receipt_item_id.
  function __computeReceiptReadiness(receipt) {
    const items = receipt.items || [];
    const itemStatusRows = receipt.itemStatuses || receipt.item_statuses || [];

    // Pre-process rows for one-to-one consumption (SKU-collision safe).
    const statusRows = itemStatusRows.map(s => ({
      sku: (s.sku || '').toLowerCase().trim(),
      receiptItemId: s.receipt_item_id || s.receiptItemId || null,
      status: s.status,
      used: false,
    }));

    let totalQty = 0;
    let deliveredQty = 0;
    let pickedUpQty = 0;
    let readyQty = 0;
    let outForDeliveryQty = 0;
    let waitingQty = 0;

    items.forEach(item => {
      const qty = item.quantity || 1;
      totalQty += qty;
      const skuKey = item.sku?.toLowerCase().trim();
      const receiptItemId = item.id;

      // Match an item_statuses row: receipt_item_id first, then SKU among
      // rows that have no receipt_item_id.
      let row = null;
      if (receiptItemId) {
        row = statusRows.find(r =>
          !r.used && r.receiptItemId && String(r.receiptItemId) === String(receiptItemId)
        ) || null;
      }
      if (!row && skuKey) {
        row = statusRows.find(r => !r.used && !r.receiptItemId && r.sku === skuKey) || null;
      }

      if (row) {
        row.used = true;
        const fb = row.status;
        if (ITEM_TERMINAL_STATUSES.includes(fb)) {
          deliveredQty += qty;
          if (fb === 'picked_up') pickedUpQty += qty;
        } else if (fb === 'out_for_delivery') {
          outForDeliveryQty += qty;
        } else if (['at_store', 'in_warehouse', 'reserved'].includes(fb)) {
          readyQty += qty;
        } else {
          // Row exists but is still pending/in-transit.
          waitingQty += qty;
        }
      } else {
        // No status row at all — genuinely waiting.
        waitingQty += qty;
      }
    });

    const allReady = totalQty > 0 && waitingQty === 0 && readyQty > 0;
    const allDelivered = totalQty > 0 && deliveredQty === totalQty;
    const hasAnyProgress = deliveredQty > 0 || readyQty > 0 || outForDeliveryQty > 0;

    return {
      totalQty,
      deliveredQty,
      pickedUpQty,
      readyQty,
      outForDeliveryQty,
      waitingQty,
      allReady,
      allDelivered,
      hasAnyProgress
    };
  }

  // PERF: Precompute readiness for every receipt ONCE per receipts change.
  // Mobile (and any consumer) can do an O(1) lookup instead of recomputing per render.
  // Result shape is identical to getItemReadinessByQuantity(receipt).
  const readinessByReceiptId = useMemo(() => {
    const out = {};
    for (const r of receipts) {
      out[r.id] = __computeReceiptReadiness(r);
    }
    return out;
    // __computeReceiptReadiness is a stable function declaration (hoisted),
    // so it intentionally isn't in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts]);

  // Helper: Get individual item status (for item detail view).
  // Inventory is retired (Jul 2026) — status comes exclusively from
  // item_statuses rows, matched receipt_item_id-first, then SKU among rows
  // with no receipt_item_id (same rules as chat.py / receipt_context.py).
  // Name-only items (no SKU) can only match by receipt_item_id.
  //
  // Signature is unchanged for call-site compatibility:
  //   - reservedUnits / globalInventory are accepted but no longer consulted.
  //   - claimedUnitIds now tracks CONSUMED item_statuses rows (by object
  //     identity) so two items sharing a SKU can't both claim the same
  //     SKU-level row; getAllItemStatusesForReceipt passes one Set across
  //     all items of a receipt, which is what makes that work.
  //   - claimedUnitId in the return value is always null (there are no
  //     units left to claim).
  const getItemStatusByInventory = (item, reservedUnits, globalInventory = [], claimedUnitIds = new Set(), itemStatuses = []) => {
    const qty = item.quantity || 1;
    const skuKey = item.sku?.toLowerCase().trim();
    const receiptItemId = item.id; // The specific receipt_item row ID

    let statusRow = null;
    if (receiptItemId) {
      statusRow = (itemStatuses || []).find(s =>
        !claimedUnitIds.has(s) &&
        String(s.receipt_item_id || s.receiptItemId || '') === String(receiptItemId)
      ) || null;
    }
    if (!statusRow && skuKey) {
      statusRow = (itemStatuses || []).find(s =>
        !claimedUnitIds.has(s) &&
        !(s.receipt_item_id || s.receiptItemId) &&
        (s.sku || '').toLowerCase().trim() === skuKey
      ) || null;
    }

    if (statusRow) {
      claimedUnitIds.add(statusRow); // consume the row (SKU-collision safety)
      const s = statusRow.status;
      if (ITEM_TERMINAL_STATUSES.includes(s)) {
        return { status: s, delivered: qty, ready: 0, waiting: 0, outForDelivery: 0, claimedUnitId: null };
      }
      if (s === 'out_for_delivery') {
        return { status: 'out_for_delivery', delivered: 0, ready: 0, waiting: 0, outForDelivery: 1, claimedUnitId: null };
      }
      if (['at_store', 'in_warehouse', 'reserved'].includes(s)) {
        return { status: 'ready', delivered: 0, ready: qty, waiting: 0, outForDelivery: 0, claimedUnitId: null };
      }
      // Row exists but is still pending/in-transit — falls through to waiting.
    }

    // No status row (or a still-pending one) — genuinely waiting.
    return { status: 'waiting', delivered: 0, ready: 0, waiting: qty, outForDelivery: 0, claimedUnitId: null };
  };

  // ========== NEW: Get all item statuses for a receipt, properly tracking claimed units ==========
  // This prevents the bug where multiple items with the same SKU all show "Ready" 
  // even when there aren't enough units
  const getAllItemStatusesForReceipt = (items, reservedUnits, globalInventory = [], itemStatuses = []) => {
    const statusMap = new Map(); // itemId -> status
    const claimedUnitIds = new Set(); // Track which units have been assigned
    
    // Process items in order - first come, first served for unclaimed units
    for (const item of items) {
      const status = getItemStatusByInventory(item, reservedUnits, globalInventory, claimedUnitIds, itemStatuses);
      statusMap.set(item.id, status);
      
      // Mark the claimed unit so other items can't claim it
      if (status.claimedUnitId) {
        claimedUnitIds.add(status.claimedUnitId);
      }
    }
    
    return statusMap;
  };

  const formatPhoneNumber = (value) => {
    const phone = value.replace(/\D/g, '');
    if (phone.length <= 3) return phone;
    if (phone.length <= 6) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
  };

  const calculateTotals = (items, deliveryServices = null) => {
    const itemsSubtotal = items.reduce((sum, item) => {
      return sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0);
    }, 0);
    
    // Calculate delivery services total
    let servicesTotal = 0;
    if (deliveryServices) {
      if (deliveryServices.delivery?.enabled) servicesTotal += parseFloat(deliveryServices.delivery.price) || 0;
      if (deliveryServices.setup?.enabled) servicesTotal += parseFloat(deliveryServices.setup.price) || 0;
      if (deliveryServices.wastePickup?.enabled) servicesTotal += parseFloat(deliveryServices.wastePickup.price) || 0;
      if (deliveryServices.pickup?.enabled) servicesTotal += parseFloat(deliveryServices.pickup.price) || 0;
    }
    
    const subtotal = Math.round(itemsSubtotal * 100) / 100;
    const taxRate = (parseFloat(storeSettings.tax_rate) || 8) / 100; // Convert percentage to decimal
    // If taxExempt flag is set on deliveryServices, services are NOT taxed (some stores don't tax delivery/setup/waste)
    // Default behavior (flag absent or false): services ARE taxed (preserves existing behavior)
    const servicesAreTaxExempt = !!(deliveryServices && deliveryServices.taxExempt);
    const taxableAmount = servicesAreTaxExempt
      ? subtotal
      : Math.round((subtotal + servicesTotal) * 100) / 100;
    const tax = Math.round(taxableAmount * taxRate * 100) / 100;
    const total = Math.round((subtotal + servicesTotal + tax) * 100) / 100;
    return { subtotal, tax, total, servicesTotal, taxRate: parseFloat(storeSettings.tax_rate) || 8 };
  };

  // Helper: guarantee every item has a non-empty SKU.
  // Items entered without a real item number still need one so they can be
  // serialized into inventory units and flow through the system the normal
  // way. When the user leaves the item-number field blank, we assign a
  // unique synthetic SKU (MISC-XXXXXX) rather than reusing the product name
  // — reused names would collide across customers and pollute SKU-based
  // matching (scanner catalog/on-order, inventory auto-assign, delivery).
  const ensureSku = (rawSku) => {
    const sku = (rawSku || '').toUpperCase().trim();
    if (sku) return sku;
    const rand = (crypto.randomUUID().replace(/-/g, '').slice(0, 6)).toUpperCase();
    return `MISC-${rand}`;
  };

  // Helper: Explode items with qty > 1 into individual line items
  // This enables partial deliveries - each item can be independently selected
  // PRESERVES set membership (setId, setName) for grouped items
  const explodeItems = (items) => {
    const explodedItems = [];
    for (const item of items) {
      const qty = parseInt(item.quantity) || 1;
      const unitPrice = parseFloat(item.price) || 0;
      
      if (qty > 1) {
        // Create individual line items for each unit
        for (let i = 0; i < qty; i++) {
          explodedItems.push({
            ...item,
            quantity: 1,
            price: unitPrice,
            setId: item.setId || null,
            setName: item.setName || null
          });
        }
        devLog(`💥 Exploded ${item.sku || item.name}: qty ${qty} → ${qty} individual items`);
      } else {
        // Single item, keep as-is
        explodedItems.push({
          ...item,
          quantity: 1,
          price: unitPrice,
          setId: item.setId || null,
          setName: item.setName || null
        });
      }
    }
    return explodedItems;
  };

  // Payment status helper
  const getPaymentStatus = (receipt) => {
    const total = Math.round((parseFloat(receipt.total) || 0) * 100) / 100;
    const amountPaid = Math.round((parseFloat(receipt.amount_paid) || 0) * 100) / 100;
    const balance = Math.round((total - amountPaid) * 100) / 100;
    
    if (amountPaid >= total && total > 0) {
      return { status: 'paid', label: 'Paid in Full', color: 'emerald', balance: 0 };
    } else if (amountPaid > 0) {
      return { status: 'partial', label: 'Partial Payment', color: 'amber', balance };
    } else {
      return { status: 'unpaid', label: 'Unpaid', color: 'red', balance: total };
    }
  };

  // ==========================================
  // PAYMENT HISTORY FUNCTIONS
  // ==========================================
  const handleAddPayment = (targetForm = 'edit') => {
    const amount = parseFloat(paymentFormData.amount);
    if (!amount || amount <= 0) return;
    
    const newPayment = {
      id: Date.now(),
      amount: amount,
      date: paymentFormData.date || getLocalDate(),
      note: paymentFormData.note || '',
      paymentType: paymentFormData.paymentType || 'cash',
      created_at: new Date().toISOString()
    };
    
    if (targetForm === 'edit' && editFormData) {
      const currentHistory = editFormData.paymentHistory || [];
      const newHistory = [...currentHistory, newPayment];
      const newTotalPaid = newHistory.reduce((sum, p) => sum + p.amount, 0);
      
      setEditFormData({
        ...editFormData,
        paymentHistory: newHistory,
        amountPaid: newTotalPaid
      });
    } else {
      const currentHistory = formData.paymentHistory || [];
      const newHistory = [...currentHistory, newPayment];
      const newTotalPaid = newHistory.reduce((sum, p) => sum + p.amount, 0);
      
      setFormData({
        ...formData,
        paymentHistory: newHistory,
        amountPaid: newTotalPaid
      });
    }
    
    // Reset form
    setPaymentFormData({
      amount: '',
      date: getLocalDate(),
      note: '',
      paymentType: 'cash'
    });
    setShowPaymentForm(false);
  };
  
  const handleRemovePayment = (paymentId, targetForm = 'edit') => {
    if (targetForm === 'edit' && editFormData) {
      const currentHistory = editFormData.paymentHistory || [];
      const newHistory = currentHistory.filter(p => p.id !== paymentId);
      const newTotalPaid = newHistory.reduce((sum, p) => sum + p.amount, 0);
      
      setEditFormData({
        ...editFormData,
        paymentHistory: newHistory,
        amountPaid: newTotalPaid
      });
    } else {
      const currentHistory = formData.paymentHistory || [];
      const newHistory = currentHistory.filter(p => p.id !== paymentId);
      const newTotalPaid = newHistory.reduce((sum, p) => sum + p.amount, 0);
      
      setFormData({
        ...formData,
        paymentHistory: newHistory,
        amountPaid: newTotalPaid
      });
    }
  };

  // ==========================================
  // SIGNATURES FUNCTIONS - NEW
  // ==========================================
  const fetchSignatures = async (receiptId) => {
    if (!receiptId) return;
    
    setLoadingSignatures(true);
    try {
      const { data, error } = await supabase
        .from('delivery_signatures')
        .select('*')
        .eq('receipt_id', receiptId)
        .order('signed_at', { ascending: false });
      
      if (error) throw error;
      
      let sigs = data || [];

      // RETROACTIVE ITEM DERIVATION: signatures written before the snapshot
      // fix have an empty items_delivered, but their route stop still knows
      // exactly which items it carried (receipt_item_ids for unitless items,
      // inventory_unit_ids for legacy stock). Derive the list at read time —
      // no backfill needed, works for every historical signature. New
      // signatures carry their own snapshot and skip all of this.
      try {
        const needy = sigs.filter(
          (s) => (!s.items_delivered || s.items_delivered.length === 0) && s.route_stop_id
        );
        if (needy.length > 0) {
          const stopIds = [...new Set(needy.map((s) => s.route_stop_id))];
          const { data: stops } = await supabase
            .from('delivery_route_stops')
            .select('id, receipt_item_ids, inventory_unit_ids')
            .in('id', stopIds);
          const stopMap = new Map((stops || []).map((st) => [st.id, st]));

          // Collect ids across all needy stops for two batched lookups
          const directItemIds = new Set();
          const unitIds = new Set();
          (stops || []).forEach((st) => {
            (st.receipt_item_ids || []).forEach((id) => directItemIds.add(String(id)));
            (st.inventory_unit_ids || []).forEach((id) => unitIds.add(String(id)));
          });

          let unitRows = [];
          if (unitIds.size > 0) {
            const { data: u } = await supabase
              .from('inventory_units')
              .select('id, sku, receipt_item_id')
              .in('id', [...unitIds]);
            unitRows = u || [];
            unitRows.forEach((un) => {
              if (un.receipt_item_id) directItemIds.add(String(un.receipt_item_id));
            });
          }

          let itemRows = [];
          if (directItemIds.size > 0) {
            const { data: ri } = await supabase
              .from('receipt_items')
              .select('id, sku, item_name')
              .in('id', [...directItemIds]);
            itemRows = ri || [];
          }
          const itemById = new Map(itemRows.map((r) => [String(r.id), r]));
          const unitById = new Map(unitRows.map((u2) => [String(u2.id), u2]));

          sigs = sigs.map((sig) => {
            if (sig.items_delivered && sig.items_delivered.length > 0) return sig;
            const stop = stopMap.get(sig.route_stop_id);
            if (!stop) return sig;

            const groups = {};
            const add = (sku, name) => {
              const key = (sku || 'UNKNOWN').trim().toUpperCase();
              if (!groups[key]) {
                groups[key] = { sku: key, name: name || 'Unknown Item', quantity: 0, item_numbers: [] };
              }
              groups[key].quantity += 1;
            };
            // Unitless items ride the stop by receipt_item_id; legacy stock
            // rides by unit id. The two lists are disjoint by construction.
            (stop.receipt_item_ids || []).forEach((id) => {
              const row = itemById.get(String(id));
              add(row?.sku, row?.item_name);
            });
            (stop.inventory_unit_ids || []).forEach((id) => {
              const un = unitById.get(String(id));
              if (!un) return;
              const row = un.receipt_item_id ? itemById.get(String(un.receipt_item_id)) : null;
              add(row?.sku || un.sku, row?.item_name);
            });

            const derived = Object.values(groups);
            if (derived.length === 0) return sig;
            const count = derived.reduce((s, g) => s + g.quantity, 0);
            return {
              ...sig,
              items_delivered: derived,
              total_items_in_signature: sig.total_items_in_signature || count,
            };
          });
        }
      } catch (deriveError) {
        // Display-only enrichment — never block the signatures modal over it.
        devLog('Signature item derivation skipped:', deriveError);
      }

      setSignatures(sigs);
    } catch (error) {
      devError('Error fetching signatures:', error);
      setSignatures([]);
    } finally {
      setLoadingSignatures(false);
    }
  };

  const openSignaturesDrawer = (receipt) => {
    setSelectedReceipt(receipt);
    setShowSignaturesDrawer(true);
    fetchSignatures(receipt.id);
  };

  // ==========================================
  // RECEIPT SCANNER FUNCTIONS
  // ==========================================
  
  // Open item modal for adding a new item
  const handleAddItem = (target = 'create') => {
    setItemFormData({
      name: '',
      sku: '',
      color: '',
      quantity: 1,
      price: '',
      manufacturerId: null
    });
    setEditingItemIndex(null);
    setItemModalTarget(target);
    setShowItemModal(true);
  };
  
  // Open item modal for editing an existing item
  const handleEditItem = (index, target = 'create') => {
    const items = target === 'edit' ? (editFormData?.items || []) : formData.items;
    const item = items[index];
    if (item) {
      setItemFormData({
        name: item.name || item.item_name || '',
        sku: item.sku || '',
        color: item.color || '',
        quantity: item.quantity || 1,
        price: item.price === 0 || item.price === '0' ? '0' : (item.price || ''),
        manufacturerId: item.manufacturerId || item.manufacturer_id || null
      });
      setEditingItemIndex(index);
      setItemModalTarget(target);
      setShowItemModal(true);
    }
  };
  
  // Save item from modal (add new or update existing)
  const handleSaveItemFromModal = () => {
    // Require a product name. The item NUMBER (SKU) is optional — if left
    // blank, a unique one is assigned automatically at save (ensureSku), so
    // the item can still be serialized and tracked the normal way.
    if (!itemFormData.name || !itemFormData.name.trim()) {
      addNotification({
        title: 'Product name required',
        message: 'Enter a product name. An item number is optional — one will be assigned automatically if you leave it blank.'
      });
      return;
    }

    const newItem = {
      name: itemFormData.name.trim(),
      item_name: itemFormData.name.trim(), // Also set item_name for database compatibility
      sku: itemFormData.sku.toUpperCase().trim(),
      color: itemFormData.color,
      quantity: parseInt(itemFormData.quantity) || 1,
      price: itemFormData.price === '' ? 0 : parseFloat(itemFormData.price) || 0,
      manufacturerId: itemFormData.manufacturerId || null
    };
    
    if (itemModalTarget === 'edit' && editFormData) {
      // Working with edit form (receipt detail modal)
      let updatedItems;
      if (editingItemIndex !== null) {
        // Editing existing item - preserve id and set fields
        updatedItems = [...editFormData.items];
        updatedItems[editingItemIndex] = { 
          ...updatedItems[editingItemIndex], 
          ...newItem 
        };
      } else {
        // Adding new item
        updatedItems = [...editFormData.items, newItem];
      }
      const { subtotal, tax, total } = calculateTotals(updatedItems);
      setEditFormData({
        ...editFormData,
        items: updatedItems,
        subtotal,
        tax,
        total
      });
    } else {
      // Working with create form
      let updatedItems;
      if (editingItemIndex !== null) {
        // Editing existing item
        updatedItems = [...formData.items];
        updatedItems[editingItemIndex] = { ...updatedItems[editingItemIndex], ...newItem };
      } else {
        // Adding new item
        updatedItems = [...formData.items, newItem];
      }
      setFormData({
        ...formData,
        items: updatedItems
      });
    }
    
    // Close modal and reset
    setShowItemModal(false);
    setEditingItemIndex(null);
    setItemFormData({
      name: '',
      sku: '',
      color: '',
      quantity: 1,
      price: '',
      manufacturerId: null
    });
  };
  
  // Close item modal without saving
  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setEditingItemIndex(null);
    setItemFormData({
      name: '',
      sku: '',
      color: '',
      quantity: 1,
      price: '',
      manufacturerId: null
    });
  };

  const handleBulkAddItems = () => {
    // Parse SKUs from input - handle comma, space, newline, or tab separated
    const skus = bulkSkuInput
      .split(/[\s,\t\n]+/)
      .map(sku => sku.trim().toUpperCase())
      .filter(sku => sku.length > 0);
    
    if (skus.length === 0) {
      setShowBulkAddModal(false);
      setBulkSkuInput('');
      return;
    }
    
    // Create new items with SKUs pre-filled (already uppercased)
    const newItems = skus.map(sku => ({
      name: '',
      sku: sku,
      color: '',
      quantity: 1,
      price: 0,
      manufacturerId: null
    }));
    
    if (bulkAddTarget === 'edit' && editFormData) {
      // Adding to edit form (receipt detail modal)
      const existingItems = (editFormData.items || []).filter(item => 
        item.name || item.sku || item.price > 0
      );
      const allItems = [...existingItems, ...newItems];
      const { subtotal, tax, total } = calculateTotals(allItems);
      
      setEditFormData({
        ...editFormData,
        items: allItems,
        subtotal,
        tax,
        total
      });
    } else {
      // Adding to create form
      const existingItems = formData.items.filter(item => 
        item.name || item.sku || item.price > 0
      );
      
      setFormData({
        ...formData,
        items: [...existingItems, ...newItems]
      });
    }
    
    setShowBulkAddModal(false);
    setBulkSkuInput('');
  };

  // ==========================================
  // GROUPED ITEMS FUNCTIONS
  // ==========================================
  
  // Add item to the group being built
  const handleAddItemToGroup = () => {
    setGroupItemFormData({ name: '', sku: '', color: '', quantity: 1, manufacturerId: null });
    setEditingGroupItemIndex(null);
    setShowGroupItemForm(true);
    // Scroll to form after it renders
    setTimeout(() => {
      document.getElementById('group-item-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };
  
  // Add a blank inline row to the group (auto-carries manufacturer from previous row)
  const handleAddInlineGroupRow = () => {
    const lastItem = groupedItems.length > 0 ? groupedItems[groupedItems.length - 1] : null;
    const newRow = {
      name: '',
      sku: '',
      color: '',
      quantity: 1,
      manufacturerId: lastItem?.manufacturerId || null
    };
    const newIndex = groupedItems.length;
    setGroupedItems([...groupedItems, newRow]);
    setEditingGroupItemIndex(newIndex); // Expand this new row
    // Focus the new row's name input after render
    setTimeout(() => {
      document.getElementById(`group-row-name-${newIndex}`)?.focus();
    }, 50);
  };
  
  // Update a single field on a group item inline (no save button needed).
  // Uses a functional state update so several updates fired in the same tick
  // (e.g. name + sku + manufacturerId from an autocomplete onSelect) don't each
  // start from a stale `groupedItems` snapshot and clobber one another.
  const handleUpdateGroupItemInline = (index, field, value) => {
    setGroupedItems((prev) => {
      if (!prev[index]) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Update MULTIPLE fields on a group item in a single state update.
  // Needed for catalog autocomplete onSelect, which sets name + sku (+ manufacturer)
  // at once — doing them as separate sequential single-field calls dropped the SKU.
  const handleUpdateGroupItemFields = (index, fields) => {
    setGroupedItems((prev) => {
      if (!prev[index]) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], ...fields };
      return updated;
    });
  };

  // Bulk-add several catalog picks to the group at once (multi-select dropdown).
  // `picks` is an array of { name, sku, manufacturerId, quantity } (brand already
  // resolved to manufacturerId by the caller). The first pick fills the target row
  // if that row is still empty; remaining picks are appended as brand-new rows.
  // Each row carries its chosen quantity and the color already on the target row.
  const handleAddGroupItemsBulk = (index, picks) => {
    if (!Array.isArray(picks) || picks.length === 0) return;
    setGroupedItems((prev) => {
      const updated = [...prev];
      const target = updated[index] || {
        name: '',
        sku: '',
        color: '',
        quantity: 1,
        manufacturerId: null,
      };
      const carryColor = target.color || '';

      const makeRow = (p) => ({
        name: p.name || '',
        sku: (p.sku || '').toUpperCase(),
        color: carryColor,
        quantity: Math.max(1, parseInt(p.quantity) || 1),
        manufacturerId: p.manufacturerId || null,
      });

      const targetIsEmpty =
        !(target.name && target.name.trim()) && !(target.sku && target.sku.trim());

      let startIdx = 0;
      if (targetIsEmpty && updated[index]) {
        // Reuse the row the user typed into for the first pick.
        updated[index] = makeRow(picks[0]);
        startIdx = 1;
      }

      const newRows = picks.slice(startIdx).map(makeRow);
      updated.push(...newRows);
      return updated;
    });
    // Collapse any open single-item editor so the new rows show cleanly.
    setEditingGroupItemIndex(null);
    setShowGroupItemForm(false);
  };
  
  // Edit an item within the group
  const handleEditGroupItem = (index) => {
    const item = groupedItems[index];
    if (item) {
      setGroupItemFormData({
        name: item.name || '',
        sku: item.sku || '',
        color: item.color || '',
        quantity: item.quantity || 1,
        manufacturerId: item.manufacturerId || null
      });
      setEditingGroupItemIndex(index);
      setShowGroupItemForm(true);
      // Scroll to form after it renders
      setTimeout(() => {
        document.getElementById('group-item-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  };
  
  // Save item to the group (add new or update existing)
  const handleSaveGroupItem = () => {
    const newItem = {
      name: groupItemFormData.name,
      sku: groupItemFormData.sku.toUpperCase(),
      color: groupItemFormData.color,
      quantity: parseInt(groupItemFormData.quantity) || 1,
      manufacturerId: groupItemFormData.manufacturerId || null
    };
    
    if (editingGroupItemIndex !== null) {
      // Update existing item
      const updated = [...groupedItems];
      updated[editingGroupItemIndex] = newItem;
      setGroupedItems(updated);
    } else {
      // Add new item
      setGroupedItems([...groupedItems, newItem]);
    }
    
    // Reset form
    setShowGroupItemForm(false);
    setEditingGroupItemIndex(null);
    setGroupItemFormData({ name: '', sku: '', color: '', quantity: 1, manufacturerId: null });
  };
  
  // Remove item from the group
  const handleRemoveGroupItem = (index) => {
    const newItems = groupedItems.filter((_, i) => i !== index);
    setGroupedItems(newItems);
    // If we were editing this item, clear editing state
    if (editingGroupItemIndex === index) {
      setEditingGroupItemIndex(null);
    } else if (editingGroupItemIndex !== null && editingGroupItemIndex > index) {
      // Adjust index if we removed an item before the one being edited
      setEditingGroupItemIndex(editingGroupItemIndex - 1);
    }
  };
  
  // Close group item form without saving
  const handleCloseGroupItemForm = () => {
    setShowGroupItemForm(false);
    setEditingGroupItemIndex(null);
    setGroupItemFormData({ name: '', sku: '', color: '', quantity: 1, manufacturerId: null });
  };
  
  // Reset the entire grouped items modal
  const handleCloseGroupedItemsModal = () => {
    setShowBulkSetModal(false);
    setSetName('');
    setSetPrice('');
    setGroupedItems([]);
    setEditingSetId(null);
    setShowGroupItemForm(false);
    setEditingGroupItemIndex(null);
    setGroupItemFormData({ name: '', sku: '', color: '', quantity: 1, manufacturerId: null });
  };

  // Handle adding a furniture set (multiple items grouped together)
  const handleAddSet = () => {
    // Filter to only valid items (must have name and manufacturer)
    const validItems = groupedItems.filter(item => item.name && item.name.trim() && item.manufacturerId);
    
    if (validItems.length === 0) {
      handleCloseGroupedItemsModal();
      return;
    }
    
    // Reuse the same set ID when editing, generate new one when creating
    const newSetId = editingSetId || crypto.randomUUID();
    const setTotalPrice = parseFloat(setPrice) || 0;
    
    // Calculate total pieces by summing all quantities
    const totalPieces = validItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0);
    const finalSetName = setName.trim() || `Group (${totalPieces} pieces)`;
    
    // EXPAND items into individual units for serialization
    // Each unit needs to be its own line item so it can get its own item number
    // e.g., "6 Chairs" becomes 6 separate chair entries
    const expandedItems = [];
    validItems.forEach((item) => {
      const qty = parseInt(item.quantity) || 1;
      for (let i = 0; i < qty; i++) {
        expandedItems.push({
          name: item.name || '',
          sku: item.sku || '',
          color: item.color || '',
          quantity: 1, // Always 1 - each is an individual unit
          price: 0, // Price set on last item below
          setId: newSetId,
          setName: finalSetName,
          manufacturerId: item.manufacturerId || null
        });
      }
    });
    
    // Price goes on the LAST item so calculateTotals works correctly
    if (expandedItems.length > 0) {
      expandedItems[expandedItems.length - 1].price = setTotalPrice;
    }
    
    const newSetItems = expandedItems;
    
    if (bulkAddTarget === 'edit' && editFormData) {
      // Adding to edit form (receipt detail modal)
      const existingItems = (editFormData.items || []).filter(item => {
        // When editing a set, remove old items for that set
        if (editingSetId && ((item.setId || item.set_id) === editingSetId)) return false;
        return item.name || item.sku || item.price > 0 || item.setId || item.set_id;
      });
      const allItems = [...existingItems, ...newSetItems];
      const { subtotal, tax, total } = calculateTotals(allItems);
      
      setEditFormData({
        ...editFormData,
        items: allItems,
        subtotal,
        tax,
        total
      });
      setExpandedSets(prev => ({ ...prev, [newSetId]: true }));
    } else {
      // Adding to create form
      const existingItems = formData.items.filter(item => {
        // When editing a set, remove old items for that set
        if (editingSetId && ((item.setId || item.set_id) === editingSetId)) return false;
        return item.name || item.sku || item.price > 0 || item.setId || item.set_id;
      });
      
      setFormData({
        ...formData,
        items: [...existingItems, ...newSetItems]
      });
      setExpandedSets(prev => ({ ...prev, [newSetId]: true }));
    }

    addNotification({
      title: 'Group Added',
      message: `${finalSetName} added to receipt`,
    });
    
    // Reset modal
    handleCloseGroupedItemsModal();
  };

  // Edit an existing set - re-populate the modal with set items
  const handleEditSet = (setId, target = 'create') => {
    // Set the target so handleAddSet knows where to save
    setBulkAddTarget(target);
    
    // Get items from the right source
    const sourceItems = (target === 'edit' && editFormData) 
      ? (editFormData.items || []) 
      : formData.items;
    
    // Find all items belonging to this set (handle both snake_case and camelCase)
    const setItems = sourceItems.filter(item => (item.setId || item.set_id) === setId);
    if (setItems.length === 0) return;
    
    // Get set name from first item (handle both naming conventions)
    const setNameValue = setItems[0]?.setName || setItems[0]?.set_name || '';
    
    // Calculate total price (sum of all item prices in the set)
    const totalPrice = setItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
    
    // Re-collapse expanded items back into grouped rows
    // Group by name + sku + color + manufacturerId, sum quantities
    const collapsedMap = new Map();
    setItems.forEach(item => {
      const mfrId = item.manufacturerId || item.manufacturer_id || '';
      const key = `${item.name || ''}|||${item.sku || ''}|||${item.color || ''}|||${mfrId}`;
      if (collapsedMap.has(key)) {
        const existing = collapsedMap.get(key);
        existing.quantity += 1;
      } else {
        collapsedMap.set(key, {
          name: item.name || '',
          sku: item.sku || '',
          color: item.color || '',
          quantity: 1,
          manufacturerId: mfrId || null
        });
      }
    });
    
    const collapsedItems = Array.from(collapsedMap.values());
    
    // Pre-populate modal state
    setEditingSetId(setId);
    setSetName(setNameValue);
    setSetPrice(totalPrice > 0 ? totalPrice.toString() : '');
    setGroupedItems(collapsedItems);
    setEditingGroupItemIndex(null);
    setShowGroupItemForm(false);
    setShowBulkSetModal(true);
  };

  // Remove an entire set
  const handleRemoveSet = (setId) => {
    if (bulkAddTarget === 'edit' && editFormData) {
      const newItems = editFormData.items.filter(item => item.setId !== setId);
      const { subtotal, tax, total } = calculateTotals(newItems);
      setEditFormData({
        ...editFormData,
        items: newItems.length > 0 ? newItems : [{ name: '', sku: '', color: '', quantity: 1, price: '', manufacturerId: null }],
        subtotal,
        tax,
        total
      });
    } else {
      const newItems = formData.items.filter(item => item.setId !== setId);
      setFormData({
        ...formData,
        items: newItems.length > 0 ? newItems : [{ name: '', sku: '', color: '', quantity: 1, price: '', manufacturerId: null }]
      });
    }
    // Clean up expanded state
    setExpandedSets(prev => {
      const newState = { ...prev };
      delete newState[setId];
      return newState;
    });
  };

  // Toggle set expansion in form view
  const toggleSetExpansion = (setId) => {
    setExpandedSets(prev => ({ ...prev, [setId]: !prev[setId] }));
  };

  // Helper to group items by setId for rendering
  const groupItemsForDisplay = (items) => {
    const groups = [];
    let currentSetId = null;
    let currentGroup = null;
    
    items.forEach((item, index) => {
      if (item.setId) {
        // Part of a set
        if (item.setId !== currentSetId) {
          // Start new set group
          currentSetId = item.setId;
          currentGroup = {
            type: 'set',
            setId: item.setId,
            setName: item.setName,
            items: [{ ...item, originalIndex: index }]
          };
          groups.push(currentGroup);
        } else {
          // Add to current set group
          currentGroup.items.push({ ...item, originalIndex: index });
        }
      } else {
        // Single item (not part of a set)
        currentSetId = null;
        currentGroup = null;
        groups.push({
          type: 'single',
          items: [{ ...item, originalIndex: index }]
        });
      }
    });
    
    return groups;
  };

  // Calculate total price for a set
  const getSetTotal = (setItems) => {
    return setItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const handleOpenModal = async (receipt = null) => {
    if (receipt) {
      // Fetch full receipt data including items
      const fullReceipt = await fetchReceiptById(receipt.id);
      const receiptData = fullReceipt || receipt;
      
      setEditingReceipt(receiptData);
      // Handle both snake_case (from DB) and camelCase (from mock data)
      const items = receiptData.items || receiptData.receipt_items || [];
      const formattedItems = items.map(item => ({
        id: item.id, // CRITICAL: preserve ID so updates don't break inventory links
        name: item.name || item.item_name || '',
        sku: item.sku || '',
        color: item.color || '',
        quantity: item.quantity || 1,
        price: item.price !== undefined && item.price !== null ? item.price : '',
        setId: item.set_id || item.setId || null,
        setName: item.set_name || item.setName || null,
        manufacturerId: item.manufacturer_id || item.manufacturerId || null
      }));
      
      setFormData({
        customerName: receiptData.customerName || receiptData.customer_name || '',
        customerPhone: receiptData.customerPhone || receiptData.customer_phone || '',
        customerEmail: receiptData.customerEmail || receiptData.customer_email || '',
        deliveryAddress: receiptData.deliveryAddress || receiptData.delivery_address || '',
        items: formattedItems.length > 0 ? formattedItems : [{ name: '', sku: '', color: '', quantity: 1, price: '', manufacturerId: null }],
        deliveryStatus: receiptData.deliveryStatus || receiptData.delivery_status || 'pending',
        deliveryDate: receiptData.deliveryDate || receiptData.delivery_date || '',
        internalNotes: receiptData.internalNotes || receiptData.internal_notes || '',
        itemStatuses: receiptData.itemStatuses || receiptData.item_statuses || formattedItems.map(item => ({
          itemName: item.name,
          sku: item.sku,
          status: 'pending',
          notes: ''
        })),
        customerPreferences: (() => {
          // fetchReceiptById returns {} when there is no preferences row, so
          // the old `|| { notes: customer_notes }` fallback never ran and a
          // save wrote customer_notes back as null. Normalize to the camelCase
          // keys the form binds to.
          const prefs = receiptData.customerPreferences || receiptData.customer_preferences || {};
          return {
            preferredDeliveryDay: prefs.preferredDeliveryDay || prefs.preferred_delivery_day || '',
            preferredTimeWindow: prefs.preferredTimeWindow || prefs.preferred_time_window || '',
            notes: prefs.notes || receiptData.notes || receiptData.customer_notes || ''
          };
        })(),
        // Address parts. The stored address is one string; show it in the
        // street field so it is visible and editable. handleSaveReceipt keeps
        // it exactly as stored unless the user changes it (see there).
        // Without these keys the save wiped the address.
        streetAddress: receiptData.deliveryAddress || receiptData.delivery_address || '',
        apartmentNumber: receiptData.apartmentNumber || receiptData.apartment_number || '',
        city: '',
        state: '',
        zipCode: '',
        // Fees (incl. taxExempt) and payment history — without these keys the
        // save wrote delivery_services=null / payment_history=[] and
        // recomputed the total without fees.
        deliveryServices: receiptData.deliveryServices || receiptData.delivery_services || {
          delivery: { enabled: false, price: '' },
          setup: { enabled: false, price: '' },
          wastePickup: { enabled: false, price: '' },
          pickup: { enabled: false, price: '' }
        },
        paymentHistory: receiptData.paymentHistory || receiptData.payment_history || [],
        // Payment fields
        amountPaid: receiptData.amountPaid || receiptData.amount_paid || 0,
        // Sales tracking
        salesperson: receiptData.salesperson || '',
        // SMS consent
        smsConsent: receiptData.smsConsent || receiptData.sms_consent || false
      });
    } else {
      setEditingReceipt(null);
      setFormData({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        deliveryAddress: '',
        streetAddress: '',
        apartmentNumber: '',
        city: '',
        state: '',
        zipCode: '',
        items: [],
        deliveryStatus: 'pending',
        deliveryDate: '',
        internalNotes: '',
        itemStatuses: [],
        customerPreferences: {
          preferredDeliveryDay: '',
          preferredTimeWindow: '',
          notes: ''
        },
        amountPaid: 0,
        salesperson: '',
        smsConsent: false
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingReceipt(null);
    setShowEditModalUnsavedWarning(false);
  };

  // Check if there are unsaved changes in the full edit modal
  const hasEditModalUnsavedChanges = () => {
    if (!showModal || !editingReceipt) return false;
    
    // Compare key fields between formData and editingReceipt
    const original = {
      customerName: editingReceipt.customerName || editingReceipt.customer_name || '',
      customerPhone: editingReceipt.customerPhone || editingReceipt.customer_phone || '',
      customerEmail: editingReceipt.customerEmail || editingReceipt.customer_email || '',
      deliveryAddress: editingReceipt.deliveryAddress || editingReceipt.delivery_address || '',
      deliveryDate: editingReceipt.deliveryDate || editingReceipt.delivery_date || '',
      deliveryStatus: editingReceipt.deliveryStatus || editingReceipt.delivery_status || 'pending',
      internalNotes: editingReceipt.internalNotes || editingReceipt.internal_notes || '',
      amountPaid: editingReceipt.amountPaid || editingReceipt.amount_paid || 0,
      salesperson: editingReceipt.salesperson || '',
      smsConsent: editingReceipt.smsConsent || editingReceipt.sms_consent || false,
    };
    
    const current = {
      customerName: formData.customerName || '',
      customerPhone: formData.customerPhone || '',
      customerEmail: formData.customerEmail || '',
      deliveryAddress: formData.deliveryAddress || '',
      deliveryDate: formData.deliveryDate || '',
      deliveryStatus: formData.deliveryStatus || 'pending',
      internalNotes: formData.internalNotes || '',
      amountPaid: formData.amountPaid || 0,
      salesperson: formData.salesperson || '',
      smsConsent: formData.smsConsent || false,
    };
    
    // Check basic fields
    for (const key of Object.keys(original)) {
      if (String(original[key]) !== String(current[key])) {
        return true;
      }
    }
    
    // Check items - compare length and content
    const originalItems = editingReceipt.items || [];
    const currentItems = formData.items || [];
    
    if (originalItems.length !== currentItems.length) return true;
    
    for (let i = 0; i < originalItems.length; i++) {
      const origItem = originalItems[i];
      const currItem = currentItems[i];
      
      if (
        (origItem.name || origItem.item_name || '') !== (currItem.name || currItem.item_name || '') ||
        (origItem.sku || '') !== (currItem.sku || '') ||
        (origItem.color || '') !== (currItem.color || '') ||
        Number(origItem.quantity || 1) !== Number(currItem.quantity || 1) ||
        Number(origItem.price || 0) !== Number(currItem.price || 0)
      ) {
        return true;
      }
    }
    
    return false;
  };

  // Attempt to close the full edit modal - shows warning if there are unsaved changes
  const handleAttemptCloseModal = () => {
    if (hasEditModalUnsavedChanges()) {
      setShowEditModalUnsavedWarning(true);
    } else {
      handleCloseModal();
    }
  };

  // Confirm discarding unsaved changes and close edit modal
  const confirmDiscardEditModalChanges = () => {
    setShowEditModalUnsavedWarning(false);
    handleCloseModal();
  };

  // Cancel discarding changes - just hide the warning
  const cancelDiscardEditModalChanges = () => {
    setShowEditModalUnsavedWarning(false);
  };

  // Print Receipt Function
  // Helper function to build receipt HTML content
  const buildReceiptHTML = async (receipt) => {
    // Self-heal: if the passed object is a "lite" record missing the delivery
    // address, fetch it so it can never silently drop from the printed receipt.
    if (receipt?.id && !(receipt.delivery_address || receipt.deliveryAddress)) {
      try {
        const { data: addrRow, error: addrErr } = await supabase
          .from('receipts')
          .select('delivery_address')
          .eq('id', receipt.id)
          .single();
        if (!addrErr && addrRow?.delivery_address) {
          receipt = { ...receipt, delivery_address: addrRow.delivery_address };
        }
      } catch (err) {
        devError('Error fetching delivery_address for print:', err);
      }
    }

    const storeName = currentStore?.name || 'Furniture Outlet';
    const storePhone = currentStore?.phone || '';
    const taxRate = parseFloat(storeSettings.tax_rate) || 8;
    
    // Get primary address from store_addresses, fallback to currentStore.address
    const primaryAddress = storeAddresses.find(a => a.is_primary);
    const storeAddress = primaryAddress?.address || currentStore?.address || '';
    
    // Get all store locations for footer
    const allLocations = storeAddresses.filter(a => a.address);
    
    // Get delivery services
    const deliveryServices = receipt.delivery_services || receipt.deliveryServices || {};
    const deliveryPrice = deliveryServices.delivery?.enabled ? parseFloat(deliveryServices.delivery.price) || 0 : 0;
    const setupPrice = deliveryServices.setup?.enabled ? parseFloat(deliveryServices.setup.price) || 0 : 0;
    const wastePrice = deliveryServices.wastePickup?.enabled ? parseFloat(deliveryServices.wastePickup.price) || 0 : 0;
    const pickupPrice = deliveryServices.pickup?.enabled ? parseFloat(deliveryServices.pickup.price) || 0 : 0;
    const hasDeliveryServices = deliveryPrice > 0 || setupPrice > 0 || wastePrice > 0 || pickupPrice > 0;
    const servicesTotal = deliveryPrice + setupPrice + wastePrice + pickupPrice;
    
    // Get payment history
    const paymentHistory = receipt.payment_history || receipt.paymentHistory || [];
    
    // Calculate totals with proper rounding to avoid floating point errors
    const items = receipt.items || [];
    const subtotal = Math.round((receipt.subtotal || items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0)) * 100) / 100;
    const tax = Math.round((receipt.tax || (subtotal * (taxRate / 100))) * 100) / 100;
    const total = Math.round((receipt.total || (subtotal + tax)) * 100) / 100;
    const amountPaid = Math.round((receipt.amount_paid || receipt.amountPaid || 0) * 100) / 100;
    const balance = Math.round((total - amountPaid) * 100) / 100;
    
    // Fetch delivery signatures for this receipt
    let receiptSignatures = [];
    try {
      const { data, error } = await supabase
        .from('delivery_signatures')
        .select('*')
        .eq('receipt_id', receipt.id)
        .order('signed_at', { ascending: true }); // Oldest first for print
      
      if (!error && data) {
        receiptSignatures = data;
      }
    } catch (err) {
      devError('Error fetching signatures for print:', err);
    }
    
    return { receipt, storeName, storePhone, storeAddress, allLocations, deliveryServices, deliveryPrice, setupPrice, wastePrice, pickupPrice, hasDeliveryServices, servicesTotal, paymentHistory, items, subtotal, tax, total, amountPaid, balance, receiptSignatures, taxRate };
  };

  // Share receipt via Web Share API with a nice formatted image
  const handleShareReceipt = async (receipt) => {
    const customerName = receipt.customer_name || receipt.customerName || 'Customer';
    const storeName = currentStore?.name || 'Furniture Outlet';
    const storePhone = currentStore?.phone || '';
    const primaryAddress = storeAddresses.find(a => a.is_primary);
    const storeAddress = primaryAddress?.address || currentStore?.address || '';
    
    // Get items and calculate totals
    const items = receipt.items || [];
    const subtotal = parseFloat(receipt.subtotal || 0).toFixed(2);
    const tax = parseFloat(receipt.tax || 0).toFixed(2);
    const total = parseFloat(receipt.total || 0).toFixed(2);
    const amountPaid = parseFloat(receipt.amount_paid || receipt.amountPaid || 0).toFixed(2);
    const balance = (parseFloat(total) - parseFloat(amountPaid)).toFixed(2);
    
    // Get delivery services
    const deliveryServices = receipt.delivery_services || receipt.deliveryServices || {};
    const deliveryPrice = deliveryServices.delivery?.enabled ? parseFloat(deliveryServices.delivery.price) || 0 : 0;
    const setupPrice = deliveryServices.setup?.enabled ? parseFloat(deliveryServices.setup.price) || 0 : 0;
    const wastePrice = deliveryServices.wastePickup?.enabled ? parseFloat(deliveryServices.wastePickup.price) || 0 : 0;
    const pickupPrice = deliveryServices.pickup?.enabled ? parseFloat(deliveryServices.pickup.price) || 0 : 0;
    const hasDeliveryServices = deliveryPrice > 0 || setupPrice > 0 || wastePrice > 0 || pickupPrice > 0;
    
    // Create the styled receipt HTML for image generation
    const receiptHTML = `
      <div id="share-receipt" style="
        width: 400px;
        padding: 30px;
        background: white;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        color: #000;
        border: 2px solid #333;
      ">
        <!-- Header -->
        <div style="text-align: center; border-bottom: 3px solid #333; padding-bottom: 15px; margin-bottom: 20px;">
          <div style="font-size: 24px; font-weight: bold; margin-bottom: 5px;">${storeName}</div>
          ${storeAddress ? `<div style="font-size: 12px; color: #444;">${storeAddress}</div>` : ''}
          ${storePhone ? `<div style="font-size: 12px; color: #444;">Phone: ${storePhone}</div>` : ''}
        </div>
        
        <!-- Receipt Title -->
        <div style="text-align: center; font-size: 18px; font-weight: bold; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 20px;">
          Sales Receipt
        </div>
        
        <!-- Receipt Info -->
        <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #ddd;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
            <span style="font-weight: 600; color: #555;">Date:</span>
            <span>${new Date(receipt.created_at || receipt.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
            <span style="font-weight: 600; color: #555;">Receipt #:</span>
            <span>${receipt.id}</span>
          </div>
        </div>
        
        <!-- Customer Info -->
        <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #ddd;">
          <div style="font-size: 12px; font-weight: bold; text-transform: uppercase; color: #333; margin-bottom: 8px;">Customer</div>
          <div style="margin-bottom: 3px;"><strong>${customerName}</strong></div>
          ${receipt.customer_phone || receipt.customerPhone ? `<div style="font-size: 12px; color: #666;">${receipt.customer_phone || receipt.customerPhone}</div>` : ''}
        </div>
        
        <!-- Items -->
        <div style="margin-bottom: 15px;">
          <div style="font-size: 12px; font-weight: bold; text-transform: uppercase; color: #333; margin-bottom: 10px;">Items</div>
          ${items.map(item => `
            <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee;">
              <div>
                <div style="font-weight: 500;">${item.name || item.item_name}</div>
                <div style="font-size: 11px; color: #666;">Qty: ${item.quantity || 1}${item.sku ? ` • SKU: ${item.sku}` : ''}</div>
              </div>
              <div style="font-weight: 500;">$${parseFloat(item.price || 0).toFixed(2)}</div>
            </div>
          `).join('')}
        </div>
        
        <!-- Services if any -->
        ${hasDeliveryServices ? `
          <div style="margin-bottom: 15px; padding-top: 10px; border-top: 1px solid #ddd;">
            <div style="font-size: 12px; font-weight: bold; text-transform: uppercase; color: #333; margin-bottom: 8px;">Services</div>
            ${deliveryPrice > 0 ? `<div style="display: flex; justify-content: space-between; padding: 4px 0;"><span>Delivery</span><span>$${deliveryPrice.toFixed(2)}</span></div>` : ''}
            ${setupPrice > 0 ? `<div style="display: flex; justify-content: space-between; padding: 4px 0;"><span>Setup</span><span>$${setupPrice.toFixed(2)}</span></div>` : ''}
            ${wastePrice > 0 ? `<div style="display: flex; justify-content: space-between; padding: 4px 0;"><span>Waste Pickup</span><span>$${wastePrice.toFixed(2)}</span></div>` : ''}
            ${pickupPrice > 0 ? `<div style="display: flex; justify-content: space-between; padding: 4px 0;"><span>Pickup</span><span>$${pickupPrice.toFixed(2)}</span></div>` : ''}
          </div>
        ` : ''}
        
        <!-- Totals -->
        <div style="background: #f5f5f5; padding: 15px; margin-top: 15px; border-radius: 4px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
            <span>Subtotal</span>
            <span>$${subtotal}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
            <span>Tax</span>
            <span>$${tax}</span>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 18px; font-weight: bold; padding-top: 8px; border-top: 2px solid #333;">
            <span>Total</span>
            <span>$${total}</span>
          </div>
          ${parseFloat(amountPaid) > 0 ? `
            <div style="display: flex; justify-content: space-between; margin-top: 8px; color: #22c55e;">
              <span>Amount Paid</span>
              <span>$${amountPaid}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-weight: bold; color: ${parseFloat(balance) > 0 ? '#ef4444' : '#22c55e'};">
              <span>Balance</span>
              <span>$${balance}</span>
            </div>
          ` : ''}
        </div>
        
        <!-- Footer -->
        <div style="text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 12px; color: #666;">
          Thank you for your business!
        </div>
      </div>
    `;
    
    try {
      // Create a temporary container
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.innerHTML = receiptHTML;
      document.body.appendChild(container);
      
      const receiptElement = container.querySelector('#share-receipt');
      
      // Try to use html2canvas if available, otherwise fall back to text share
      if (typeof html2canvas !== 'undefined') {
        const canvas = await html2canvas(receiptElement, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false
        });
        
        // Convert canvas to blob
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        const file = new File([blob], `receipt-${receipt.id}.png`, { type: 'image/png' });
        
        document.body.removeChild(container);
        
        // Check if file sharing is supported
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `Receipt #${receipt.id} - ${storeName}`,
            files: [file]
          });
          devLog('Receipt image shared successfully');
        } else if (navigator.share) {
          // Fall back to text share if file sharing not supported
          const shareText = `📋 Receipt from ${storeName}\n\nCustomer: ${customerName}\nTotal: $${total}\nReceipt #${receipt.id}`;
          await navigator.share({ title: `Receipt #${receipt.id}`, text: shareText });
        } else {
          // Download the image instead
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `receipt-${receipt.id}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } else {
        // html2canvas not loaded - try loading it dynamically
        document.body.removeChild(container);
        
        // Show loading state
        const loadingToast = document.createElement('div');
        loadingToast.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:#333;color:#fff;padding:20px 30px;border-radius:12px;z-index:99999;font-family:system-ui;';
        loadingToast.textContent = 'Generating receipt image...';
        document.body.appendChild(loadingToast);
        
        // Load html2canvas dynamically
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script.onload = async () => {
          document.body.removeChild(loadingToast);
          // Retry the share after loading
          handleShareReceipt(receipt);
        };
        script.onerror = () => {
          document.body.removeChild(loadingToast);
          // Fall back to text share
          const shareText = `📋 Receipt from ${storeName}\n\nCustomer: ${customerName}\nDate: ${new Date(receipt.created_at || receipt.createdAt).toLocaleDateString()}\n\nItems:\n${items.map(item => `• ${item.name || item.item_name} (${item.quantity || 1}x) - $${parseFloat(item.price || 0).toFixed(2)}`).join('\n')}\n\nTotal: $${total}\n\nReceipt #${receipt.id}`;
          if (navigator.share) {
            navigator.share({ title: `Receipt #${receipt.id}`, text: shareText });
          } else {
            navigator.clipboard.writeText(shareText);
            alert('Receipt copied to clipboard!');
          }
        };
        document.body.appendChild(script);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        devError('Share failed:', err);
        // Fall back to text share
        const shareText = `📋 Receipt from ${storeName}\n\nCustomer: ${customerName}\nTotal: $${total}\nReceipt #${receipt.id}`;
        if (navigator.share) {
          try {
            await navigator.share({ title: `Receipt #${receipt.id}`, text: shareText });
          } catch (e) {
            if (e.name !== 'AbortError') {
              navigator.clipboard.writeText(shareText);
              alert('Receipt copied to clipboard!');
            }
          }
        } else {
          navigator.clipboard.writeText(shareText);
          alert('Receipt copied to clipboard!');
        }
      }
    }
  };

  const handlePrintReceipt = async (receiptArg) => {
    const data = await buildReceiptHTML(receiptArg);
    const { receipt, storeName, storePhone, storeAddress, allLocations, deliveryServices, deliveryPrice, setupPrice, wastePrice, pickupPrice, hasDeliveryServices, servicesTotal, paymentHistory, items, subtotal, tax, total, amountPaid, balance, receiptSignatures, taxRate } = data;
    
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Receipt - ${receipt.customer_name || receipt.customerName}</title>
        <style>
          @media screen {
            body { padding: 24px 16px; }
          }

          @page {
            size: letter;
            margin: 0.5in;
          }

          @media print {
            html, body { margin: 0; padding: 0; }
          }

          html, body {
            width: 100%;
            margin: 0 auto;
          }

          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            max-width: 7.5in;
            margin: 0 auto;
            padding: 0;
            background: white;
            color: #000;
            box-sizing: border-box;
            font-size: 12px;
            line-height: 1.4;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .receipt-container {
            border: 2px solid #333;
            padding: 20px;
            background: white;
            box-sizing: border-box;
          }
          
          .header {
            text-align: center;
            border-bottom: 3px solid #333;
            padding-bottom: 12px;
            margin-bottom: 16px;
          }
          
          .store-name {
            font-size: 22px;
            font-weight: bold;
            color: #000;
            margin: 0 0 4px 0;
            letter-spacing: 1px;
          }
          
          .store-info {
            font-size: 11px;
            color: #444;
            line-height: 1.4;
          }
          
          .receipt-title {
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            margin: 12px 0;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          
          .section {
            margin: 14px 0;
          }
          
          .section-title {
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
            color: #333;
            border-bottom: 2px solid #ddd;
            padding-bottom: 5px;
            margin-bottom: 8px;
            letter-spacing: 1px;
          }
          
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            border-bottom: 1px solid #eee;
          }
          
          .info-label {
            font-weight: 600;
            color: #555;
          }
          
          .info-value {
            color: #000;
          }
          
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 12px 0;
            table-layout: fixed;
          }
          
          .items-table th {
            background: #f5f5f5;
            padding: 8px 6px;
            text-align: left;
            font-weight: bold;
            border-bottom: 2px solid #333;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .items-table th:first-child { width: 50%; }
          .items-table th:nth-child(2) { width: 10%; text-align: center; }
          .items-table th:nth-child(3) { width: 20%; text-align: right; }
          .items-table th:nth-child(4) { width: 20%; text-align: right; }
          
          .items-table td {
            padding: 12px 8px;
            border-bottom: 1px solid #ddd;
            vertical-align: top;
          }
          
          .items-table td:nth-child(2) { text-align: center; }
          .items-table td:nth-child(3) { text-align: right; }
          .items-table td:nth-child(4) { text-align: right; }
          
          .items-table tr:last-child td {
            border-bottom: 2px solid #333;
          }
          
          .item-name {
            font-weight: 600;
            color: #000;
          }
          
          .item-sku {
            font-size: 11px;
            color: #666;
            font-family: 'Courier New', monospace;
          }
          
          .item-description {
            font-size: 12px;
            color: #555;
            font-style: italic;
            margin-top: 4px;
            line-height: 1.4;
          }
          
          .text-right {
            text-align: right;
          }
          
          .services-section {
            margin: 12px 0;
            padding: 10px;
            background: #f9f9f9;
            border: 1px solid #ddd;
            border-radius: 4px;
          }
          
          .services-title {
            font-size: 11px;
            font-weight: bold;
            text-transform: uppercase;
            color: #555;
            margin-bottom: 6px;
            letter-spacing: 0.5px;
          }
          
          .service-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 12px;
          }
          
          .service-label {
            color: #555;
          }
          
          .service-price {
            font-weight: 600;
            color: #000;
          }
          
          .payment-history {
            margin: 12px 0;
            padding: 10px;
            background: #f0fdf4;
            border: 1px solid #86efac;
            border-radius: 4px;
          }
          
          .payment-history-title {
            font-size: 11px;
            font-weight: bold;
            text-transform: uppercase;
            color: #166534;
            margin-bottom: 6px;
            letter-spacing: 0.5px;
          }
          
          .payment-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            border-bottom: 1px solid #bbf7d0;
            font-size: 12px;
          }
          
          .payment-row:last-child {
            border-bottom: none;
          }
          
          .payment-info {
            color: #166534;
          }
          
          .payment-date {
            font-size: 11px;
            color: #4ade80;
          }
          
          .payment-amount {
            font-weight: 600;
            color: #166534;
          }
          
          .totals {
            margin-top: 14px;
            border-top: 3px solid #333;
            padding-top: 10px;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            font-size: 13px;
          }
          
          .total-row.grand-total {
            font-size: 18px;
            font-weight: bold;
            border-top: 2px solid #333;
            border-bottom: 3px double #333;
            padding: 10px 0;
            margin-top: 6px;
          }
          
          .total-row.balance {
            font-size: 16px;
            font-weight: bold;
            color: ${balance > 0 ? '#dc2626' : '#16a34a'};
            margin-top: 6px;
          }
          
          .footer {
            margin-top: 20px;
            padding-top: 12px;
            border-top: 2px solid #ddd;
            text-align: center;
            color: #666;
            font-size: 11px;
            line-height: 1.5;
          }
          
          .footer-highlight {
            font-weight: bold;
            color: #000;
            font-size: 13px;
            margin-top: 10px;
          }
          
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 24px;
            background: #000;
            color: white;
            border: none;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          }
          
          .print-button:hover {
            background: #333;
          }
          
          @media print {
            .print-button { display: none; }
          }
          
          .badge {
            display: inline-block;
            padding: 4px 12px;
            background: #f0f0f0;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .badge.pending { background: #fef3c7; color: #92400e; }
          .badge.processing { background: #dbeafe; color: #1e40af; }
          .badge.delivered { background: #d1fae5; color: #065f46; }
          
          .locations-section {
            margin-top: 25px;
            padding-top: 20px;
            border-top: 1px dashed #ccc;
          }
          
          .locations-title {
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
            color: #666;
            margin-bottom: 12px;
            letter-spacing: 0.5px;
          }
          
          .location-item {
            font-size: 12px;
            color: #555;
            margin-bottom: 8px;
            padding-left: 12px;
            border-left: 2px solid #ddd;
          }
          
          .location-name {
            font-weight: 600;
            color: #333;
          }
          
          .signatures-section {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 3px solid #333;
            page-break-inside: avoid;
          }
          
          .signatures-title {
            font-size: 16px;
            font-weight: bold;
            text-transform: uppercase;
            color: #333;
            margin-bottom: 20px;
            letter-spacing: 1px;
            text-align: center;
          }
          
          .signature-card {
            border: 1px solid #ddd;
            border-radius: 8px;
            margin-bottom: 20px;
            overflow: hidden;
            page-break-inside: avoid;
          }
          
          .signature-header {
            background: #f5f5f5;
            padding: 12px 15px;
            border-bottom: 1px solid #ddd;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .signature-type {
            font-weight: 600;
            color: #333;
            font-size: 14px;
          }
          
          .signature-type.partial {
            color: #d97706;
          }
          
          .signature-badge {
            display: inline-block;
            padding: 2px 8px;
            background: #fef3c7;
            color: #92400e;
            font-size: 10px;
            font-weight: 600;
            border-radius: 4px;
            text-transform: uppercase;
            margin-left: 8px;
          }
          
          .signature-date {
            font-size: 12px;
            color: #666;
          }
          
          .signature-items {
            padding: 12px 15px;
            border-bottom: 1px solid #eee;
            background: #fafafa;
          }
          
          .signature-items-title {
            font-size: 11px;
            font-weight: 600;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 8px;
          }
          
          .signature-item {
            font-size: 13px;
            color: #333;
            padding: 4px 0;
            display: flex;
            justify-content: space-between;
          }
          
          .signature-item-name {
            font-weight: 500;
          }
          
          .signature-item-sku {
            font-family: 'Courier New', monospace;
            font-size: 11px;
            color: #666;
          }
          
          .signature-image-container {
            padding: 15px;
            text-align: center;
          }
          
          .signature-image-label {
            font-size: 11px;
            font-weight: 600;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 8px;
          }
          
          .signature-image {
            max-width: 300px;
            max-height: 120px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: white;
          }
          
          .signature-count {
            font-size: 12px;
            color: #666;
            text-align: right;
          }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">🖨️ Print Receipt</button>
        
        <div class="receipt-container">
          <div class="header">
            <div class="store-name">${storeName}</div>
            <div class="store-info">
              ${allLocations.length > 0 ? allLocations.map(loc => `
                <div style="margin-bottom: 4px;">
                  <strong>${loc.name}${loc.is_primary ? '' : ''}</strong><br>
                  ${loc.address}
                </div>
              `).join('') : (storeAddress ? `${storeAddress}<br>` : '')}
              ${storePhone ? `<div style="margin-top: 8px;">Phone: ${storePhone}</div>` : ''}
            </div>
          </div>
          
          <div class="receipt-title">Sales Receipt</div>
          
          <div class="section">
            <div class="section-title">Receipt Information</div>
            <div class="info-row">
              <span class="info-label">Date:</span>
              <span class="info-value">${new Date(receipt.created_at || receipt.createdAt).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</span>
            </div>
            ${receipt.delivery_date || receipt.deliveryDate ? `
            <div class="info-row">
              <span class="info-label">Projected Delivery Date:</span>
              <span class="info-value">${new Date(receipt.delivery_date || receipt.deliveryDate).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric'
              })}</span>
            </div>
            ` : ''}
            <div class="info-row">
              <span class="info-label">Status:</span>
              <span class="info-value">
                <span class="badge ${receipt.delivery_status || receipt.deliveryStatus}">${(receipt.delivery_status || receipt.deliveryStatus || 'pending').replace(/_/g, ' ')}</span>
              </span>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Customer Information</div>
            <div class="info-row">
              <span class="info-label">Name:</span>
              <span class="info-value">${receipt.customer_name || receipt.customerName}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Phone:</span>
              <span class="info-value">${receipt.customer_phone || receipt.customerPhone}</span>
            </div>
            ${receipt.customer_email || receipt.customerEmail ? `
            <div class="info-row">
              <span class="info-label">Email:</span>
              <span class="info-value">${receipt.customer_email || receipt.customerEmail}</span>
            </div>
            ` : ''}
            ${receipt.delivery_address || receipt.deliveryAddress ? `
            <div class="info-row">
              <span class="info-label">Delivery Address:</span>
              <span class="info-value">${receipt.delivery_address || receipt.deliveryAddress}</span>
            </div>
            ` : ''}
          </div>
          
          <div class="section">
            <div class="section-title">Items Purchased</div>
            <table class="items-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                ${items.map(item => `
                  <tr>
                    <td>
                      <div class="item-name">${item.name || item.item_name}</div>
                      <div class="item-sku">SKU: ${item.sku}</div>
                      ${item.color ? `<div class="item-sku">Color: ${item.color}</div>` : ''}
                      ${item.description ? `<div class="item-description">${item.description}</div>` : ''}
                    </td>
                    <td>${item.quantity || 1}</td>
                    <td>$${(item.price || 0).toFixed(2)}</td>
                    <td>$${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          ${hasDeliveryServices ? `
          <div class="services-section">
            <div class="services-title">🚚 Delivery Services</div>
            ${deliveryPrice > 0 ? `
            <div class="service-row">
              <span class="service-label">Delivery</span>
              <span class="service-price">$${deliveryPrice.toFixed(2)}</span>
            </div>
            ` : ''}
            ${setupPrice > 0 ? `
            <div class="service-row">
              <span class="service-label">Setup / Assembly</span>
              <span class="service-price">$${setupPrice.toFixed(2)}</span>
            </div>
            ` : ''}
            ${wastePrice > 0 ? `
            <div class="service-row">
              <span class="service-label">Waste Pickup</span>
              <span class="service-price">$${wastePrice.toFixed(2)}</span>
            </div>
            ` : ''}
            ${pickupPrice > 0 ? `
            <div class="service-row">
              <span class="service-label">Customer Pickup</span>
              <span class="service-price">$${pickupPrice.toFixed(2)}</span>
            </div>
            ` : ''}
            <div class="service-row" style="border-top: 1px solid #ccc; padding-top: 8px; margin-top: 4px;">
              <span class="service-label" style="font-weight: 600;">Services Total</span>
              <span class="service-price">$${servicesTotal.toFixed(2)}</span>
            </div>
          </div>
          ` : ''}
          
          <div class="totals">
            <div class="total-row">
              <span>Subtotal:</span>
              <span>$${subtotal.toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>Tax (${taxRate}%):</span>
              <span>$${tax.toFixed(2)}</span>
            </div>
            <div class="total-row grand-total">
              <span>TOTAL:</span>
              <span>$${total.toFixed(2)}</span>
            </div>
          </div>
          
          ${paymentHistory.length > 0 || amountPaid > 0 ? `
          <div class="payment-history">
            <div class="payment-history-title">💳 Payment History</div>
            ${paymentHistory.length > 0 ? paymentHistory.map(payment => `
            <div class="payment-row">
              <div class="payment-info">
                <div>${payment.note || 'Payment'}</div>
                <div class="payment-date">${new Date(payment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
              </div>
              <div class="payment-amount">$${parseFloat(payment.amount).toFixed(2)}</div>
            </div>
            `).join('') : `
            <div class="payment-row">
              <div class="payment-info">
                <div>Payment Received</div>
              </div>
              <div class="payment-amount">$${amountPaid.toFixed(2)}</div>
            </div>
            `}
            <div class="payment-row" style="border-top: 2px solid #86efac; padding-top: 10px; margin-top: 8px;">
              <div class="payment-info" style="font-weight: 700;">Total Paid</div>
              <div class="payment-amount" style="font-size: 16px;">$${amountPaid.toFixed(2)}</div>
            </div>
            <div class="payment-row" style="background: ${balance > 0 ? '#fef2f2' : '#f0fdf4'}; margin: 8px -15px -15px -15px; padding: 12px 15px; border-radius: 0 0 4px 4px;">
              <div style="font-weight: 700; color: ${balance > 0 ? '#dc2626' : '#16a34a'};">Balance ${balance <= 0 ? 'PAID' : 'DUE'}</div>
              <div style="font-weight: 700; font-size: 18px; color: ${balance > 0 ? '#dc2626' : '#16a34a'};">$${Math.abs(balance).toFixed(2)}</div>
            </div>
          </div>
          ` : ''}
          
          ${receiptSignatures.length > 0 ? `
          <div class="signatures-section">
            <div class="signatures-title">✍️ Delivery Signatures</div>
            ${receiptSignatures.map((sig, index) => `
              <div class="signature-card">
                <div class="signature-header">
                  <div>
                    <span class="signature-type ${sig.is_partial ? 'partial' : ''}">
                      ${sig.is_partial ? 'Partial Delivery' : 'Full Delivery'} #${index + 1}
                      ${sig.is_partial ? '<span class="signature-badge">PARTIAL</span>' : ''}
                    </span>
                  </div>
                  <div>
                    <span class="signature-date">${new Date(sig.signed_at).toLocaleString()}</span>
                    <span class="signature-count">${sig.total_items_in_signature || 0} item${(sig.total_items_in_signature || 0) !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                ${sig.items_delivered && sig.items_delivered.length > 0 ? `
                <div class="signature-items">
                  <div class="signature-items-title">Items in This Delivery:</div>
                  ${sig.items_delivered.map(item => `
                    <div class="signature-item">
                      <span class="signature-item-name">${item.quantity || 1}x ${item.name}</span>
                      <span class="signature-item-sku">${item.sku || ''}</span>
                    </div>
                  `).join('')}
                </div>
                ` : ''}
                <div class="signature-image-container">
                  <div class="signature-image-label">Customer Signature:</div>
                  <img src="${sig.signature_data}" alt="Customer Signature" class="signature-image" />
                </div>
              </div>
            `).join('')}
          </div>
          ` : ''}
          
          <div class="footer">
            Thank you for your business!<br>
            ${storePhone ? `Questions? Call us at ${storePhone}` : ''}
            <div class="footer-highlight">
              We appreciate your trust in ${storeName}
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    if (isIOS) {
      // iOS: print via an iframe sized to the device viewport. A fixed 800px
      // frame on a ~390px phone is what caused the "max zoom" — iOS scales the
      // frame's CSS pixel width onto the page. Sizing to innerWidth and letting
      // the document's @page rule govern the sheet prints at the correct scale.
      let printFrame = document.getElementById('receipt-print-frame');
      if (printFrame) {
        printFrame.remove(); // start clean each time to avoid stale layout
      }
      printFrame = document.createElement('iframe');
      printFrame.id = 'receipt-print-frame';
      printFrame.setAttribute('aria-hidden', 'true');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '100vw';
      printFrame.style.height = '100vh';
      printFrame.style.border = '0';
      printFrame.style.opacity = '0';
      printFrame.style.pointerEvents = 'none';
      printFrame.style.zIndex = '-1';
      document.body.appendChild(printFrame);

      printFrame.contentDocument.open();
      printFrame.contentDocument.write(printContent);
      printFrame.contentDocument.close();

      // Wait for content (and any images) to lay out, then print.
      setTimeout(() => {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
        } catch (err) {
          devError('iOS print failed:', err);
        }
      }, 600);
    } else {
      // Desktop: Use window.open approach
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // Popup blocked - fallback to iframe
        let printFrame = document.getElementById('receipt-print-frame');
        if (!printFrame) {
          printFrame = document.createElement('iframe');
          printFrame.id = 'receipt-print-frame';
          printFrame.style.position = 'absolute';
          printFrame.style.top = '-10000px';
          document.body.appendChild(printFrame);
        }
        printFrame.contentDocument.open();
        printFrame.contentDocument.write(printContent);
        printFrame.contentDocument.close();
        setTimeout(() => {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
        }, 500);
      }
    }
  };

  const handleSaveReceipt = async () => {
    // Prevent double-click
    if (isSavingReceipt) {
      devLog('🛡️ Save already in progress');
      return;
    }
    setIsSavingReceipt(true);

    // Sanitize all input data
    const cleanData = sanitizeReceiptData(formData);
    
    // Combine separate address fields into deliveryAddress
    const addressParts = [
      formData.streetAddress?.trim(),
      formData.apartmentNumber?.trim(),
      formData.city?.trim(),
      formData.state?.trim(),
      formData.zipCode?.trim()
    ].filter(Boolean);
    const streetTrim = formData.streetAddress?.trim() || '';
    const aptTrim = formData.apartmentNumber?.trim() || '';
    // When editing, the street field is seeded with the stored full address
    // (handleOpenModal). If the user did not touch the address fields, keep
    // the stored string exactly as it is instead of rebuilding it.
    const storedAddress = editingReceipt
      ? (editingReceipt.deliveryAddress || editingReceipt.delivery_address || '')
      : '';
    const addressUntouched = !!editingReceipt
      && streetTrim === storedAddress.trim()
      && !formData.city?.trim() && !formData.state?.trim() && !formData.zipCode?.trim();
    // If the street was edited but still carries ", Apt N", don't append it twice.
    const aptAlreadyInStreet = !!aptTrim && streetTrim.toLowerCase().includes(('apt ' + aptTrim).toLowerCase());
    if (addressUntouched) {
      cleanData.deliveryAddress = storedAddress;
    } else {
      cleanData.deliveryAddress = addressParts.length > 0
        ? `${streetTrim}${aptTrim && !aptAlreadyInStreet ? ', Apt ' + aptTrim : ''}${formData.city ? ', ' + formData.city.trim() : ''}${formData.state ? ', ' + formData.state.trim().toUpperCase() : ''}${formData.zipCode ? ' ' + formData.zipCode.trim() : ''}`.replace(/^,\s*/, '').trim()
        : '';
    }
    
      const { subtotal, tax, total } = calculateTotals(cleanData.items, formData.deliveryServices);
    
      if (editingReceipt) {
      // Update existing receipt
      try {
        // Check if status is changing to delivered - use formData directly for reliable check
        // SSOT: picked_up closes an order exactly like delivered. Force-
        // completing via the dropdown as Picked Up previously closed the
        // receipt silently — no survey_status, no review request. Keep the
        // variable names; every downstream side-effect now fires for both.
        const wasDelivered = ITEM_TERMINAL_STATUSES.includes(editingReceipt.deliveryStatus) || ITEM_TERMINAL_STATUSES.includes(editingReceipt.delivery_status);
        const isNowDelivered = ITEM_TERMINAL_STATUSES.includes(formData.deliveryStatus);
        const justDelivered = !wasDelivered && isNowDelivered;
        
        devLog('📊 Status change check:', { wasDelivered, isNowDelivered, justDelivered, formDataStatus: formData.deliveryStatus });
        
        // Track the status we ultimately persist. If NEW items get added to a
        // receipt that's already completed (delivered / picked_up), we demote
        // it to 'partially_delivered' further below so it moves back to the
        // New Orders tab — the tab filter is driven purely by this field.
        let finalDeliveryStatus = formData.deliveryStatus || 'pending';
        
        // Check if SMS consent is being newly granted
        const hadSmsConsent = editingReceipt.smsConsent || editingReceipt.sms_consent;
        const hasSmsConsent = formData.smsConsent;
        const newlyConsented = hasSmsConsent && !hadSmsConsent;

        const { error: updateError } = await supabase
          .from('receipts')
          .update({
            customer_name: cleanData.customerName,
            customer_phone: cleanData.customerPhone,
            customer_email: cleanData.customerEmail || null,
            delivery_address: cleanData.deliveryAddress || null,
            apartment_number: formData.apartmentNumber || null,
            delivery_status: formData.deliveryStatus || 'pending',
            delivery_date: cleanData.deliveryDate || null,  // Convert empty string to null
            customer_notes: cleanData.customerPreferences?.notes || null, // Delivery instructions
            internal_notes: cleanData.internalNotes || null, // AI Customer Care Notes
            subtotal,
            tax,
            total,
            // Payment tracking
            amount_paid: parseFloat(cleanData.amountPaid) || 0,
            payment_history: formData.paymentHistory || [],
            // Sales tracking
            salesperson: cleanData.salesperson || null,
            // Delivery services
            delivery_services: formData.deliveryServices || null,
            // SMS consent (TCPA compliance)
            sms_consent: formData.smsConsent || false,
            ...(newlyConsented && { sms_consent_at: new Date().toISOString() }),
            // Initialize survey when marked as delivered
            ...(justDelivered && { survey_status: 'pending' })
          })
          .eq('id', editingReceipt.id);

        if (updateError) {
          devError('Error updating receipt:', updateError);
          throw new Error(`Failed to update receipt: ${updateError.message}`);
        }

        // UPDATE ITEMS - Smart update: preserve existing items, only add new ones
        if (cleanData.items && cleanData.items.length > 0) {
          // Filter out empty items
          const validItems = cleanData.items.filter(item => 
            (item.name && item.name.trim()) || (item.sku && item.sku.trim())
          );
          
          // Separate items with IDs (existing) from new items (no ID)
          const existingItems = validItems.filter(item => item.id);
          const newItems = validItems.filter(item => !item.id);
          
          devLog(`📦 Processing ${existingItems.length} existing items, ${newItems.length} new items`);

          // DELETE removed items: any receipt_item row in the DB whose id is no
          // longer present in the form was deleted by the user (e.g. removing a
          // piece from a saved group). Without this, those rows persist and the
          // item reappears on reload. Release their inventory + statuses first.
          try {
            const { data: dbItems, error: dbItemsError } = await supabase
              .from('receipt_items')
              .select('id')
              .eq('receipt_id', editingReceipt.id);

            if (dbItemsError) {
              devError('Error fetching existing receipt items for reconcile:', dbItemsError);
            } else {
              const keptIds = new Set(existingItems.map(i => i.id));
              const removedIds = (dbItems || [])
                .map(r => r.id)
                .filter(id => !keptIds.has(id));

              if (removedIds.length > 0) {
                devLog(`🗑️ Deleting ${removedIds.length} removed item(s):`, removedIds);

                // Release any inventory units reserved for the removed items
                // back into the available pool, and drop phantom units entirely.
                const { data: linkedUnits, error: unitsError } = await supabase
                  .from('inventory_units')
                  .select('id, notes')
                  .in('receipt_item_id', removedIds);

                if (unitsError) {
                  devError('Error fetching units for removed items:', unitsError);
                } else if (linkedUnits && linkedUnits.length > 0) {
                  const phantomUnitIds = [];
                  const releaseUnitIds = [];
                  for (const u of linkedUnits) {
                    if ((u.notes || '').toLowerCase().includes('no pre-existing stock')) {
                      phantomUnitIds.push(u.id);
                    } else {
                      releaseUnitIds.push(u.id);
                    }
                  }

                  if (releaseUnitIds.length > 0) {
                    const { error: relErr } = await supabase
                      .from('inventory_units')
                      .update({
                        status: 'available',
                        reserved_for_receipt_id: null,
                        receipt_item_id: null,
                        reserved_at: null,
                        updated_at: new Date().toISOString()
                      })
                      .in('id', releaseUnitIds);
                    if (relErr) devError('Error releasing units for removed items:', relErr);
                  }

                  if (phantomUnitIds.length > 0) {
                    const { error: phErr } = await supabase
                      .from('inventory_units')
                      .delete()
                      .in('id', phantomUnitIds);
                    if (phErr) devError('Error deleting phantom units for removed items:', phErr);
                  }
                }

                // Remove item_statuses rows for the removed items
                const { error: statusErr } = await supabase
                  .from('item_statuses')
                  .delete()
                  .in('receipt_item_id', removedIds);
                if (statusErr) devError('Error deleting item_statuses for removed items:', statusErr);

                // Finally delete the receipt_items rows themselves
                const { error: delItemsErr } = await supabase
                  .from('receipt_items')
                  .delete()
                  .in('id', removedIds);
                if (delItemsErr) {
                  devError('Error deleting removed receipt items:', delItemsErr);
                } else {
                  devLog(`✅ Deleted ${removedIds.length} removed item(s)`);
                }
              }
            }
          } catch (reconcileError) {
            devError('Item reconcile/delete failed:', reconcileError);
          }
          
          // UPDATE existing items in place (preserves inventory links!)
          for (const item of existingItems) {
            const { error: updateItemError } = await supabase
              .from('receipt_items')
              .update({
                item_name: item.name,
                sku: ensureSku(item.sku),
                color: item.color || '',
                quantity: item.quantity || 1,
                price: parseFloat(item.price) || 0,
                set_id: item.setId || null,
                set_name: item.setName || null,
                manufacturer_id: item.manufacturerId || null
              })
              .eq('id', item.id);
            
            if (updateItemError) {
              devError('Error updating item:', item.id, updateItemError);
            }
          }
          
          // Only INSERT genuinely new items
          if (newItems.length > 0) {
            // Explode items with qty > 1 into individual line items for partial delivery support
            const explodedItems = explodeItems(newItems);
            devLog(`📦 Exploded ${newItems.length} new items → ${explodedItems.length} line items`);
            
            const itemsToInsert = explodedItems.map((item, index) => ({
              receipt_id: editingReceipt.id,
              item_name: item.name,
              sku: ensureSku(item.sku),
              color: item.color,
              quantity: 1,
              price: parseFloat(item.price) || 0,
              position: existingItems.length + index,
              set_id: item.setId || null,
              set_name: item.setName || null,
              manufacturer_id: item.manufacturerId || null
            }));

            const { data: insertedItems, error: insertItemsError } = await supabase
              .from('receipt_items')
              .insert(itemsToInsert)
              .select('id, sku, item_name, position, manufacturer_id');

            if (insertItemsError) {
              devError('Error inserting new items:', insertItemsError);
            } else {
              devLog('✅ New items inserted, got IDs:', insertedItems?.map(i => i.id));
              
              // BUGFIX: A completed receipt that receives a NEW item is no
              // longer fully fulfilled. If the user didn't explicitly change
              // the status dropdown in this edit, demote delivered/picked_up
              // → partially_delivered so the receipt returns to New Orders.
              // (The existing per-item delivery flow promotes it back to
              // 'delivered' once every item reaches a terminal status.)
              const originalStatus = editingReceipt.deliveryStatus || editingReceipt.delivery_status;
              const statusUntouched = formData.deliveryStatus === originalStatus;
              if (statusUntouched && ITEM_TERMINAL_STATUSES.includes(finalDeliveryStatus)) {
                const { error: demoteError } = await supabase
                  .from('receipts')
                  .update({ delivery_status: 'partially_delivered' })
                  .eq('id', editingReceipt.id);
                
                if (demoteError) {
                  devError('Error moving completed receipt back to active:', demoteError);
                } else {
                  finalDeliveryStatus = 'partially_delivered';
                  devLog('🔄 New item on completed receipt → demoted to partially_delivered (back to New Orders tab)');
                  addNotification({ title: 'Order Reopened', message: 'New item added — order moved back to New Orders' });
                }
              }
              
              // AUTO-MATCH: Check if any new items can be matched to available inventory
              // Matches on BOTH SKU + manufacturer_id (if set) for data integrity
              devLog('🔍 Checking inventory for matching items...');
              
              for (const insertedItem of (insertedItems || [])) {
                if (!insertedItem.sku) continue;
                
                // Find an available unit for this SKU + manufacturer
                let availableUnit = null;
                
                if (currentStore?.id) {
                  // Try store-specific first
                  let storeQuery = supabase
                    .from('inventory_units')
                    .select('id, sku, unit_serial, store_id')
                    .ilike('sku', insertedItem.sku)
                    .eq('status', 'available')
                    .eq('store_id', currentStore.id)
                    .is('reserved_for_receipt_id', null)
                    .is('receipt_item_id', null);
                  
                  // Only filter by manufacturer if receipt item has one (backwards compatible)
                  if (insertedItem.manufacturer_id) {
                    storeQuery = storeQuery.eq('manufacturer_id', insertedItem.manufacturer_id);
                  }
                  
                  const { data: storeUnit } = await storeQuery.limit(1).maybeSingle();
                  
                  if (storeUnit) {
                    availableUnit = storeUnit;
                  }
                }
                
                // If no store-specific, try any available
                if (!availableUnit) {
                  let anyQuery = supabase
                    .from('inventory_units')
                    .select('id, sku, unit_serial, store_id')
                    .ilike('sku', insertedItem.sku)
                    .eq('status', 'available')
                    .is('reserved_for_receipt_id', null)
                    .is('receipt_item_id', null);
                  
                  if (insertedItem.manufacturer_id) {
                    anyQuery = anyQuery.eq('manufacturer_id', insertedItem.manufacturer_id);
                  }
                  
                  const { data: anyUnit } = await anyQuery.limit(1).maybeSingle();
                  
                  if (anyUnit) {
                    availableUnit = anyUnit;
                  }
                }
                
                if (!availableUnit) {
                  devLog(`⏳ ${insertedItem.sku} (item ${insertedItem.id}) - no available units`);
                  continue;
                }
                
                devLog(`📦 Found available unit for ${insertedItem.sku}:`, availableUnit.id);
                
                // Reserve the unit FOR THIS SPECIFIC ITEM
                const { error: reserveErr } = await supabase
                  .from('inventory_units')
                  .update({ 
                    status: 'reserved',
                    reserved_for_receipt_id: editingReceipt.id,
                    receipt_item_id: insertedItem.id,
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', availableUnit.id);
                
                if (reserveErr) {
                  devError(`Error reserving unit for ${insertedItem.sku}:`, reserveErr);
                  continue;
                }
                
                // Create item_statuses entry
                await supabase
                  .from('item_statuses')
                  .upsert([{
                    receipt_id: editingReceipt.id,
                    receipt_item_id: insertedItem.id,
                    item_name: insertedItem.item_name,
                    sku: insertedItem.sku,
                    status: 'at_store',
                    notes: `Auto-assigned from inventory`
                  }], {
                    onConflict: 'receipt_id,receipt_item_id'
                  });
                
                devLog(`✅ Reserved unit ${availableUnit.id} for item ${insertedItem.id} (${insertedItem.sku})`);
              }
            }
          } else {
            devLog('✅ No new items to insert, existing items updated in place');
          }
        } else {
          // The form has NO items. If the receipt previously had items, the user
          // removed all of them — delete every remaining row (and release units).
          try {
            const { data: dbItems, error: dbItemsError } = await supabase
              .from('receipt_items')
              .select('id')
              .eq('receipt_id', editingReceipt.id);

            if (dbItemsError) {
              devError('Error fetching items to clear receipt:', dbItemsError);
            } else if (dbItems && dbItems.length > 0) {
              const removedIds = dbItems.map(r => r.id);
              devLog(`🗑️ Clearing all ${removedIds.length} item(s) from receipt`);

              const { data: linkedUnits } = await supabase
                .from('inventory_units')
                .select('id, notes')
                .in('receipt_item_id', removedIds);

              if (linkedUnits && linkedUnits.length > 0) {
                const phantomUnitIds = [];
                const releaseUnitIds = [];
                for (const u of linkedUnits) {
                  if ((u.notes || '').toLowerCase().includes('no pre-existing stock')) {
                    phantomUnitIds.push(u.id);
                  } else {
                    releaseUnitIds.push(u.id);
                  }
                }
                if (releaseUnitIds.length > 0) {
                  await supabase
                    .from('inventory_units')
                    .update({
                      status: 'available',
                      reserved_for_receipt_id: null,
                      receipt_item_id: null,
                      reserved_at: null,
                      updated_at: new Date().toISOString()
                    })
                    .in('id', releaseUnitIds);
                }
                if (phantomUnitIds.length > 0) {
                  await supabase
                    .from('inventory_units')
                    .delete()
                    .in('id', phantomUnitIds);
                }
              }

              await supabase
                .from('item_statuses')
                .delete()
                .in('receipt_item_id', removedIds);

              const { error: delAllErr } = await supabase
                .from('receipt_items')
                .delete()
                .in('id', removedIds);
              if (delAllErr) devError('Error clearing receipt items:', delAllErr);
            }
          } catch (clearError) {
            devError('Clear-all items failed:', clearError);
          }
        }

        // Trigger survey if just delivered
        if (justDelivered) {
          devLog('🎯 Order marked as delivered, triggering customer survey...');
          
          // Inventory is retired — propagate the receipt-level status change
          // to item_statuses (the sole fulfillment source). Without this the
          // AI chat / customer receipt keep reporting items as ready/pending
          // on an order that was just marked delivered.
          try {
            const statusSweep = await markAllItemStatusesDelivered(editingReceipt.id, formData.deliveryStatus);
            devLog(`📦 Marked ${statusSweep.updated} item status(es) as ${formData.deliveryStatus}`);
          } catch (statusErr) {
            devError('Error marking item statuses as delivered:', statusErr);
          }
          
          // Survey gates on having a phone number only. The SMS-consent
          // checkbox is a point-of-sale reminder for staff, NOT a send gate
          // (store policy, Jul 2026) — matching the receipt SMS, which also
          // sends without it. STOP/opt-out is still enforced server-side
          // (sms_opted_out checks in the SMS endpoints).
          const hasPhone = formData.customerPhone || editingReceipt.customerPhone || editingReceipt.customer_phone;
          
          if (!hasPhone) {
            devLog('⚠️ Cannot send survey - no phone number');
            addNotification({ title: 'Marked Complete', message: 'No phone number - survey not sent' });
          } else {
            try {
              const response = await fetch('/api/chat-survey', {
                method: 'POST',
                headers: await authHeaders(),
                body: JSON.stringify({
                  receipt_id: editingReceipt.id,
                  conversation_stage: 'start_survey'
                })
              });

              const data = await response.json();
              if (data.success && data.skipped) {
                // Duplicate guard: backend didn't send because a survey
                // already went out for this receipt — say so honestly
                devLog('⏭️ Survey skipped - already sent previously for this receipt');
                addNotification({ title: 'Marked Complete', message: 'Survey already sent previously - not re-sent' });
              } else if (data.success) {
                devLog('✅ Survey started successfully');
                addNotification({ title: 'Marked Complete', message: 'Customer survey sent via SMS' });
              } else {
                devError('Survey trigger failed:', data.error);
                addNotification({ title: 'Marked Complete', message: 'Survey failed to send - check logs' });
              }
            } catch (error) {
              devError('Error triggering survey:', error);
              addNotification({ title: 'Marked Complete', message: 'Survey failed to send - check logs' });
            }
          }
        } else {
          addNotification({ title: 'Receipt Updated', message: 'Changes saved successfully' });
        }

        // Reload receipts from database
        await loadReceipts(null, true); // silent: keep the list on screen while it refreshes
        
        // Update local state immediately to reflect changes
        const updatedReceipts = receipts.map(r =>
          r.id === editingReceipt.id 
            ? { 
                ...r, 
                delivery_status: finalDeliveryStatus,
                deliveryStatus: finalDeliveryStatus,  // Also update camelCase version
                customer_name: cleanData.customerName,
                customer_phone: cleanData.customerPhone,
                customer_email: cleanData.customerEmail,
                delivery_address: cleanData.deliveryAddress,
                delivery_date: cleanData.deliveryDate,
                estimated_time_slot: cleanData.estimatedTimeSlot,
                customer_notes: cleanData.customerPreferences?.notes || null,
                internal_notes: cleanData.internalNotes,
                amount_paid: parseFloat(cleanData.amountPaid) || 0,
                payment_history: formData.paymentHistory || []
              }
            : r
        );
        setReceipts(updatedReceipts);
        
        handleCloseModal();
        
      } catch (error) {
        devError('Error updating receipt:', error);
        addNotification({ title: 'Update Failed', message: error.message, isEscalation: true });
      } finally {
        setIsSavingReceipt(false);
      }
      
    } else {
      // Create new receipt - WAIT for Supabase
      try {
        devLog('Saving new receipt to Supabase...');
        
        const { data: receipt, error } = await supabase
          .from('receipts')
          .insert([{
            store_id: currentStore?.id || null,
            customer_name: cleanData.customerName,
            customer_phone: cleanData.customerPhone,
            customer_email: cleanData.customerEmail || null,
            delivery_address: cleanData.deliveryAddress || null,
            apartment_number: formData.apartmentNumber || null,
            business_name: currentStore?.name || 'Your Store',
            business_phone: currentStore?.phone || currentStore?.business_phone || '',
            business_address: currentStore?.address || currentStore?.store_address || '',
            google_review_url: currentStore?.google_review_url || null,
            subtotal,
            tax,
            total,
            delivery_status: cleanData.deliveryStatus,
            delivery_date: cleanData.deliveryDate || null,  // Convert empty string to null
            customer_notes: cleanData.customerPreferences?.notes || null, // Delivery instructions
            internal_notes: cleanData.internalNotes || null, // AI Customer Care Notes
            is_ai_handling: true,
            // Payment tracking
            amount_paid: parseFloat(cleanData.amountPaid) || 0,
            payment_history: formData.paymentHistory || [],
            // Sales tracking
            salesperson: cleanData.salesperson || null,
            // Delivery services
            delivery_services: formData.deliveryServices || null,
            // SMS consent (TCPA compliance)
            sms_consent: formData.smsConsent || false,
            sms_consent_at: formData.smsConsent ? new Date().toISOString() : null
          }])
          .select()
          .single();

        if (error) {
          devError('Supabase error:', error);
          throw error;
        }

        devLog('Receipt saved with ID:', receipt.id);

        // Insert items (filter out empty items first)
        if (cleanData.items && cleanData.items.length > 0) {
          const validItems = cleanData.items.filter(item => 
            (item.name && item.name.trim()) || (item.sku && item.sku.trim())
          );
          
          if (validItems.length === 0) {
            devLog('⚠️ No valid items to insert for new receipt');
          }
          
          // Explode items with qty > 1 into individual line items for partial delivery support
          const explodedItems = explodeItems(validItems);
          devLog(`📦 Exploded ${validItems.length} items → ${explodedItems.length} line items`);
          
          const itemsToInsert = explodedItems.map((item, index) => ({
            receipt_id: receipt.id,
            item_name: item.name,
            sku: ensureSku(item.sku), // Auto-generate MISC-XXXXXX if blank
            color: item.color,
            quantity: 1, // Always 1 after explosion
            price: item.price,
            position: index,
            set_id: item.setId || null,
            set_name: item.setName || null,
            manufacturer_id: item.manufacturerId || null
          }));

          if (itemsToInsert.length > 0) {
            // INSERT AND GET THE IDs BACK
            const { data: insertedItems, error: itemsError } = await supabase
              .from('receipt_items')
              .insert(itemsToInsert)
              .select('id, sku, item_name, position, manufacturer_id');

            if (itemsError) {
              devError('Items error:', itemsError);
            } else {
              devLog('✅ Items inserted, got IDs:', insertedItems?.map(i => i.id));
              
              // AUTO-MATCH: Check if any items are already in inventory and reserve them
              // Matches on BOTH SKU + manufacturer_id (if set) for data integrity
              devLog('🔍 Checking inventory for matching items...');
              devLog('🏪 Current store ID:', currentStore?.id);
              
              for (const insertedItem of (insertedItems || [])) {
                if (!insertedItem.sku) continue;
                
                // Check if this SPECIFIC item already has a unit assigned
                const { data: existingUnit } = await supabase
                  .from('inventory_units')
                  .select('id, status')
                  .eq('receipt_item_id', insertedItem.id)
                  .maybeSingle();
                
                if (existingUnit) {
                  devLog(`📋 Item ${insertedItem.id} already has unit assigned`);
                  continue;
                }
                
                // Find an available unit for this SKU + manufacturer
                let availableUnit = null;
                
                if (currentStore?.id) {
                  // Try store-specific first
                  let storeQuery = supabase
                    .from('inventory_units')
                    .select('id, sku, unit_serial, store_id')
                    .ilike('sku', insertedItem.sku)
                    .eq('status', 'available')
                    .eq('store_id', currentStore.id)
                    .is('reserved_for_receipt_id', null)
                    .is('receipt_item_id', null);
                  
                  // Only filter by manufacturer if receipt item has one (backwards compatible)
                  if (insertedItem.manufacturer_id) {
                    storeQuery = storeQuery.eq('manufacturer_id', insertedItem.manufacturer_id);
                  }
                  
                  const { data: storeUnit } = await storeQuery.limit(1).maybeSingle();
                  
                  if (storeUnit) {
                    availableUnit = storeUnit;
                    devLog(`📦 Found store unit for ${insertedItem.sku}`);
                  }
                }
                
                // If no store-specific, try any available
                if (!availableUnit) {
                  let anyQuery = supabase
                    .from('inventory_units')
                    .select('id, sku, unit_serial, store_id')
                    .ilike('sku', insertedItem.sku)
                    .eq('status', 'available')
                    .is('reserved_for_receipt_id', null)
                    .is('receipt_item_id', null);
                  
                  if (insertedItem.manufacturer_id) {
                    anyQuery = anyQuery.eq('manufacturer_id', insertedItem.manufacturer_id);
                  }
                  
                  const { data: anyUnit } = await anyQuery.limit(1).maybeSingle();
                  
                  if (anyUnit) {
                    availableUnit = anyUnit;
                  }
                }
                
                if (!availableUnit) {
                  devLog(`⏳ ${insertedItem.sku} (item ${insertedItem.id}) - no available units`);
                  continue;
                }
                
                devLog(`📦 Found available unit for ${insertedItem.sku}:`, availableUnit.id);
                
                // Reserve the unit FOR THIS SPECIFIC ITEM
                const { error: reserveErr } = await supabase
                  .from('inventory_units')
                  .update({ 
                    status: 'reserved',
                    reserved_for_receipt_id: receipt.id,
                    receipt_item_id: insertedItem.id,  // ← THE KEY FIX!
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', availableUnit.id);
                
                if (reserveErr) {
                  devError(`Error reserving unit for ${insertedItem.sku}:`, reserveErr);
                  continue;
                }
                
                // Create item_statuses entry
                await supabase
                  .from('item_statuses')
                  .upsert([{
                    receipt_id: receipt.id,
                    receipt_item_id: insertedItem.id,
                    item_name: insertedItem.item_name,
                    sku: insertedItem.sku,
                    status: 'at_store',
                    notes: `Auto-assigned from inventory`
                  }], {
                    onConflict: 'receipt_id,receipt_item_id'
                  });
                
                devLog(`✅ Reserved unit ${availableUnit.id} for item ${insertedItem.id} (${insertedItem.sku})`);
              }
            }
          }
        }

        // Insert customer preferences
        if (cleanData.customerPreferences?.notes || 
            cleanData.customerPreferences?.preferredDeliveryDay || 
            cleanData.customerPreferences?.preferredTimeWindow) {
          const { error: prefsError } = await supabase
            .from('customer_preferences')
            .insert([{
              receipt_id: receipt.id,
              preferred_delivery_day: cleanData.customerPreferences.preferredDeliveryDay,
              preferred_time_window: cleanData.customerPreferences.preferredTimeWindow,
              notes: cleanData.customerPreferences.notes
            }]);

          if (prefsError) {
            devError('Preferences error:', prefsError);
          }
        }
        
        // Reload receipts from database
        await loadReceipts(null, true); // silent: keep the list on screen while it refreshes
        handleCloseModal();
        setSearchQuery(''); // Clear search to show new receipt
        
        // Trigger inventory refresh so linked items show correctly when user switches to Inventory tab
        setInventoryRefreshTrigger(prev => prev + 1);
        devLog('🔄 Triggered inventory refresh after receipt creation');
        
        addNotification({ title: 'Receipt Created', message: 'New receipt saved successfully' });
        
      } catch (error) {
        devError('Failed to save receipt:', error);
        addNotification({ title: 'Save Failed', message: error.message, isEscalation: true });
      } finally {
        setIsSavingReceipt(false);
      }
    }
  };

  const handleSendSMS = async (receipt) => {
    // Check if customer has opted out of SMS
    if (receipt.sms_opted_out) {
      addNotification({ 
        title: 'Cannot Send Receipt', 
        message: 'Customer has opted out of SMS. Wait for them to text START to re-subscribe.', 
        isEscalation: true 
      });
      return;
    }

    // Prevent double-click
    if (isSendingSMS) {
      devLog('🛡️ SMS send already in progress');
      return;
    }
    setIsSendingSMS(true);
    setSendingSMSReceiptId(receipt.id); // Track which receipt is sending

    try {
      // Handle both field name formats (camelCase and snake_case)
      const phoneNumber = receipt.customerPhone || receipt.customer_phone;
      const customerName = receipt.customerName || receipt.customer_name;
      const receiptId = receipt.id;
      
      devLog('📤 Sending SMS...');
      devLog('Phone:', phoneNumber);
      devLog('Receipt ID:', receiptId);
      devLog('Customer:', customerName);
      
      if (!phoneNumber) {
        addNotification({ title: 'Error', message: 'No phone number found on receipt', isEscalation: true });
        return;
      }
      
      if (!receiptId) {
        addNotification({ title: 'Error', message: 'No receipt ID found', isEscalation: true });
        return;
      }
      
      // Send SMS via ClickSend API
      const response = await fetch('/api/sms/send-receipt', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          receiptId: receiptId,
          customerName: customerName || 'Customer',
          businessName: currentStore?.name || null
        })
      });

      const data = await response.json();
      devLog('📬 API Response:', data);

      if (response.ok && data.success) {
        // Update receipt with sent timestamp
        const now = new Date();
        const timeString = now.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
        const dateString = now.toISOString().split('T')[0];
        
        // Update in Supabase
        await supabase
          .from('receipts')
          .update({ sent_at: now.toISOString() })
          .eq('id', receiptId);
        
        // Update local state
        setReceipts(receipts.map(r => 
          r.id === receipt.id 
            ? { ...r, sentAt: `${dateString} ${timeString}`, sent_at: now.toISOString() }
            : r
        ));
        
        // Update selectedReceipt if viewing this receipt
        if (selectedReceipt?.id === receipt.id) {
          setSelectedReceipt(prev => ({
            ...prev,
            sentAt: `${dateString} ${timeString}`,
            sent_at: now.toISOString()
          }));
        }
        
        // Show "Sent ✓" state for 3 seconds before switching to "Resend"
        setSmsSentReceipts(prev => ({ ...prev, [receiptId]: true }));
        setTimeout(() => {
          setSmsSentReceipts(prev => {
            const newState = { ...prev };
            delete newState[receiptId];
            return newState;
          });
        }, 3000);
        
        addNotification({ title: 'SMS Sent', message: `Message sent! ID: ${data.messageId}` });
      } else {
        devError('❌ API Error:', data);
        addNotification({ title: 'SMS Failed', message: data.error || 'Unknown error', isEscalation: true });
      }
    } catch (error) {
      devError('❌ Error sending SMS:', error);
      addNotification({ title: 'SMS Failed', message: error.message, isEscalation: true });
    } finally {
      setIsSendingSMS(false);
      setSendingSMSReceiptId(null);
    }
  };

  // ==========================================
  // PARTIAL DELIVERY HANDLERS - NEW
  // ==========================================
  
  // Open partial delivery modal for a receipt
  const openPartialDeliveryModal = async (receipt) => {
    // Fetch full receipt data if not already loaded
    let fullReceipt = receipt;
    if (!receipt.items || receipt.items.length === 0) {
      fullReceipt = await fetchReceiptById(receipt.id);
    }
    
    // Also fetch inventory units for this receipt to get accurate status
    const { data: inventoryUnits } = await supabase
      .from('inventory_units')
      .select('sku, status, unit_serial')
      .eq('reserved_for_receipt_id', receipt.id);
    
    // Build a status map from inventory_units (source of truth)
    const unitStatusMap = {};
    (inventoryUnits || []).forEach(unit => {
      const skuKey = unit.sku?.toLowerCase();
      if (!unitStatusMap[skuKey]) {
        unitStatusMap[skuKey] = { total: 0, delivered: 0, reserved: 0 };
      }
      unitStatusMap[skuKey].total++;
      if (UNIT_TERMINAL_STATUSES.includes(unit.status)) {
        unitStatusMap[skuKey].delivered++;
      } else if (unit.status === 'reserved') {
        unitStatusMap[skuKey].reserved++;
      }
    });
    
    // Attach unit status to receipt for modal use
    fullReceipt.inventoryUnitStatus = unitStatusMap;
    
    setPartialDeliveryReceipt(fullReceipt);
    
    // Initialize selection - pre-check items that have reserved units (ready to deliver)
    const initialSelection = {};
    (fullReceipt.items || []).forEach(item => {
      const skuKey = item.sku?.toLowerCase();
      const unitStatus = unitStatusMap[skuKey];
      
      // Check if there are reserved (ready) units that aren't delivered yet
      const hasReservedUnits = unitStatus && unitStatus.reserved > 0;
      const allDelivered = unitStatus && unitStatus.delivered >= (item.quantity || 1);
      
      // Pre-check if item has units ready and not fully delivered
      initialSelection[item.sku] = hasReservedUnits && !allDelivered;
    });
    
    setSelectedDeliveryItems(initialSelection);
    setShowPartialDeliveryModal(true);
  };

  // Toggle item selection in partial delivery modal
  const toggleDeliveryItem = (sku) => {
    setSelectedDeliveryItems(prev => ({
      ...prev,
      [sku]: !prev[sku]
    }));
  };

  // ==========================================
  // UNDO DELIVERY - Put item back to reserved status
  // Called from receipt detail when user wants to undo an accidental delivery
  // ==========================================
  const handleUndoDelivery = async (item, receipt) => {
    if (!item || !receipt) return;

    // Legacy delivered items without an id: resolve one so the busy-lock
    // and receipt_item_id lookups below work. (This handler also has
    // SKU-based fallbacks, so no loud failure needed here.)
    if (!item.id) item = (await ensureReceiptItemId(receipt.id, item)) || item;
    
    // Prevent double-click
    if (isUndoingDelivery === item.id) {
      devLog('🛡️ Undo already in progress');
      return;
    }
    
    setIsUndoingDelivery(item.id);
    
    try {
      devLog('🔍 Looking for delivered inventory unit:', { receiptId: receipt.id, sku: item.sku });
      
      // First try: Find by receipt_id and SKU with delivered status
      let { data: units, error: findError } = await supabase
        .from('inventory_units')
        .select('*')
        .eq('reserved_for_receipt_id', receipt.id)
        .ilike('sku', item.sku)
        .in('status', UNIT_TERMINAL_STATUSES);
      
      devLog('🔍 Search result (by receipt_id + sku + delivered):', units);
      
      // Second try: If not found, search by receipt_item_id
      if ((!units || units.length === 0) && item.id) {
        devLog('🔍 Trying by receipt_item_id:', item.id);
        const { data: units2 } = await supabase
          .from('inventory_units')
          .select('*')
          .eq('receipt_item_id', item.id)
          .in('status', UNIT_TERMINAL_STATUSES);
        
        devLog('🔍 Search result (by receipt_item_id):', units2);
        if (units2 && units2.length > 0) {
          units = units2;
        }
      }
      
      // Third try: If still not found, search more broadly - any delivered unit with this SKU for this receipt
      if (!units || units.length === 0) {
        devLog('🔍 Trying broader search - any delivered unit with SKU:', item.sku);
        const { data: units3 } = await supabase
          .from('inventory_units')
          .select('*')
          .ilike('sku', item.sku)
          .in('status', UNIT_TERMINAL_STATUSES)
          .limit(10);
        
        devLog('🔍 All delivered units with this SKU:', units3);
        
        // Check if any of them were linked to this receipt (might have receipt_id in different format)
        if (units3 && units3.length > 0) {
          const matchingUnit = units3.find(u => 
            u.reserved_for_receipt_id === receipt.id || 
            String(u.reserved_for_receipt_id) === String(receipt.id)
          );
          if (matchingUnit) {
            units = [matchingUnit];
            devLog('🔍 Found matching unit:', matchingUnit);
          }
        }
      }
      
      // STANDALONE RECEIPTS PATH: items marked delivered/picked-up through
      // the receipts-only flow never had an inventory unit attached. That's
      // a legitimate state, not an error — undo just reverts the status
      // record (and the receipt status below). Only touch a unit if one
      // actually exists.
      const unit = units && units.length > 0 ? units[0] : null;

      if (unit) {
        devLog('✅ Found unit to undo:', unit);

        const { error: invError } = await supabase
          .from('inventory_units')
          .update({
            status: 'reserved',
            reserved_for_receipt_id: receipt.id, // Re-link to receipt just in case
            updated_at: new Date().toISOString()
          })
          .eq('id', unit.id);

        if (invError) throw invError;
      } else {
        devLog('ℹ️ No inventory unit attached — standalone receipts undo (status record only)');
      }
      
      // Update item_status back to at_store
      await supabase
        .from('item_statuses')
        .upsert([{
          receipt_id: receipt.id,
          receipt_item_id: item.id,
          item_name: item.name || item.item_name,
          sku: item.sku,
          status: 'at_store',
          notes: `Delivery undone on ${new Date().toLocaleDateString()}`
        }], { onConflict: 'receipt_id,receipt_item_id' });
      
      // Move receipt back to active orders (pending status) via backend
      try {
        await fetch('/api/update-receipt-status', {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({ receipt_id: receipt.id, delivery_status: 'pending' })
        });
        devLog(`📋 Moved receipt ${receipt.id} back to active orders`);
      } catch (err) {
        devWarn('Could not update receipt status:', err);
      }
      
      // Refresh receipts list
      await loadReceipts(null, true); // silent: keep the list on screen while it refreshes
      
      // Update selected receipt if viewing it
      if (selectedReceipt?.id === receipt.id) {
        await loadReceiptDetails(receipt.id);
      }
      
      devLog(`✅ Undid delivery for ${item.sku} - back to reserved`);
      addNotification({ title: 'Delivery Undone', message: `${item.name || item.item_name} is back to reserved` });
      
    } catch (error) {
      devError('Error undoing delivery:', error);
      addNotification({ title: 'Undo Failed', message: 'Please try again', isEscalation: true });
    } finally {
      setIsUndoingDelivery(null);
    }
  };

  // Process partial delivery
  const handlePartialDelivery = async () => {
    if (!partialDeliveryReceipt) return;
    
    const skusToDeliver = Object.entries(selectedDeliveryItems)
      .filter(([_, isSelected]) => isSelected)
      .map(([sku, _]) => sku);
    
    if (skusToDeliver.length === 0) {
      addNotification({ title: 'No Items Selected', message: 'Please select at least one item to deliver', isEscalation: true });
      return;
    }
    
    setIsProcessingPartialDelivery(true);
    
    try {
      // Mark selected items as delivered
      const result = await deliverPartialItems(partialDeliveryReceipt.id, skusToDeliver);
      devLog('📦 Partial delivery result:', result);
      
      // Check if all items are now delivered
      const deliveryStatus = await checkAllItemsDelivered(partialDeliveryReceipt.id);
      devLog('📊 Delivery status:', deliveryStatus);
      
      if (deliveryStatus.allDelivered) {
        // ALL items delivered - update via backend and trigger survey
        try {
          await fetch('/api/update-receipt-status', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: partialDeliveryReceipt.id,
              delivery_status: 'delivered',
              survey_status: 'pending'
            })
          });
        } catch (err) {
          devError('Receipt status update error:', err);
        }
        
        // Trigger customer survey
        try {
          const response = await fetch('/api/chat-survey', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: partialDeliveryReceipt.id,
              conversation_stage: 'start_survey'
            })
          });
          
          const data = await response.json();
          if (data.success && data.skipped) {
            // Duplicate guard: survey already sent for this receipt — don't claim a new send
            devLog('⏭️ Survey skipped - already sent previously for this receipt');
            addNotification({ title: 'All Items Delivered', message: `${deliveryStatus.deliveredCount} items delivered. Survey already sent previously - not re-sent.` });
          } else if (data.success) {
            devLog('✅ Survey triggered for completed delivery');
            addNotification({ title: 'All Items Delivered', message: `${deliveryStatus.deliveredCount} items delivered. Survey sent!` });
          } else {
            addNotification({ title: 'All Items Delivered', message: 'Survey failed to send - check manually' });
          }
        } catch (err) {
          devError('Survey error:', err);
          addNotification({ title: 'All Items Delivered', message: 'Survey failed to send' });
        }
        
      } else {
        // PARTIAL delivery - update via backend and send SMS
        try {
          await fetch('/api/update-receipt-status', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: partialDeliveryReceipt.id,
              delivery_status: 'partially_delivered'
            })
          });
        } catch (err) {
          devError('Receipt status update error:', err);
        }
        
        // Send partial delivery SMS notification
        try {
          const response = await fetch('/api/partial-delivery-sms', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              receipt_id: partialDeliveryReceipt.id,
              delivered_items: skusToDeliver,
              remaining_count: deliveryStatus.remainingCount
            })
          });
          
          const data = await response.json();
          if (data.success) {
            devLog('✅ Partial delivery SMS sent');
          }
        } catch (err) {
          devError('Partial delivery SMS error:', err);
        }
        
        addNotification({ title: 'Partial Delivery', message: `${skusToDeliver.length} item(s) delivered. ${deliveryStatus.remainingCount} remaining.` });
      }
      
      // Reload receipts and close modal
      await loadReceipts(null, true); // silent: keep the list on screen while it refreshes
      setShowPartialDeliveryModal(false);
      setPartialDeliveryReceipt(null);
      setSelectedDeliveryItems({});
      
    } catch (error) {
      devError('Error processing partial delivery:', error);
      addNotification({ title: 'Delivery Failed', message: 'Please try again', isEscalation: true });
    } finally {
      setIsProcessingPartialDelivery(false);
    }
  };

  // Mark a single item as delivered (from receipt view)
  const handleMarkSingleItemDelivered = async (receiptId, item, statusInfo) => {
    if (!receiptId || !item.sku) return;
    
    try {
      // 1. Update item_statuses to 'delivered'
      const { error: statusError } = await supabase
        .from('item_statuses')
        .update({ 
          status: 'delivered', 
          notes: `Delivered ${new Date().toLocaleDateString()}`,
          updated_at: new Date().toISOString()
        })
        .eq('receipt_id', receiptId)
        .eq('receipt_item_id', item.id);
      
      if (statusError) {
        devError('Error updating item status:', statusError);
        throw statusError;
      }
      
      // 2. Update inventory_units to 'delivered' for this item
      const { error: invError } = await supabase
        .from('inventory_units')
        .update({ 
          status: 'delivered',
          updated_at: new Date().toISOString()
        })
        .eq('reserved_for_receipt_id', receiptId)
        .ilike('sku', item.sku);
      
      if (invError) {
        devError('Error updating inventory:', invError);
      }
      
      // 3. Get total receipt items count from receipt_items table
      const { data: receiptItems, error: itemsError } = await supabase
        .from('receipt_items')
        .select('id, sku, item_name')
        .eq('receipt_id', receiptId);
      
      if (itemsError) {
        devError('Error fetching receipt items:', itemsError);
      }
      
      // Filter out empty/invalid items for accurate counting
      const validItems = (receiptItems || []).filter(item => 
        (item.sku && item.sku.trim()) || (item.item_name && item.item_name.trim())
      );
      const totalItems = validItems.length;
      devLog(`📦 Total valid items on receipt: ${totalItems}`, validItems);
      
      // 4. Check how many items have 'delivered' or 'picked_up' status in item_statuses
      const { data: allStatuses, error: statusesError } = await supabase
        .from('item_statuses')
        .select('status, sku')
        .eq('receipt_id', receiptId);
      
      if (statusesError) {
        devError('Error fetching item statuses:', statusesError);
      }
      
      const deliveredCount = allStatuses?.filter(s => ITEM_TERMINAL_STATUSES.includes(s.status)).length || 0;
      devLog(`📊 Completed count: ${deliveredCount}/${totalItems}`, allStatuses);
      
      // 5. CRITICAL: Only mark as fully delivered if ALL receipt items are delivered/picked_up
      // This means deliveredCount must equal totalItems AND totalItems must be > 0
      const allDelivered = totalItems > 0 && deliveredCount >= totalItems;
      const anyDelivered = deliveredCount > 0;
      
      devLog(`🚚 All complete? ${allDelivered} (${deliveredCount} >= ${totalItems})`);
      
      // 6. Update receipt delivery_status via backend (bypasses RLS)
      if (allDelivered) {
        devLog('✅ All items complete - updating receipt to delivered via backend');
        try {
          await fetch('/api/update-receipt-status', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: receiptId,
              delivery_status: 'delivered',
              survey_status: 'pending'
            })
          });
        } catch (err) {
          devError('Receipt status update error:', err);
        }
        
        try {
          const surveyResp = await fetch('/api/chat-survey', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: receiptId,
              conversation_stage: 'start_survey'
            })
          });
          const surveyData = await surveyResp.json();
          if (surveyData.success && surveyData.skipped) {
            devLog('⏭️ Survey skipped - already sent previously for this receipt');
          } else if (surveyData.success) {
            devLog('✅ Survey triggered');
          } else {
            devError('Survey trigger failed:', surveyData.error);
          }
        } catch (err) {
          devError('Survey trigger error:', err);
        }
      } else if (anyDelivered) {
        devLog('📦 Partial delivery - updating receipt to partially_delivered via backend');
        try {
          await fetch('/api/update-receipt-status', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: receiptId,
              delivery_status: 'partially_delivered'
            })
          });
        } catch (err) {
          devError('Receipt status update error:', err);
        }
      }
      
      // 7. Reload the receipt to show updated status
      const updatedReceipt = await fetchReceiptById(receiptId);
      if (updatedReceipt) {
        setSelectedReceipt(updatedReceipt);
        // Update in receipts list too
        setReceipts(prev => prev.map(r => r.id === receiptId ? updatedReceipt : r));
      }
      
      // Show success message
      if (allDelivered) {
        addNotification({ title: 'All Items Delivered', message: 'Customer survey sent!' });
      } else {
        addNotification({ title: 'Item Delivered', message: `${item.name || item.item_name} delivered (${deliveredCount}/${totalItems})` });
      }
      
    } catch (error) {
      devError('Error marking item as delivered:', error);
      addNotification({ title: 'Delivery Failed', message: 'Please try again', isEscalation: true });
    }
  };

  // Mark a specific unit (receipt_item) as delivered or picked_up
  // This works at the individual item/unit level using item.id (receipt_item_id)
  // ============================================================
  // SELF-HEAL: IMPORTED / LEGACY ITEMS WITHOUT A receipt_items ID
  // POS file imports ("Imported order (no line detail in file)") and
  // legacy JSON items can reach the UI without item.id. Every status
  // write keys on receipt_item_id, so those clicks silently no-oped
  // (the old guards devError'd — invisible in prod). This resolves an
  // id at click time: adopt an existing receipt_items row (receipt +
  // SKU match), or create the missing row(s) from the receipt's own
  // items. When the receipt has ZERO receipt_items rows (pure-import
  // case) we heal ALL its items in one pass so the line list stays
  // complete on the next DB load. Items that already carry an id pass
  // through untouched — the normal path is byte-for-byte unchanged.
  // ============================================================
  const patchHealedItemId = (targetItem, newId) => {
    const tSku = (targetItem.sku || '').trim();
    const tName = targetItem.name || targetItem.item_name || '';
    const matches = (it) =>
      !it.id &&
      (it.sku || '').trim() === tSku &&
      (it.name || it.item_name || '') === tName;
    setSelectedReceipt((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      if (!prev.items.some(matches)) return prev;
      return {
        ...prev,
        items: prev.items.map((it) => (matches(it) ? { ...it, id: newId } : it)),
      };
    });
    setReceipts((prev) =>
      Array.isArray(prev)
        ? prev.map((r) =>
            Array.isArray(r?.items) && r.items.some(matches)
              ? { ...r, items: r.items.map((it) => (matches(it) ? { ...it, id: newId } : it)) }
              : r,
          )
        : prev,
    );
  };

  // The POS importer (/api/import-orders) writes item_statuses rows with NO
  // receipt_item_id (it never creates receipt_items). When a heal gives the
  // line a real receipt_items id, link the importer's orphan status row to
  // it — so an imported status (e.g. STORIS "delivered") becomes THE status
  // row and the next upsert updates it instead of stranding it. limit(1) +
  // update-by-id respects the unique (receipt_id, receipt_item_id) index.
  const linkOrphanStatusRow = async (receiptId, sku, receiptItemId) => {
    const cleanSku = (sku || '').trim();
    if (!receiptId || !cleanSku || !receiptItemId) return;
    try {
      const { data: orphans } = await supabase
        .from('item_statuses')
        .select('id')
        .eq('receipt_id', receiptId)
        .is('receipt_item_id', null)
        .ilike('sku', cleanSku)
        .limit(1);
      if (orphans?.[0]?.id) {
        await supabase
          .from('item_statuses')
          .update({ receipt_item_id: receiptItemId })
          .eq('id', orphans[0].id);
        devLog(`🔗 Linked orphan item_statuses row to receipt_item ${receiptItemId}`);
      }
    } catch (e) {
      devError('linkOrphanStatusRow failed:', e);
    }
  };

  const ensureReceiptItemId = async (receiptId, item) => {
    if (item?.id) return item;
    if (!receiptId || !item) return item;
    const sku = (item.sku || '').trim();
    try {
      // 1) Adopt an existing row for this receipt + SKU (covers rows that
      //    exist in receipt_items but lost their id in the UI mapping).
      if (sku) {
        const { data: existing } = await supabase
          .from('receipt_items')
          .select('id')
          .eq('receipt_id', receiptId)
          .ilike('sku', sku)
          .limit(1)
          .maybeSingle();
        if (existing?.id) {
          patchHealedItemId(item, existing.id);
          await linkOrphanStatusRow(receiptId, sku, existing.id);
          return { ...item, id: existing.id };
        }
      }

      // 2) Nothing to adopt — create the missing row(s).
      const { count } = await supabase
        .from('receipt_items')
        .select('id', { count: 'exact', head: true })
        .eq('receipt_id', receiptId);

      const uiItems =
        selectedReceiptRef.current?.id === receiptId &&
        Array.isArray(selectedReceiptRef.current?.items)
          ? selectedReceiptRef.current.items
          : [item];

      // Pure-import receipts (zero rows): heal every id-less item so the
      // next load shows the complete line list, not just the clicked one.
      const sources = ((count || 0) === 0 ? uiItems : [item]).filter((it) => !it?.id);
      if (sources.length === 0) return item;

      const toInsert = sources.map((it, idx) => ({
        receipt_id: receiptId,
        item_name: it.name || it.item_name || 'Imported item',
        sku: ensureSku((it.sku || '').trim()),
        color: it.color || null,
        quantity: it.quantity || 1,
        price: parseFloat(it.price) || 0,
        position:
          typeof it.originalIndex === 'number' ? it.originalIndex : (count || 0) + idx,
        set_id: it.set_id || it.setId || null,
        set_name: it.set_name || it.setName || null,
        manufacturer_id: it.manufacturer_id || it.manufacturerId || null,
      }));

      const { data: created, error: insertErr } = await supabase
        .from('receipt_items')
        .insert(toInsert)
        .select('id, sku, item_name');
      if (insertErr || !created?.length) {
        devError('ensureReceiptItemId: insert failed', insertErr);
        return item;
      }

      // Rows come back in insert order — patch each into memory, link the
      // importer's orphan status row, and pick out the clicked item's new
      // id (match by pre-insert sku + name).
      let healedItem = item;
      const clickedName = item.name || item.item_name || '';
      for (let i = 0; i < created.length; i += 1) {
        const row = created[i];
        const src = sources[i];
        if (!src) continue;
        patchHealedItemId(src, row.id);
        // eslint-disable-next-line no-await-in-loop
        await linkOrphanStatusRow(receiptId, row.sku || src.sku, row.id);
        if (
          (src.sku || '').trim() === sku &&
          (src.name || src.item_name || '') === clickedName &&
          healedItem === item
        ) {
          healedItem = { ...item, id: row.id, sku: row.sku || item.sku };
        }
      }
      devLog(`🩹 Healed ${created.length} receipt_items row(s) for receipt ${receiptId}`);
      return healedItem;
    } catch (e) {
      devError('ensureReceiptItemId failed:', e);
      return item;
    }
  };

  const handleMarkUnitStatus = async (receiptId, item, newStatus = 'delivered', options = {}) => {
    // Imported/legacy items may arrive without an internal id — resolve one
    // (find-or-create the receipt_items row) before writing. Fails LOUDLY
    // now instead of the old silent return staff couldn't see in prod.
    if (!item?.id) item = (await ensureReceiptItemId(receiptId, item)) || item;
    if (!receiptId || !item.id) {
      devError('handleMarkUnitStatus: Missing receiptId or item.id');
      window.alert("Couldn't update this item — no line-item record exists and creating one failed. Check your connection and try again.");
      return;
    }

    // Instant feedback: light up the clicked pill's spinner for THIS item
    // before any network call runs. Cleared in finally so it can never stick.
    const busyItemId = item.id;
    setItemBusy(busyItemId, newStatus);

    try {
      devLog(`📦 Marking unit ${item.id} as ${newStatus}`);

      // Human label per status — used in notes + toasts. delivered/picked_up
      // strings are byte-identical to the old hardcoded ternary.
      const statusActionLabel =
        newStatus === 'picked_up' ? 'Picked up'
        : newStatus === 'out_for_delivery' ? 'On truck'
        : newStatus === 'at_store' ? 'Ready'
        : 'Delivered';
      
      // 1. Update or insert item_statuses for this specific item
      const { error: statusError } = await supabase
        .from('item_statuses')
        .upsert({ 
          receipt_id: receiptId,
          receipt_item_id: item.id,
          sku: item.sku,
          item_name: item.name || item.item_name,
          status: newStatus, 
          notes: `${statusActionLabel} ${new Date().toLocaleDateString()}`,
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'receipt_id,receipt_item_id',
          ignoreDuplicates: false 
        });
      
      if (statusError) {
        devError('Error upserting item status:', statusError);
        // Try direct update if upsert fails
        await supabase
          .from('item_statuses')
          .update({ 
            status: newStatus, 
            notes: `${statusActionLabel} ${new Date().toLocaleDateString()}`,
            updated_at: new Date().toISOString()
          })
          .eq('receipt_id', receiptId)
          .eq('receipt_item_id', item.id);
      }
      
      // 2. Update inventory_units for this specific item (by receipt_item_id first)
      const { data: updatedUnits, error: invError } = await supabase
        .from('inventory_units')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('receipt_item_id', item.id)
        .select();
      
      if (invError || !updatedUnits?.length) {
        devLog('No units found by receipt_item_id, trying by reserved_for_receipt_id and SKU');
        // Fallback: try by reserved_for_receipt_id and SKU
        // Also set receipt_item_id so future lookups (like AI chat) can find this unit
        await supabase
          .from('inventory_units')
          .update({ 
            status: newStatus,
            receipt_item_id: item.id,
            updated_at: new Date().toISOString()
          })
          .eq('reserved_for_receipt_id', receiptId)
          .ilike('sku', item.sku);
      }
      
      // 3. Get total receipt items count
      const { data: receiptItems, error: itemsError } = await supabase
        .from('receipt_items')
        .select('id, sku, item_name')
        .eq('receipt_id', receiptId);
      
      if (itemsError) {
        devError('Error fetching receipt items:', itemsError);
      }
      
      const validItems = (receiptItems || []).filter(i => 
        (i.sku && i.sku.trim()) || (i.item_name && i.item_name.trim())
      );
      const totalItems = validItems.length;
      
      // 4. Check how many items are delivered or picked_up
      const { data: allStatuses } = await supabase
        .from('item_statuses')
        .select('status, sku')
        .eq('receipt_id', receiptId);
      
      const completedCount = allStatuses?.filter(s => 
        ITEM_TERMINAL_STATUSES.includes(s.status)
      ).length || 0;
      
      devLog(`📊 Completed count: ${completedCount}/${totalItems}`);
      
      // 5. Update receipt delivery_status
      const allCompleted = totalItems > 0 && completedCount >= totalItems;
      const anyCompleted = completedCount > 0;
      
      if (allCompleted) {
        devLog('✅ All items completed - updating receipt status via backend');
        const finalStatus = newStatus === 'picked_up' ? 'picked_up' : 'delivered';
        try {
          const resp = await fetch('/api/update-receipt-status', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: receiptId,
              delivery_status: finalStatus,
              survey_status: 'pending'
            })
          });
          const result = await resp.json();
          console.log('📋 update-receipt-status response:', resp.status, result);
        } catch (err) {
          devError('Receipt status update error:', err);
        }
        
        // Trigger survey
        try {
          const surveyResp = await fetch('/api/chat-survey', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: receiptId,
              conversation_stage: 'start_survey'
            })
          });
          const surveyData = await surveyResp.json();
          if (surveyData.success && surveyData.skipped) {
            devLog('⏭️ Survey skipped - already sent previously for this receipt');
          } else if (surveyData.success) {
            devLog('✅ Survey triggered');
          } else {
            devError('Survey trigger failed:', surveyData.error);
          }
        } catch (err) {
          devError('Survey trigger error:', err);
        }
      } else if (anyCompleted) {
        devLog('📦 Partial completion - updating receipt status via backend');
        try {
          await fetch('/api/update-receipt-status', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: receiptId,
              delivery_status: 'partially_delivered'
            })
          });
        } catch (err) {
          devError('Receipt status update error:', err);
        }
      } else if (newStatus === 'out_for_delivery' || newStatus === 'at_store') {
        // Nothing fulfilled yet and this click was on the truck axis: roll
        // the RECEIPT status the way the old dispatch flow did, so the
        // customer receipt page and the phone agent say the right thing.
        // Any item on the truck → receipt 'out_for_delivery'; last item
        // walked back off the truck → receipt returns to 'pending'.
        // (allStatuses was fetched after the upsert, so it includes this
        // click's write.)
        const anyOnTruck = (allStatuses || []).some((s) => s.status === 'out_for_delivery');
        const truckRollup = anyOnTruck ? 'out_for_delivery' : 'pending';
        devLog(`🚚 Truck-axis rollup → receipt ${truckRollup}`);
        try {
          await fetch('/api/update-receipt-status', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: receiptId,
              delivery_status: truckRollup
            })
          });
        } catch (err) {
          devError('Receipt status update error:', err);
        }
      }

      // The receipt's status is saved (for a completed receipt, the survey
      // text still follows below). Update its card now so it moves between
      // Active and Completed without waiting on the survey or a full
      // reload. Not awaited: it runs alongside the steps below.
      refreshReceiptInList(receiptId);

      // 6. Reload the receipt WITH inventoryUnits (loadReceiptDetails fetches them, fetchReceiptById does not)
      // isRefresh=true preserves expandedSkuGroups so grouped sets stay open
      await loadReceiptDetails(receiptId, true);
      
      // Show success
      const statusText =
        newStatus === 'picked_up' ? 'Picked Up'
        : newStatus === 'out_for_delivery' ? 'On Truck'
        : newStatus === 'at_store' ? 'Ready'
        : 'Delivered';
      if (allCompleted) {
        addNotification({ title: `All Items ${statusText}`, message: 'Customer survey sent!' });
      } else {
        addNotification({ 
          title: `Item ${statusText}`, 
          message: `${item.name || item.item_name} (${completedCount}/${totalItems})` 
        });
      }
      
    } catch (error) {
      devError('Error marking unit status:', error);
      addNotification({ title: 'Update Failed', message: 'Please try again', isEscalation: true });
    } finally {
      clearItemBusy(busyItemId);
    }
  };

  // Reserve an available inventory unit for a receipt line so it shows as Ready (same logic as new-item AUTO-MATCH)
  const handleMarkItemReady = async (receiptId, item) => {
    // Same self-heal as handleMarkUnitStatus — imported/legacy items
    // without an id get a receipt_items row created on first touch.
    if (!item?.id) item = (await ensureReceiptItemId(receiptId, item)) || item;
    if (!receiptId || !item?.id) {
      devError('handleMarkItemReady: Missing receiptId or item.id');
      window.alert("Couldn't update this item — no line-item record exists and creating one failed. Check your connection and try again.");
      return;
    }
    const sku = (item.sku || '').trim();
    const manufacturerId = item.manufacturerId || item.manufacturer_id || null;
    // No-SKU (or no-manufacturer) items can't be matched to an inventory
    // unit — the unit lookups all key on SKU + manufacturer. But "ready"
    // is a receipt-side state (item_statuses), not an inventory state, so
    // these items can still be marked ready; we just skip the unit
    // reservation and write the status row directly. Staff physically have
    // the item; it simply never got an item number.
    const canMatchInventory = !!(sku && manufacturerId);

    const itemName = item.name || item.item_name || '';
    const readyFamily = ['reserved', 'at_store', 'in_warehouse'];

    setMarkingItemReady(true);
    // Per-item spinner on the Ready pill for this specific row (the global
    // flag above still locks ready-transitions everywhere, unchanged).
    const busyItemId = item.id;
    setItemBusy(busyItemId, 'ready');
    try {
      // GUARD (all items): with inventory retired, item_statuses is the
      // source of truth, so the already-ready / already-completed / on-truck
      // checks must run for EVERY item — not just the no-SKU path. (The old
      // unit-based guards below see zero units and never fire.)
      const { data: priorStatus } = await supabase
        .from('item_statuses')
        .select('status')
        .eq('receipt_id', receiptId)
        .eq('receipt_item_id', item.id)
        .maybeSingle();
      if (priorStatus && ITEM_ARRIVED_STATUSES.includes(priorStatus.status)) {
        addNotification({ title: 'Already ready', message: 'This item is already marked ready.' });
        return;
      }
      if (priorStatus && UNIT_TERMINAL_STATUSES.includes(priorStatus.status)) {
        addNotification({ title: 'Already completed', message: 'This item is already delivered or picked up.' });
        return;
      }
      if (priorStatus && priorStatus.status === 'out_for_delivery') {
        addNotification({ title: 'Out for delivery', message: 'This item is already on a truck.' });
        return;
      }

      if (canMatchInventory) {
        const { data: unitsForLine, error: lineUnitsError } = await supabase
          .from('inventory_units')
          .select('id, status')
          .eq('receipt_item_id', item.id);

        if (lineUnitsError) {
          devError('handleMarkItemReady: units by receipt_item_id', lineUnitsError);
        }

        for (const u of unitsForLine || []) {
          if (readyFamily.includes(u.status)) {
            addNotification({ title: 'Already ready', message: 'This item is already marked ready.' });
            return;
          }
          if (UNIT_TERMINAL_STATUSES.includes(u.status)) {
            addNotification({ title: 'Already completed', message: 'This line is already delivered or picked up.' });
            return;
          }
          if (u.status === 'out_for_delivery') {
            addNotification({ title: 'Out for delivery', message: 'This line already has a unit out for delivery.' });
            return;
          }
        }

        let availableUnit = null;

        if (currentStore?.id) {
          let storeQuery = supabase
            .from('inventory_units')
            .select('id, sku, unit_serial, store_id')
            .ilike('sku', sku)
            .eq('status', 'available')
            .eq('store_id', currentStore.id)
            .is('reserved_for_receipt_id', null)
            .is('receipt_item_id', null);

          storeQuery = storeQuery.eq('manufacturer_id', manufacturerId);

          const { data: storeUnit } = await storeQuery.limit(1).maybeSingle();
          if (storeUnit) availableUnit = storeUnit;
        }

        if (!availableUnit) {
          let anyQuery = supabase
            .from('inventory_units')
            .select('id, sku, unit_serial, store_id')
            .ilike('sku', sku)
            .eq('status', 'available')
            .is('reserved_for_receipt_id', null)
            .is('receipt_item_id', null);

          anyQuery = anyQuery.eq('manufacturer_id', manufacturerId);

          const { data: anyUnit } = await anyQuery.limit(1).maybeSingle();
          if (anyUnit) availableUnit = anyUnit;
        }

        if (availableUnit) {
          const { error: reserveErr } = await supabase
            .from('inventory_units')
            .update({
              status: 'reserved',
              reserved_for_receipt_id: receiptId,
              receipt_item_id: item.id,
              reserved_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', availableUnit.id);

          if (reserveErr) {
            devError('handleMarkItemReady: reserve error', reserveErr);
            addNotification({ title: 'Could not mark ready', message: reserveErr.message || 'Please try again', isEscalation: true });
            return;
          }
        }
        // No available unit in stock: do NOT create inventory. Inventory is
        // only ever added by a manual add or an invoice scan. Marking ready
        // here is a receipt-side state only — we fall through to write the
        // item_statuses 'at_store' row below, exactly like the no-SKU path.
      }
      // (No-SKU / no-manufacturer items need nothing extra here: the status
      // guard above already ran for every item, and the 'at_store' row is
      // written to item_statuses below regardless of branch.)

      await supabase
        .from('item_statuses')
        .upsert(
          [
            {
              receipt_id: receiptId,
              receipt_item_id: item.id,
              item_name: itemName,
              sku,
              status: 'at_store',
              notes: 'Marked ready from order item editor'
            }
          ],
          { onConflict: 'receipt_id,receipt_item_id' }
        );

      await loadReceiptDetails(receiptId, true);
      addNotification({
        title: 'Item marked ready',
        message: itemName || sku
      });
    } catch (error) {
      devError('handleMarkItemReady error:', error);
      addNotification({ title: 'Update Failed', message: 'Please try again', isEscalation: true });
    } finally {
      setMarkingItemReady(false);
      clearItemBusy(busyItemId);
    }
  };

  // Reverse of handleMarkItemReady: release the reserved inventory unit and put the line back to Waiting.
  // - If the unit was created on-the-fly (no pre-existing stock), DELETE it so it doesn't pollute inventory.
  // - Otherwise, return it to the available pool by clearing the reservation.
  // Releases ONE reserved unit per call (matches handleMarkItemReady which reserves one per call).
  const handleUndoReady = async (receiptId, item) => {
    // Same self-heal — an id-less item that somehow shows ready/partial
    // still needs a receipt_items row before its status can be walked back.
    if (!item?.id) item = (await ensureReceiptItemId(receiptId, item)) || item;
    if (!receiptId || !item?.id) {
      devError('handleUndoReady: Missing receiptId or item.id');
      window.alert("Couldn't update this item — no line-item record exists and creating one failed. Check your connection and try again.");
      return;
    }

    const itemName = item.name || item.item_name || '';
    setMarkingItemReady(true);
    // Per-item spinner on the Waiting pill for this specific row.
    const busyItemId = item.id;
    setItemBusy(busyItemId, 'waiting');
    try {
      // 1. Find a reserved/at_store/in_warehouse unit for this receipt_item
      let unit = null;
      const { data: unitsByItemId, error: byItemErr } = await supabase
        .from('inventory_units')
        .select('*')
        .eq('receipt_item_id', item.id)
        .in('status', ['reserved', 'at_store', 'in_warehouse'])
        .limit(1);
      if (byItemErr) devError('handleUndoReady: lookup by receipt_item_id', byItemErr);
      if (unitsByItemId && unitsByItemId.length > 0) unit = unitsByItemId[0];

      // Fallback: by reserved_for_receipt_id + SKU (covers older units without receipt_item_id link)
      if (!unit && item.sku) {
        const { data: unitsBySku } = await supabase
          .from('inventory_units')
          .select('*')
          .eq('reserved_for_receipt_id', receiptId)
          .ilike('sku', item.sku)
          .in('status', ['reserved', 'at_store', 'in_warehouse'])
          .limit(1);
        if (unitsBySku && unitsBySku.length > 0) unit = unitsBySku[0];
      }

      // An item may have been marked ready without a reserved unit: no-SKU
      // items always, and SKU'd items too when no stock existed at the time
      // (marking ready no longer creates inventory). In those cases the ready
      // state lives only in item_statuses, so "nothing to undo" is true only
      // when there's also no ready status row to clear.
      if (!unit) {
        const { data: statusRow } = await supabase
          .from('item_statuses')
          .select('status')
          .eq('receipt_id', receiptId)
          .eq('receipt_item_id', item.id)
          .maybeSingle();
        const hasReadyStatus = !!(statusRow && ITEM_ARRIVED_STATUSES.includes(statusRow.status));
        if (!hasReadyStatus) {
          addNotification({
            title: 'Nothing to undo',
            message: "This item isn't marked ready."
          });
          return;
        }
        // hasReadyStatus && no unit -> status-only item: fall through; the
        // unit block is skipped and the item_statuses row is deleted below.
      }

      // 2. Decide: delete phantom unit or release back to available pool
      //    (only when a unit actually exists — status-only items skip this).
      if (unit) {
        const isPhantomUnit = (unit.notes || '').toLowerCase().includes('no pre-existing stock');

        if (isPhantomUnit) {
          const { error: delErr } = await supabase
            .from('inventory_units')
            .delete()
            .eq('id', unit.id);
          if (delErr) {
            devError('handleUndoReady: delete phantom error', delErr);
            addNotification({ title: 'Undo failed', message: delErr.message || 'Please try again', isEscalation: true });
            return;
          }
        } else {
          const { error: updErr } = await supabase
            .from('inventory_units')
            .update({
              status: 'available',
              reserved_for_receipt_id: null,
              receipt_item_id: null,
              reserved_at: null,
              updated_at: new Date().toISOString()
            })
            .eq('id', unit.id);
          if (updErr) {
            devError('handleUndoReady: release error', updErr);
            addNotification({ title: 'Undo failed', message: updErr.message || 'Please try again', isEscalation: true });
            return;
          }
        }
      }

      // 3. Remove the item_statuses row so the line displays as Waiting again
      await supabase
        .from('item_statuses')
        .delete()
        .eq('receipt_id', receiptId)
        .eq('receipt_item_id', item.id);

      await loadReceiptDetails(receiptId, true);
      addNotification({
        title: 'Ready undone',
        message: `${itemName || item.sku} is back to Waiting`
      });
    } catch (error) {
      devError('handleUndoReady error:', error);
      addNotification({ title: 'Undo failed', message: 'Please try again', isEscalation: true });
    } finally {
      setMarkingItemReady(false);
      clearItemBusy(busyItemId);
    }
  };

  // Toggle item selection in receipt detail view (for creating delivery orders)
  // FIX: Use item.id instead of SKU so each line item can be selected independently
  const toggleItemForDelivery = (itemId) => {
    setSelectedItemsForDelivery(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Clear selected items when receipt modal closes or changes
  const clearSelectedItemsForDelivery = () => {
    setSelectedItemsForDelivery({});
  };

  // Create delivery order from selected items in receipt detail view
  const handleCreateDeliveryFromReceipt = async () => {
    if (!selectedReceipt) return;
    
    // FIX: Get selected item IDs instead of SKUs
    const selectedItemIds = Object.entries(selectedItemsForDelivery)
      .filter(([_, isSelected]) => isSelected)
      .map(([itemId, _]) => itemId);
    
    if (selectedItemIds.length === 0) {
      addNotification({ title: 'No Items Selected', message: 'Please select at least one item', isEscalation: true });
      return;
    }
    
    // Get the actual items that were selected (for SKU lookup)
    const itemsToDeliver = (selectedReceipt.items || []).filter(item => 
      selectedItemIds.includes(String(item.id))
    );
    
    // Extract SKUs for the delivery function (it still needs SKUs for inventory lookup)
    const skusToDeliver = itemsToDeliver.map(item => item.sku);
    
    setIsCreatingDeliveryFromReceipt(true);
    
    try {
      const result = await createPartialDeliveryOrder(
        selectedReceipt.id, 
        skusToDeliver, 
        currentStore?.id,
        selectedItemIds  // Pass item IDs for precise inventory matching
      );
      
      if (result.success) {
        // Reload receipts to show updated status
        await loadReceipts(null, true); // silent: keep the list on screen while it refreshes
        
        // Reload the current receipt to show updated status
        const updatedReceipt = await fetchReceiptById(selectedReceipt.id);
        if (updatedReceipt) {
          setSelectedReceipt(updatedReceipt);
        }
        
        // Clear selection
        clearSelectedItemsForDelivery();
        
        // Show success message
        const message = result.isPartial 
          ? `Partial delivery order created!\n\n${result.itemCount} item(s) added to delivery route.\n\nGo to Deliveries tab to dispatch to driver.`
          : `Delivery order created!\n\n${result.itemCount} item(s) added to delivery route.\n\nGo to Deliveries tab to dispatch to driver.`;
        
        addNotification({ title: 'Delivery Order', message: message });
        
      } else {
        addNotification({ title: 'Delivery Order Failed', message: result.error || 'Unknown error', isEscalation: true });
      }
      
    } catch (error) {
      devError('Error creating delivery order:', error);
      addNotification({ title: 'Delivery Order Failed', message: 'Please try again', isEscalation: true });
    } finally {
      setIsCreatingDeliveryFromReceipt(false);
    }
  };

  // Show delete confirmation warning
  const handleAttemptDeleteReceipt = (id) => {
    setDeleteTargetId(id);
    setShowDeleteWarning(true);
  };

  // Actually perform the delete after confirmation
  const confirmDeleteReceipt = async () => {
    if (!deleteTargetId) return;
    
    // Prevent double-click
    if (isDeletingReceipt) {
      devLog('🛡️ Delete already in progress');
      return;
    }
    
    setIsDeletingReceipt(true);
    try {
      // Delete from database
      await deleteReceipt(deleteTargetId);
      
      // Update local state
      setReceipts(receipts.filter(r => r.id !== deleteTargetId));
      
      // Close any open modals that might be showing this receipt
      if (selectedReceipt?.id === deleteTargetId) {
        setShowReceiptModal(false);
        setSelectedReceipt(null);
      }
      
      addNotification({ title: 'Receipt Deleted', message: 'Receipt has been removed' });
    } catch (error) {
      devError('Error deleting receipt:', error);
      addNotification({ title: 'Delete Failed', message: 'Please try again', isEscalation: true });
    } finally {
      setIsDeletingReceipt(false);
      setShowDeleteWarning(false);
      setDeleteTargetId(null);
    }
  };

  // Cancel delete
  const cancelDeleteReceipt = () => {
    setShowDeleteWarning(false);
    setDeleteTargetId(null);
  };

  // Legacy function - now just calls the attempt function
  const handleDeleteReceipt = (id) => {
    handleAttemptDeleteReceipt(id);
  };

  // Open receipt detail view with chat
  const handleOpenReceipt = async (receipt) => {
    // ═══ INSTANT OPEN ═══════════════════════════════════════════════════
    // The drawer opens NOW with the data we already have (the list row),
    // then the full fetch below hydrates it in place. Previously the drawer
    // waited on 3-4 sequential network round-trips before appearing, so a
    // tap felt dead for seconds. The token guards against a stale fetch
    // clobbering a newer open (open A → open B → A's response lands last).
    const openToken = ++openReceiptTokenRef.current;
    const stillCurrent = () => openReceiptTokenRef.current === openToken;

    // Push / deep-link callers sometimes pass only { id } — prefer the
    // richer copy already sitting in the receipts list when the passed
    // object is bare.
    const listReceipt = Array.isArray(receipts)
      ? receipts.find((r) => r?.id === receipt?.id)
      : null;
    const hasOwnData = !!(
      receipt &&
      (receipt.customerName ||
        receipt.customer_name ||
        (receipt.items || []).length > 0)
    );
    const seedReceipt = hasOwnData ? receipt : (listReceipt || receipt);

    // Single form-builder replacing the three near-identical copies that
    // lived in the success / no-DB / catch branches (shape unchanged).
    const buildEditFormData = (r) => {
      // Normalize items to have consistent field names - PRESERVE ID and SET FIELDS for inventory links!
      const normalizedItems = (r.items || []).map(item => ({
        id: item.id, // CRITICAL: preserve ID so updates don't break inventory links
        name: item.name || item.item_name || '',
        sku: item.sku || '',
        color: item.color || '',
        quantity: item.quantity || 1,
        price: item.price !== undefined && item.price !== null ? item.price : '',
        setId: item.set_id || item.setId || null,
        setName: item.set_name || item.setName || null,
        manufacturerId: item.manufacturer_id || item.manufacturerId || null
      }));
      return {
        customerName: r.customerName || r.customer_name || '',
        customerPhone: r.customerPhone || r.customer_phone || '',
        customerEmail: r.customerEmail || r.customer_email || '',
        deliveryAddress: r.deliveryAddress || r.delivery_address || '',
        apartmentNumber: r.apartmentNumber || r.apartment_number || '',
        deliveryDate: r.deliveryDate || r.delivery_date || '',
        deliveryStatus: r.deliveryStatus || r.delivery_status || 'pending',
        items: normalizedItems,
        subtotal: r.subtotal || 0,
        tax: r.tax || 0,
        total: r.total || 0,
        amountPaid: r.amountPaid || r.amount_paid || 0,
        paymentHistory: r.paymentHistory || r.payment_history || [],
        internalNotes: r.internalNotes || r.internal_notes || '',
        itemStatuses: r.itemStatuses || [],
        salesperson: r.salesperson || '',
        deliveryServices: r.deliveryServices || r.delivery_services || {
          delivery: { enabled: false, price: '' },
          setup: { enabled: false, price: '' },
          wastePickup: { enabled: false, price: '' },
          pickup: { enabled: false, price: '' }
        },
        smsConsent: r.smsConsent || r.sms_consent || false
      };
    };

    setSelectedReceipt(seedReceipt);
    // Provisional (matches what the list card's AI/You toggle shows);
    // corrected with authoritative data once the full receipt loads.
    setIsStaffMode(
      (seedReceipt?.is_ai_handling ?? seedReceipt?.isAIHandling) === false
    );
    setEditFormData(buildEditFormData(seedReceipt || {}));
    setIsEditingInModal(false); // Always start in view mode
    setShowReceiptModal(true);
    setIsLoadingReceiptDetails(true);

    // Mark as read in local state — moved up from post-fetch so the unread
    // badge clears the moment the drawer opens.
    markReceiptAsRead(receipt.id);

    try {
      // Acknowledge any escalation when opening the receipt
      if (escalations[receipt.id] && !escalations[receipt.id].acknowledged) {
        // Update local state
        setEscalations(prev => ({
          ...prev,
          [receipt.id]: {
            ...prev[receipt.id],
            acknowledged: true
          }
        }));
        
        // For low-rating escalations, persist acknowledgment to localStorage
        // (message-based escalations persist via the messages table instead)
        if (escalations[receipt.id].type === 'low_rating') {
          try {
            const dismissed = JSON.parse(localStorage.getItem('dismissedLowRatingEscalations') || '{}');
            dismissed[receipt.id] = Date.now();
            localStorage.setItem('dismissedLowRatingEscalations', JSON.stringify(dismissed));
            devLog('✅ Low-rating escalation dismissed in localStorage for receipt:', receipt.id);
          } catch (e) {
            devError('Error saving dismissed escalation to localStorage:', e);
          }
        }
        
        // Update database - mark all escalation messages for this receipt as acknowledged
        try {
          await supabase
            .from('messages')
            .update({ escalation_acknowledged: true })
            .eq('receipt_id', receipt.id)
            .eq('escalation_needed', true);
          devLog('✅ Escalation acknowledged in database for receipt:', receipt.id);
        } catch (dbErr) {
          devError('Error updating escalation in database:', dbErr);
        }
      }
      
      // Try to load from Supabase first
      const fullReceipt = await fetchReceiptById(receipt.id);
      if (fullReceipt) {
        // Inventory units + set fields: derived from what fetchReceiptById
        // already loaded (was three more sequential re-fetches).
        const drawerFields = deriveDrawerFields(fullReceipt, receipt.id);
        fullReceipt.inventoryUnits = drawerFields.inventoryUnits;
        fullReceipt.items = drawerFields.items;
        devLog(`📦 handleOpenReceipt: Loaded ${fullReceipt.inventoryUnits.length} inventory units for receipt ${receipt.id}`);

        if (import.meta.env.DEV) verifyDrawerFieldsParity(receipt.id, fullReceipt, { withStatuses: false });

        // ═══ HYDRATE ═══ A newer open (or a drawer close) may have
        // superseded this fetch while it was in flight — bail out instead
        // of clobbering the newer receipt's state.
        if (!stillCurrent()) return;

        setSelectedReceipt(fullReceipt);
        setIsStaffMode(!fullReceipt.is_ai_handling);
        // Refresh the edit form with authoritative data — unless the user
        // already entered edit mode while this loaded (don't clobber typing).
        if (!isEditingInModalRef.current) {
          setEditFormData(buildEditFormData(fullReceipt));
        }
      }
      // No fullReceipt (receipt not in DB yet): the seed state set at open
      // time is all there is — nothing further to hydrate.
    } catch (error) {
      devError('Error loading receipt:', error);
      // Drawer is already open with the seed receipt; nothing to roll back.
    } finally {
      if (stillCurrent()) setIsLoadingReceiptDetails(false);
    }

    // Tail work (push dismiss + DB read-marking) belongs to this open only —
    // skip it if a newer open or a close superseded us mid-fetch.
    if (!stillCurrent()) return;

    // Dismiss push notification on all other devices for this receipt
    try {
      const swReg = await navigator.serviceWorker?.ready;
      const sub = await swReg?.pushManager?.getSubscription();
      const notificationType = escalations[receipt.id] ? 'escalation' : 'message';
      await fetch('/api/push/dismiss', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
        },
        body: JSON.stringify({
          receipt_id: receipt.id,
          store_id: currentStore?.id,
          notification_type: notificationType,
          endpoint: sub?.endpoint || null
        })
      });
    } catch (dismissErr) {
      devLog('[Push] Dismiss failed (non-critical):', dismissErr);
    }

    // Mark messages as read in database AND reset unread count on receipt
    try {
      // Update individual messages
      await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('receipt_id', receipt.id)
        .eq('is_read', false);
      
      // Reset unread_messages_count on the receipt itself
      await supabase
        .from('receipts')
        .update({ unread_messages_count: 0 })
        .eq('id', receipt.id);
    } catch (error) {
      devError('Error marking messages as read:', error);
    }
  };
  
  // Alias for notification click handler
  const handleViewReceipt = handleOpenReceipt;

  // Check if there are unsaved changes in the receipt modal
  const hasUnsavedChanges = () => {
    if (!isEditingInModal || !editFormData || !selectedReceipt) return false;
    
    // Compare key fields between editFormData and selectedReceipt
    const original = {
      customerName: selectedReceipt.customerName || selectedReceipt.customer_name || '',
      customerPhone: selectedReceipt.customerPhone || selectedReceipt.customer_phone || '',
      customerEmail: selectedReceipt.customerEmail || selectedReceipt.customer_email || '',
      deliveryAddress: selectedReceipt.deliveryAddress || selectedReceipt.delivery_address || '',
      deliveryDate: selectedReceipt.deliveryDate || selectedReceipt.delivery_date || '',
      deliveryStatus: selectedReceipt.deliveryStatus || selectedReceipt.delivery_status || 'pending',
      internalNotes: selectedReceipt.internalNotes || selectedReceipt.internal_notes || '',
      amountPaid: selectedReceipt.amountPaid || selectedReceipt.amount_paid || 0,
      salesperson: selectedReceipt.salesperson || '',
      smsConsent: selectedReceipt.smsConsent || selectedReceipt.sms_consent || false,
    };
    
    const current = {
      customerName: editFormData.customerName || '',
      customerPhone: editFormData.customerPhone || '',
      customerEmail: editFormData.customerEmail || '',
      deliveryAddress: editFormData.deliveryAddress || '',
      deliveryDate: editFormData.deliveryDate || '',
      deliveryStatus: editFormData.deliveryStatus || 'pending',
      internalNotes: editFormData.internalNotes || '',
      amountPaid: editFormData.amountPaid || 0,
      salesperson: editFormData.salesperson || '',
      smsConsent: editFormData.smsConsent || false,
    };
    
    // Check basic fields
    for (const key of Object.keys(original)) {
      if (String(original[key]) !== String(current[key])) {
        return true;
      }
    }
    
    // Check items - compare length and content
    const originalItems = selectedReceipt.items || [];
    const currentItems = editFormData.items || [];
    
    if (originalItems.length !== currentItems.length) return true;
    
    for (let i = 0; i < originalItems.length; i++) {
      const origItem = originalItems[i];
      const currItem = currentItems[i];
      
      if (
        (origItem.name || origItem.item_name || '') !== (currItem.name || currItem.item_name || '') ||
        (origItem.sku || '') !== (currItem.sku || '') ||
        (origItem.color || '') !== (currItem.color || '') ||
        Number(origItem.quantity || 1) !== Number(currItem.quantity || 1) ||
        Number(origItem.price || 0) !== Number(currItem.price || 0)
      ) {
        return true;
      }
    }
    
    return false;
  };

  // Attempt to close the receipt modal - shows warning if there are unsaved changes
  const handleAttemptCloseReceiptModal = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesWarning(true);
    } else {
      handleCloseReceiptModal();
    }
  };

  // Confirm discarding unsaved changes and close modal
  const confirmDiscardChanges = () => {
    setShowUnsavedChangesWarning(false);
    handleCloseReceiptModal();
  };

  // Cancel discarding changes - just hide the warning
  const cancelDiscardChanges = () => {
    setShowUnsavedChangesWarning(false);
  };

  const handleCloseReceiptModal = () => {
    // Invalidate any in-flight open/hydration so a late fetch response
    // can't repopulate state after the drawer has closed.
    openReceiptTokenRef.current += 1;
    setIsLoadingReceiptDetails(false);
    setShowReceiptModal(false);
    setSelectedReceipt(null);
    setStaffMessage('');
    setIsMobileChatOpen(false);
    setIsEditingInModal(false);
    setEditFormData(null);
    setIsMobileEditingDetails(false); // Reset mobile editing state
    clearSelectedItemsForDelivery(); // Clear item selection for delivery
    setShowUnsavedChangesWarning(false); // Reset warning state
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Drawer URL sync — keeps ?receipt={id} in the URL while the receipt drawer
  // is open. Enables shareable links, browser back/forward, and refresh
  // persistence. Uses { replace: true } to avoid spamming history with every
  // open/close cycle.
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    const currentReceiptParam = newParams.get('receipt');
    const targetReceiptId = (showReceiptModal && selectedReceipt?.id) ? selectedReceipt.id : null;

    if (targetReceiptId && currentReceiptParam !== targetReceiptId) {
      newParams.set('receipt', targetReceiptId);
      setSearchParams(newParams, { replace: true });
    } else if (!targetReceiptId && currentReceiptParam) {
      newParams.delete('receipt');
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReceiptModal, selectedReceipt?.id]);

  // Deep-link restore: if URL has ?receipt={id} on first load (or after
  // receipts finish loading), open that receipt automatically. Runs once.
  const deepLinkProcessedRef = useRef(false);
  useEffect(() => {
    if (deepLinkProcessedRef.current) return;
    if (!receipts || receipts.length === 0) return;
    const receiptId = searchParams.get('receipt');
    if (!receiptId) {
      deepLinkProcessedRef.current = true;
      return;
    }
    const receipt = receipts.find((r) => r.id === receiptId);
    if (receipt) {
      deepLinkProcessedRef.current = true;
      handleOpenReceipt(receipt);
    }
    // If receipt isn't found yet (perhaps still paginating), don't mark
    // processed — we'll try again on the next receipts update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts]);

  // Escape key closes the drawer (with unsaved-changes guard via
  // handleAttemptCloseReceiptModal). Only active while drawer is open.
  useEffect(() => {
    if (!showReceiptModal) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        handleAttemptCloseReceiptModal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReceiptModal]);

  // Save edits made in the receipt detail modal
  const handleSaveEditInModal = async () => {
    if (!selectedReceipt || !editFormData) return;
    
    // Prevent double-click
    if (isSavingEdit) {
      devLog('🛡️ Save already in progress');
      return;
    }
    setIsSavingEdit(true);
    
    try {
      const { subtotal, tax, total } = calculateTotals(editFormData.items, editFormData.deliveryServices);
      
      // Check if status is changing to delivered
      // SSOT: picked_up closes an order exactly like delivered (see the
      // matching note in handleSaveReceipt). Survey + closure side-effects
      // now fire for both terminal statuses.
      const wasDelivered = ITEM_TERMINAL_STATUSES.includes(selectedReceipt.deliveryStatus) || ITEM_TERMINAL_STATUSES.includes(selectedReceipt.delivery_status);
      const isNowDelivered = ITEM_TERMINAL_STATUSES.includes(editFormData.deliveryStatus);
      const justDelivered = !wasDelivered && isNowDelivered;
      
      devLog('📊 Status change check (inline edit):', { wasDelivered, isNowDelivered, justDelivered, editFormDataStatus: editFormData.deliveryStatus });

      const { error: updateError } = await supabase
        .from('receipts')
        .update({
          customer_name: editFormData.customerName,
          customer_phone: editFormData.customerPhone,
          customer_email: editFormData.customerEmail || null,
          delivery_address: editFormData.deliveryAddress || null,
          apartment_number: editFormData.apartmentNumber || null,
          delivery_status: editFormData.deliveryStatus,
          delivery_date: editFormData.deliveryDate || null,
          internal_notes: editFormData.internalNotes || null,
          subtotal,
          tax,
          total,
          amount_paid: parseFloat(editFormData.amountPaid) || 0,
          payment_history: editFormData.paymentHistory || [],
          salesperson: editFormData.salesperson || null,
          sms_consent: editFormData.smsConsent || false,
          delivery_services: editFormData.deliveryServices || null,
          ...(justDelivered && { survey_status: 'pending' })
        })
        .eq('id', selectedReceipt.id);

      if (updateError) {
        devError('Error updating receipt:', updateError);
        throw new Error(`Failed to update receipt: ${updateError.message}`);
      }

      // DELETE removed items: compare what's in the DB for this receipt against
      // the ids still present in the edit form. Anything in the DB but not in the
      // form was removed by the user (e.g. deleting a piece from a saved group),
      // so delete those rows. Without this, removed items silently reappear on
      // reload. Release their inventory units + statuses first.
      try {
        const { data: dbItems, error: dbItemsError } = await supabase
          .from('receipt_items')
          .select('id')
          .eq('receipt_id', selectedReceipt.id);

        if (dbItemsError) {
          devError('Error fetching items for reconcile (inline edit):', dbItemsError);
        } else {
          const keptIds = new Set(
            (editFormData.items || [])
              .filter(i => i.id)
              .map(i => i.id)
          );
          const removedIds = (dbItems || [])
            .map(r => r.id)
            .filter(id => !keptIds.has(id));

          if (removedIds.length > 0) {
            devLog(`🗑️ (inline edit) Deleting ${removedIds.length} removed item(s):`, removedIds);

            // Release / delete inventory units linked to the removed items
            const { data: linkedUnits, error: unitsError } = await supabase
              .from('inventory_units')
              .select('id, notes')
              .in('receipt_item_id', removedIds);

            if (unitsError) {
              devError('Error fetching units for removed items (inline edit):', unitsError);
            } else if (linkedUnits && linkedUnits.length > 0) {
              const phantomUnitIds = [];
              const releaseUnitIds = [];
              for (const u of linkedUnits) {
                if ((u.notes || '').toLowerCase().includes('no pre-existing stock')) {
                  phantomUnitIds.push(u.id);
                } else {
                  releaseUnitIds.push(u.id);
                }
              }

              if (releaseUnitIds.length > 0) {
                const { error: relErr } = await supabase
                  .from('inventory_units')
                  .update({
                    status: 'available',
                    reserved_for_receipt_id: null,
                    receipt_item_id: null,
                    reserved_at: null,
                    updated_at: new Date().toISOString()
                  })
                  .in('id', releaseUnitIds);
                if (relErr) devError('Error releasing units (inline edit):', relErr);
              }

              if (phantomUnitIds.length > 0) {
                const { error: phErr } = await supabase
                  .from('inventory_units')
                  .delete()
                  .in('id', phantomUnitIds);
                if (phErr) devError('Error deleting phantom units (inline edit):', phErr);
              }
            }

            // Remove item_statuses rows for the removed items
            const { error: statusErr } = await supabase
              .from('item_statuses')
              .delete()
              .in('receipt_item_id', removedIds);
            if (statusErr) devError('Error deleting item_statuses (inline edit):', statusErr);

            // Delete the receipt_items rows themselves
            const { error: delItemsErr } = await supabase
              .from('receipt_items')
              .delete()
              .in('id', removedIds);
            if (delItemsErr) {
              devError('Error deleting removed receipt items (inline edit):', delItemsErr);
            } else {
              devLog(`✅ (inline edit) Deleted ${removedIds.length} removed item(s)`);
            }
          }
        }
      } catch (reconcileError) {
        devError('Item reconcile/delete failed (inline edit):', reconcileError);
      }

      // UPDATE ITEMS - Only update existing items in place, preserving IDs for inventory links
      // DO NOT delete and recreate - that breaks inventory_unit links!
      if (editFormData.items && editFormData.items.length > 0) {
        const existingItems = selectedReceipt.items || [];
        let newItemsAdded = 0; // Track inserts so we can reopen a completed receipt below
        
        for (const item of editFormData.items) {
          // If item has an ID, update it in place
          if (item.id) {
            const { error: updateItemError } = await supabase
              .from('receipt_items')
              .update({
                item_name: item.name || item.item_name || '',
                sku: ensureSku(item.sku),
                color: item.color || '',
                quantity: item.quantity || 1,
                price: parseFloat(item.price) || 0,
                set_id: item.setId || null,
                set_name: item.setName || null,
                manufacturer_id: item.manufacturerId || null
              })
              .eq('id', item.id);
            
            if (updateItemError) {
              devError('Error updating item:', updateItemError);
            }
          } else {
            // NEW ITEM — explode qty > 1 into individual rows so every
            // physical piece is independently trackable (statuses, routes,
            // partial delivery). Matches handleSaveReceipt's edit path;
            // previously this path inserted one coarse row with quantity.
            const explodedPieces = explodeItems([item]);
            const rowsToInsert = explodedPieces.map((piece) => ({
              receipt_id: selectedReceipt.id,
              item_name: piece.name || piece.item_name || '',
              sku: ensureSku(piece.sku),
              color: piece.color || '',
              quantity: 1,
              price: parseFloat(piece.price) || 0,
              set_id: piece.setId || null,
              set_name: piece.setName || null,
              manufacturer_id: piece.manufacturerId || piece.manufacturer_id || null
            }));

            const { error: insertItemError } = await supabase
              .from('receipt_items')
              .insert(rowsToInsert);
            
            if (insertItemError) {
              devError('Error inserting new item:', insertItemError);
            } else {
              newItemsAdded += rowsToInsert.length;
            }
          }
        }
        devLog('✅ Items updated in place (inventory links preserved)');
        
        // BUGFIX: A completed receipt that receives a NEW item is no longer
        // fully fulfilled. If the user didn't explicitly change the status
        // dropdown in this edit, demote delivered/picked_up →
        // partially_delivered so the receipt returns to the New Orders tab.
        // (The per-item delivery flow promotes it back to 'delivered' once
        // every item reaches a terminal status again.)
        const originalStatus = selectedReceipt.deliveryStatus || selectedReceipt.delivery_status;
        const statusUntouched = editFormData.deliveryStatus === originalStatus;
        if (newItemsAdded > 0 && statusUntouched && ITEM_TERMINAL_STATUSES.includes(editFormData.deliveryStatus)) {
          const { error: demoteError } = await supabase
            .from('receipts')
            .update({ delivery_status: 'partially_delivered' })
            .eq('id', selectedReceipt.id);
          
          if (demoteError) {
            devError('Error moving completed receipt back to active (inline edit):', demoteError);
          } else {
            devLog('🔄 (inline edit) New item on completed receipt → demoted to partially_delivered (back to New Orders tab)');
            addNotification({ title: 'Order Reopened', message: 'New item added — order moved back to New Orders' });
          }
        }
      }

      // Trigger survey if just delivered
      if (justDelivered) {
        devLog('🎯 Order marked as delivered, triggering customer survey...');
        
        // Inventory is retired — propagate the receipt-level status change
        // to item_statuses (the sole fulfillment source). Without this the
        // AI chat / customer receipt keep reporting items as ready/pending
        // on an order that was just marked delivered.
        try {
          const statusSweep = await markAllItemStatusesDelivered(selectedReceipt.id, editFormData.deliveryStatus);
          devLog(`📦 Marked ${statusSweep.updated} item status(es) as ${editFormData.deliveryStatus}`);
        } catch (statusErr) {
          devError('Error marking item statuses as delivered:', statusErr);
        }
        
        // Survey gates on having a phone number only. The SMS-consent
        // checkbox is a point-of-sale reminder for staff, NOT a send gate
        // (store policy, Jul 2026) — matching the receipt SMS, which also
        // sends without it. STOP/opt-out is still enforced server-side
        // (sms_opted_out checks in the SMS endpoints).
        const hasPhone = editFormData.customerPhone || selectedReceipt.customerPhone || selectedReceipt.customer_phone;
        
        if (!hasPhone) {
          devLog('⚠️ Cannot send survey - no phone number');
          addNotification({ title: 'Marked Complete', message: 'No phone number - survey not sent' });
        } else {
          try {
            const response = await fetch('/api/chat-survey', {
              method: 'POST',
              headers: await authHeaders(),
              body: JSON.stringify({
                receipt_id: selectedReceipt.id,
                conversation_stage: 'start_survey'
              })
            });
            const data = await response.json();
            if (data.success && data.skipped) {
              // Duplicate guard: backend didn't send because a survey
              // already went out for this receipt — say so honestly
              devLog('⏭️ Survey skipped - already sent previously for this receipt');
              addNotification({ title: 'Marked Complete', message: 'Survey already sent previously - not re-sent' });
            } else if (data.success) {
              devLog('✅ Survey started successfully');
              addNotification({ title: 'Marked Complete', message: 'Customer survey sent via SMS' });
            } else {
              devError('Survey trigger failed:', data.error);
              addNotification({ title: 'Marked Complete', message: 'Survey failed to send - check logs' });
            }
          } catch (error) {
            devError('Error triggering survey:', error);
            addNotification({ title: 'Marked Complete', message: 'Survey failed to send - check logs' });
          }
        }
      } else {
        addNotification({ title: 'Receipt Updated', message: 'Changes saved successfully' });
      }

      // Reload receipts from database
      await loadReceipts(null, true); // silent: keep the list on screen while it refreshes
      
      // Reload the selected receipt to show updated data
      const updatedReceipt = await fetchReceiptById(selectedReceipt.id);
      if (updatedReceipt) {
        setSelectedReceipt(updatedReceipt);
        // Normalize items to have consistent field names - PRESERVE ID for inventory links!
        const normalizedItems = (updatedReceipt.items || []).map(item => ({
          id: item.id, // CRITICAL: preserve ID so updates don't break inventory links
          name: item.name || item.item_name || '',
          sku: item.sku || '',
          color: item.color || '',
          quantity: item.quantity || 1,
          price: item.price !== undefined && item.price !== null ? item.price : '',
          setId: item.set_id || item.setId || null,
          setName: item.set_name || item.setName || null,
          manufacturerId: item.manufacturer_id || item.manufacturerId || null
        }));
        // This must carry EVERY field the update above writes. It used to
        // omit apartmentNumber, paymentHistory, salesperson, smsConsent and
        // the item set fields, so a second Edit -> Save in the same drawer
        // session wrote them back empty.
        setEditFormData({
          customerName: updatedReceipt.customerName || updatedReceipt.customer_name || '',
          customerPhone: updatedReceipt.customerPhone || updatedReceipt.customer_phone || '',
          customerEmail: updatedReceipt.customerEmail || updatedReceipt.customer_email || '',
          deliveryAddress: updatedReceipt.deliveryAddress || updatedReceipt.delivery_address || '',
          apartmentNumber: updatedReceipt.apartmentNumber || updatedReceipt.apartment_number || '',
          deliveryDate: updatedReceipt.deliveryDate || updatedReceipt.delivery_date || '',
          deliveryStatus: updatedReceipt.deliveryStatus || updatedReceipt.delivery_status || 'pending',
          items: normalizedItems,
          subtotal: updatedReceipt.subtotal || 0,
          tax: updatedReceipt.tax || 0,
          total: updatedReceipt.total || 0,
          amountPaid: updatedReceipt.amountPaid || updatedReceipt.amount_paid || 0,
          paymentHistory: updatedReceipt.paymentHistory || updatedReceipt.payment_history || [],
          internalNotes: updatedReceipt.internalNotes || updatedReceipt.internal_notes || '',
          itemStatuses: updatedReceipt.itemStatuses || [],
          salesperson: updatedReceipt.salesperson || '',
          smsConsent: updatedReceipt.smsConsent || updatedReceipt.sms_consent || false,
          deliveryServices: updatedReceipt.deliveryServices || updatedReceipt.delivery_services || {
            delivery: { enabled: false, price: '' },
            setup: { enabled: false, price: '' },
            wastePickup: { enabled: false, price: '' },
            pickup: { enabled: false, price: '' }
          }
        });
      }
      
      setIsEditingInModal(false); // Exit edit mode
      
    } catch (error) {
      devError('Error saving edits:', error);
      addNotification({ title: 'Save Failed', message: error.message, isEscalation: true });
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Cancel edit mode and restore original data
  const handleCancelEditInModal = () => {
    if (selectedReceipt) {
      // Normalize items to have consistent field names - PRESERVE ID and SET FIELDS for inventory links!
      const normalizedItems = (selectedReceipt.items || []).map(item => ({
        id: item.id, // CRITICAL: preserve ID so updates don't break inventory links
        name: item.name || item.item_name || '',
        sku: item.sku || '',
        color: item.color || '',
        quantity: item.quantity || 1,
        price: item.price !== undefined && item.price !== null ? item.price : '',
        setId: item.set_id || item.setId || null,
        setName: item.set_name || item.setName || null,
        manufacturerId: item.manufacturer_id || item.manufacturerId || null
      }));
      setEditFormData({
        customerName: selectedReceipt.customerName || selectedReceipt.customer_name || '',
        customerPhone: selectedReceipt.customerPhone || selectedReceipt.customer_phone || '',
        customerEmail: selectedReceipt.customerEmail || selectedReceipt.customer_email || '',
        deliveryAddress: selectedReceipt.deliveryAddress || selectedReceipt.delivery_address || '',
        deliveryDate: selectedReceipt.deliveryDate || selectedReceipt.delivery_date || '',
        deliveryStatus: selectedReceipt.deliveryStatus || selectedReceipt.delivery_status || 'pending',
        salesperson: selectedReceipt.salesperson || '',
        smsConsent: selectedReceipt.smsConsent || selectedReceipt.sms_consent || false,
        items: normalizedItems,
        subtotal: selectedReceipt.subtotal || 0,
        tax: selectedReceipt.tax || 0,
        total: selectedReceipt.total || 0,
        amountPaid: selectedReceipt.amountPaid || selectedReceipt.amount_paid || 0,
        internalNotes: selectedReceipt.internalNotes || selectedReceipt.internal_notes || '',
        itemStatuses: selectedReceipt.itemStatuses || [],
        deliveryServices: selectedReceipt.deliveryServices || selectedReceipt.delivery_services || {
          delivery: { enabled: false, price: '' },
          setup: { enabled: false, price: '' },
          wastePickup: { enabled: false, price: '' },
          pickup: { enabled: false, price: '' }
        }
      });
    }
    setIsEditingInModal(false);
  };

  const handleTakeOverChat = async () => {
    if (!selectedReceipt) return;
    
    // Prevent double-click
    if (isTakingOver) {
      devLog('🛡️ Takeover already in progress');
      return;
    }
    setIsTakingOver(true);
    
    try {
      await supabase
        .from('receipts')
        .update({ is_ai_handling: false })
        .eq('id', selectedReceipt.id);

      setIsStaffMode(true);
      setSelectedReceipt({ ...selectedReceipt, is_ai_handling: false });
      await sendMessage(selectedReceipt.id, 'system', 'Staff has taken over the conversation', 'web');
    } catch (error) {
      devError('Error taking over chat:', error);
    } finally {
      setIsTakingOver(false);
    }
  };

  const handleReturnToAI = async () => {
    if (!selectedReceipt) return;
    
    // Prevent double-click
    if (isReturningToAI) {
      devLog('🛡️ Return to AI already in progress');
      return;
    }
    setIsReturningToAI(true);
    
    try {
      await supabase
        .from('receipts')
        .update({ is_ai_handling: true })
        .eq('id', selectedReceipt.id);

      setIsStaffMode(false);
      setSelectedReceipt({ ...selectedReceipt, is_ai_handling: true });
      await sendMessage(selectedReceipt.id, 'system', 'Conversation returned to AI', 'web');
    } catch (error) {
      devError('Error returning to AI:', error);
    } finally {
      setIsReturningToAI(false);
    }
  };

  // ==========================================
  // TOGGLE CHAT MODE FROM RECEIPT CARD
  // Allows toggling AI/Store mode without opening receipt
  // ==========================================
  
  const handleToggleChatMode = async (receipt, e) => {
    // Prevent card click when toggling
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    if (!receipt) return;
    
    // Prevent double-click
    if (togglingChatModeId === receipt.id) {
      devLog('🛡️ Toggle already in progress for receipt:', receipt.id);
      return;
    }
    
    setTogglingChatModeId(receipt.id);
    
    const currentlyAI = receipt.is_ai_handling !== false; // Default to AI if not set
    const newMode = !currentlyAI;
    
    try {
      // Update database
      const { error } = await supabase
        .from('receipts')
        .update({ is_ai_handling: newMode })
        .eq('id', receipt.id);
      
      if (error) throw error;
      
      // Update local state - update the receipt in the receipts array
      setReceipts(prev => prev.map(r => 
        r.id === receipt.id 
          ? { ...r, is_ai_handling: newMode }
          : r
      ));
      
      // If this receipt is currently selected, update it too
      if (selectedReceipt?.id === receipt.id) {
        setSelectedReceipt(prev => ({ ...prev, is_ai_handling: newMode }));
        setIsStaffMode(!newMode);
      }
      
      // Send system message about the mode change
      const message = newMode 
        ? 'Chat returned to AI mode' 
        : 'Staff has taken over the chat';
      await sendMessage(receipt.id, 'system', message, 'web');
      
      devLog(`✅ Chat mode toggled for receipt ${receipt.id}: AI=${newMode}`);
    } catch (error) {
      devError('Error toggling chat mode:', error);
    } finally {
      setTogglingChatModeId(null);
    }
  };

  // Search filter function for receipts
  const filterReceiptsBySearch = (receiptList) => {
    if (!searchQuery.trim()) return receiptList;
    
    const query = searchQuery.toLowerCase().trim();
    // Strip non-digits for phone comparison
    const queryDigitsOnly = query.replace(/\D/g, '');
    
    return receiptList.filter(r => {
      const customerName = (r.customerName || r.customer_name || '').toLowerCase();
      const customerPhone = (r.customerPhone || r.customer_phone || '');
      const items = r.items || [];
      
      // Search by customer name
      if (customerName.includes(query)) return true;
      
      // Search by phone number (compare digits only)
      if (queryDigitsOnly.length >= 3) {
        const phoneDigitsOnly = customerPhone.replace(/\D/g, '');
        if (phoneDigitsOnly.includes(queryDigitsOnly)) return true;
      }
      
      // Search by item name or SKU
      const itemMatch = items.some(item => {
        const itemName = (item.name || item.item_name || '').toLowerCase();
        const sku = (item.sku || '').toLowerCase();
        return itemName.includes(query) || sku.includes(query);
      });
      if (itemMatch) return true;
      
      return false;
    });
  };

  // Filter by created date (week/month).
  // When a search query is active, the period window is bypassed entirely so
  // search spans all time — otherwise a matching customer outside the selected
  // week/month would be silently hidden. Empty-search browsing still respects
  // the period pill.
  const filterReceiptsByPeriod = (receiptList) => {
    if (searchQuery.trim()) return receiptList;
    if (receiptPeriodFilter === 'all') return receiptList;
    return receiptList.filter((r) => {
      const d = getReceiptCreatedAt(r);
      if (!d) return false;
      if (receiptPeriodFilter === 'month') return isDateInCalendarMonthYear(d, receiptSelectedMonth);
      if (receiptPeriodFilter === 'week') return isDateInCurrentCalendarWeek(d);
      return true;
    });
  };

  // Filter receipts by status, then period, then search
  // PERF: Memoized so this filter chain doesn't recompute on every render
  // (e.g. polling tick, unread-count change, escalation update).
  // filterReceiptsBySearch / filterReceiptsByPeriod are stable in identity
  // each render but read from state — we list their inputs explicitly.
  const newOrdersReceipts = useMemo(() => (
    filterReceiptsBySearch(filterReceiptsByPeriod(receipts.filter(r => {
      const status = r.deliveryStatus || r.delivery_status;
      
      // Include orders that are NOT delivered yet
      return !RECEIPT_TERMINAL_STATUSES.includes(status);
    })))
    // Inputs of the filter helpers are listed below so memo invalidates correctly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [receipts, searchQuery, receiptPeriodFilter, receiptSelectedMonth]);

  const completedOrdersReceipts = useMemo(() => (
    filterReceiptsBySearch(filterReceiptsByPeriod(receipts.filter(r => {
      const status = r.deliveryStatus || r.delivery_status;
      
      // Include all delivered and picked_up orders
      return ITEM_TERMINAL_STATUSES.includes(status);
    })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [receipts, searchQuery, receiptPeriodFilter, receiptSelectedMonth]);

  // Get active receipts based on tab
  const activeReceipts = activeTab === 'new' ? newOrdersReceipts : completedOrdersReceipts;

  // Calculate notification counts for Completed tab
  // PERF: Memoized — this loop ran on every render previously.
  const completedTabNotifications = useMemo(() => {
    let newMessageCount = 0;
    let escalationCount = 0;
    
    completedOrdersReceipts.forEach(receipt => {
      // Check for unread messages
      const unreadCount = unreadMessages[receipt.id] || 0;
      const hasUnread = unreadCount > 0 || (receipt.hasActiveChat && receipt.unreadMessages > 0);
      if (hasUnread) {
        newMessageCount++;
      }
      
      // Check for unacknowledged escalations
      const escalation = escalations[receipt.id];
      if (escalation && !escalation.acknowledged) {
        escalationCount++;
      }
    });
    
    return { newMessageCount, escalationCount };
  }, [completedOrdersReceipts, unreadMessages, escalations]);

  const handleSendStaffMessage = async () => {
    if ((!staffMessage.trim() && !staffImage) || !selectedReceipt) return;

    // Check if customer has opted out of SMS
    if (selectedReceipt.sms_opted_out) {
      addNotification({ 
        title: 'Cannot Send Message', 
        message: 'Customer has opted out of SMS. Wait for them to text START to re-subscribe.', 
        isEscalation: true 
      });
      return;
    }

    setSendingMessage(true);
    
    try {
      let mediaUrl = null;
      
      // 1. If there's an image, compress and upload to Supabase Storage
      if (staffImage) {
        // Compress image for MMS (max 300KB, max 800px dimension)
        const compressImage = (file) => {
          return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = () => {
              // Calculate new dimensions (max 800px on longest side)
              const maxDim = 800;
              let width = img.width;
              let height = img.height;
              
              if (width > maxDim || height > maxDim) {
                if (width > height) {
                  height = (height / width) * maxDim;
                  width = maxDim;
                } else {
                  width = (width / height) * maxDim;
                  height = maxDim;
                }
              }
              
              canvas.width = width;
              canvas.height = height;
              ctx.drawImage(img, 0, 0, width, height);
              
              // Convert to JPEG with compression
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
                  } else {
                    reject(new Error('Failed to compress image'));
                  }
                },
                'image/jpeg',
                0.7 // 70% quality - good balance of size and quality
              );
            };
            
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = URL.createObjectURL(file);
          });
        };
        
        // Compress the image
        const compressedImage = await compressImage(staffImage);
        console.log(`Original: ${(staffImage.size / 1024).toFixed(0)}KB → Compressed: ${(compressedImage.size / 1024).toFixed(0)}KB`);
        
        const fileName = `${selectedReceipt.id}-${Date.now()}.jpg`;
        const filePath = `mms-attachments/${fileName}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('chat-media')
          .upload(filePath, compressedImage, {
            cacheControl: '3600',
            upsert: false,
            contentType: 'image/jpeg'
          });
        
        if (uploadError) {
          throw new Error(`Failed to upload image: ${uploadError.message}`);
        }
        
        // Get public URL for the uploaded image
        const { data: { publicUrl } } = supabase.storage
          .from('chat-media')
          .getPublicUrl(filePath);
        
        mediaUrl = publicUrl;
      }
      
      // 2. Save to Supabase (include media URL in message if present)
      const messageContent = mediaUrl 
        ? (staffMessage.trim() ? `${staffMessage}\n${mediaUrl}` : mediaUrl)
        : staffMessage;
      await sendMessage(selectedReceipt.id, 'staff', messageContent, 'web');

      // 3. Send via ClickSend (MMS if image, SMS if text only)
      const endpoint = mediaUrl ? '/api/sms/send-mms' : '/api/sms/send-message';
      const payload = mediaUrl 
        ? {
            customerPhone: selectedReceipt.customer_phone,
            message: staffMessage.trim() || ' ', // MMS requires some text
            mediaUrl: mediaUrl,
            receiptId: selectedReceipt.id
          }
        : {
            customerPhone: selectedReceipt.customer_phone,
            message: staffMessage
          };
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to send ${mediaUrl ? 'MMS' : 'SMS'}`);
      }

      // 4. Clear inputs and reload
      setStaffMessage('');
      setStaffImage(null);
      await loadReceiptDetails(selectedReceipt.id);
      
    } catch (error) {
      devError('Error sending staff message:', error);
      addNotification({ title: 'Message Failed', message: error.message || 'Please try again', isEscalation: true });
    } finally {
      setSendingMessage(false);
    }
  };

  // Simulate receiving a message from customer (for demo)
  const simulateCustomerMessage = async (message) => {
    if (!selectedReceipt) return;

    try {
      devLog('🔵 Simulating customer message:', message);
      
      // 1. Save customer message to database
      await sendMessage(selectedReceipt.id, 'user', message, 'test');
      
      // 2. Get the last AI message (INCLUDE survey messages for rating detection)
      const lastAIMessage = selectedReceipt.messages
        .filter(m => m.role === 'assistant')
        .pop();
      
      devLog('📨 Last AI message content:', lastAIMessage?.content?.substring(0, 100));
      
      // 3. Check if message is a rating (1-5)
      const isRating = /^[1-5]$/.test(message.trim());
      
      // 4. Check if last message was asking for rating
      const isAskingForRating = lastAIMessage?.content?.toLowerCase().includes('rate it from 1-5') || 
                                 lastAIMessage?.content?.toLowerCase().includes('rate your experience');
      
      devLog('Is rating:', isRating, '| Was asking for rating:', isAskingForRating);
      
      // 5. Route appropriately
      if (isRating && isAskingForRating) {
        // This is a survey rating response
        devLog('🎯 RATING DETECTED - Routing to survey API');
        
        const response = await fetch('/api/chat-survey', {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({
            receipt_id: selectedReceipt.id,
            customer_response: message,
            conversation_stage: 'awaiting_rating'
          })
        });
        
        const data = await response.json();
        devLog('✅ Survey API response:', data);
        
        if (!data.success) {
          devError('Survey API error:', data.error);
          addNotification({ title: 'Survey Error', message: data.error, isEscalation: true });
        }
      } else {
        // Check if it's other survey stages (also check survey messages)
        const content = lastAIMessage?.content?.toLowerCase() || '';
        let conversationStage = 'normal_chat';
        
        if (content.includes('google') && content.includes('review')) {
          conversationStage = 'ask_for_review';
          devLog('🎯 Detected: Asking for Google review');
        } else if (content.includes('what went wrong') || content.includes('tell us')) {
          conversationStage = 'collecting_feedback';
          devLog('🎯 Detected: Collecting feedback');
        }
        
        if (conversationStage !== 'normal_chat') {
          devLog('📋 Survey stage:', conversationStage);
          
          const response = await fetch('/api/chat-survey', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              receipt_id: selectedReceipt.id,
              customer_response: message,
              conversation_stage: conversationStage
            })
          });
          
          const data = await response.json();
          devLog('✅ Survey API response:', data);
        } else {
          // Normal chat
          devLog('💬 Normal chat mode - calling chat API');
          
          // CRITICAL: Refresh inventory units before sending to AI
          // selectedReceipt may have stale data if items arrived or went out for delivery since it was loaded
          let freshReceipt = { ...selectedReceipt };
          try {
            const receiptItemIds = (selectedReceipt.items || []).map(item => item.id).filter(Boolean);
            
            // PATH 1: Fetch reserved units
            const { data: reservedUnits } = await supabase
              .from('inventory_units')
              .select('*')
              .eq('reserved_for_receipt_id', selectedReceipt.id);
            
            // PATH 2: Fetch delivered/linked units by receipt_item_id
            let deliveredUnits = [];
            if (receiptItemIds.length > 0) {
              const { data: linkedUnits } = await supabase
                .from('inventory_units')
                .select('*')
                .in('receipt_item_id', receiptItemIds);
              deliveredUnits = linkedUnits || [];
            }
            
            // Merge and dedupe
            const allUnitsMap = new Map();
            (reservedUnits || []).forEach(u => allUnitsMap.set(u.id, u));
            deliveredUnits.forEach(u => allUnitsMap.set(u.id, u));
            
            // PATH 3: Fetch by SKU + store for out_for_delivery/at_store units 
            // that may not have reserved_for_receipt_id set
            const storeId = selectedReceipt.store_id;
            const itemSkus = [...new Set(
              (selectedReceipt.items || [])
                .map(item => item.sku)
                .filter(Boolean)
            )];
            
            if (storeId && itemSkus.length > 0) {
              for (const sku of itemSkus) {
                try {
                  const { data: skuUnits } = await supabase
                    .from('inventory_units')
                    .select('*')
                    .ilike('sku', sku)
                    .eq('store_id', storeId)
                    .in('status', ['out_for_delivery', 'at_store', 'in_warehouse', 'reserved'])
                    .limit(5);
                  
                  (skuUnits || []).forEach(unit => {
                    if (!allUnitsMap.has(unit.id)) {
                      // Only add if unreserved or reserved for this receipt
                      const unitReserved = unit.reserved_for_receipt_id;
                      if (!unitReserved || unitReserved === selectedReceipt.id) {
                        allUnitsMap.set(unit.id, unit);
                        devLog('🔍 PATH 3 found unit:', unit.id.substring(0,8), unit.sku, unit.status);
                      }
                    }
                  });
                } catch (skuErr) {
                  devError('PATH 3 SKU fetch error:', skuErr);
                }
              }
            }
            
            freshReceipt.inventoryUnits = Array.from(allUnitsMap.values());
            
            // Also refresh the receipt's delivery status
            const { data: freshReceiptData } = await supabase
              .from('receipts')
              .select('delivery_status')
              .eq('id', selectedReceipt.id)
              .single();
            if (freshReceiptData) {
              freshReceipt.deliveryStatus = freshReceiptData.delivery_status;
              freshReceipt.delivery_status = freshReceiptData.delivery_status;
            }
            
            devLog('🔄 Refreshed inventory for chat:', freshReceipt.inventoryUnits.length, 'units');
          } catch (refreshErr) {
            devError('Failed to refresh inventory for chat, using stale data:', refreshErr);
          }
          
          const response = await fetch('/api/chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message: message,
              receipt: freshReceipt,
              conversationHistory: selectedReceipt.messages || []
            })
          });
          
          const data = await response.json();
          devLog('✅ Chat API response:', data.message?.substring(0, 100));
          
          // Handle escalation if AI flagged it
          if (data.escalation_needed) {
            devLog('🚨 ESCALATION DETECTED:', data.escalation_reason);
            
            // Add to escalations state with blinking notification
            setEscalations(prev => ({
              ...prev,
              [selectedReceipt.id]: {
                reason: data.escalation_reason,
                timestamp: new Date(),
                acknowledged: false,
                customerName: selectedReceipt.customerName || selectedReceipt.customer_name
              }
            }));
            
            // Play urgent notification sound (double beep)
            if (notificationSound && notificationAudioRef.current) {
              notificationAudioRef.current.currentTime = 0;
              notificationAudioRef.current.play().catch(e => devLog('Audio play failed:', e));
              // Play again after short delay for urgency
              setTimeout(() => {
                if (notificationAudioRef.current) {
                  notificationAudioRef.current.currentTime = 0;
                  notificationAudioRef.current.play().catch(e => devLog('Audio play failed:', e));
                }
              }, 300);
            }
            
            // Show urgent toast notification
            const escalationNotification = {
              id: Date.now(),
              title: '🚨 ESCALATION NEEDED',
              message: `${selectedReceipt.customerName || selectedReceipt.customer_name}: ${data.escalation_reason}`,
              receiptId: selectedReceipt.id,
              isEscalation: true
            };
            setNotifications(prev => [escalationNotification, ...prev]);
            
            // Browser notification with urgency
            if (browserNotifications && Notification.permission === 'granted') {
              const notification = new Notification('🚨 URGENT: Customer Escalation', {
                body: `${selectedReceipt.customerName || selectedReceipt.customer_name}: ${data.escalation_reason}`,
                icon: '/favicon.ico',
                requireInteraction: true, // Won't auto-dismiss
                tag: `escalation-${selectedReceipt.id}`
              });
            }
          }
        }
      }
      
      // 6. Reload messages after a short delay
      devLog('🔄 Reloading receipt details in 1 second...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      await loadReceiptDetails(selectedReceipt.id);
      devLog('✅ Receipt reloaded');
      
    } catch (error) {
      devError('❌ Error:', error);
      addNotification({ title: 'Error', message: error.message, isEscalation: true });
    }
  };

  // Calculate stats for dashboard (simplified to match actual status flow)
  const stats = {
    pending: receipts.filter(r => (r.deliveryStatus || r.delivery_status) === 'pending').length,
    out_for_delivery: receipts.filter(r => {
      const status = r.deliveryStatus || r.delivery_status;
      return status === 'out_for_delivery' || status === 'partial_out_for_delivery';
    }).length,
    delivered: receipts.filter(r => {
      const status = r.deliveryStatus || r.delivery_status;
      return ITEM_TERMINAL_STATUSES.includes(status);
    }).length,
    // Ready to deliver - checks inventory_units for items that are ready
    readyToDeliver: receipts.filter(r => {
      const status = r.deliveryStatus || r.delivery_status;
      if (ITEM_TERMINAL_STATUSES.includes(status)) return false;
      
      // Check if any inventory units are already delivered/picked_up
      const inventoryUnits = r.inventoryUnits || [];
      const hasCompletedUnits = inventoryUnits.some(u => UNIT_TERMINAL_STATUSES.includes(u.status));
      if (hasCompletedUnits) return false;
      
      // Check item statuses
      const itemStatuses = r.itemStatuses || [];
      if (itemStatuses.length === 0) return false;
      
      // Check that no items are already delivered/picked_up AND all are ready
      const hasCompletedItems = itemStatuses.some(i => ITEM_TERMINAL_STATUSES.includes(i.status));
      if (hasCompletedItems) return false;
      
      return itemStatuses.every(i => 
        i.status === 'at_store' || i.status === 'in_warehouse'
      );
    }).length
  };

  // ==========================================
  // SUPABASE AUTH FUNCTIONS
  // ==========================================

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: authEmail,
        password: authPassword,
      });
      
      if (error) throw error;
    } catch (error) {
      setAuthError(error.message);
    } finally {
    // Handle storing credentials with the Credential Management API
    if ('credentials' in navigator && window.PasswordCredential) {
      if (!authError && authEmail && authPassword) {
        try {
          const cred = new window.PasswordCredential({
            id: authEmail,
            password: authPassword
          });
          void navigator.credentials.store(cred).catch((e) => {
            console.error('Error storing credentials:', e);
          });
        } catch (e) {
          console.error('Error storing credentials:', e);
        }
      }
    }
    }
  };

  // Self sign-up is disabled. Accounts are created by Exitium per store.
  // Kept as a handler so nothing that references it breaks, but it never
  // calls supabase.auth.signUp; the Supabase project setting "Allow new
  // users to sign up" should be OFF as well, since the sign-up API is
  // reachable with the public key regardless of this UI.
  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthError('Accounts are created by Exitium. Contact support to get access.');
    setAuthMode('login');
  };

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const openLogoutConfirm = () => setLogoutConfirmOpen(true);
  const cancelLogout = () => {
    if (isLoggingOut) return;
    setLogoutConfirmOpen(false);
  };

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      try {
        await removePushSubscriptionOnLogout();
      } catch (e) {
        devError('Push cleanup on logout:', e);
      }
      try {
        await supabase.auth.signOut();
      } catch (e) {
        devError('signOut failed:', e);
      }
      await clearSiteStorageAndCaches();
      // Reset all user-specific state
      setSession(null);
      setCurrentStore(null);
      setStores([]);
      setReceipts([]);
      setInvoices([]);
      setAllInventoryUnits([]);
      setStoreSettings({ tax_rate: 8.00 });
      setStoreAddresses([]);
      setEscalations({});
      setUnreadMessages({});
      setNotifications([]);
      setLogoutConfirmOpen(false);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // ==========================================
  // RETURN ALL STATE AND HANDLERS
  // ==========================================
  return {
    isPaid: currentStore?.payment_status,
    // URL State
    searchParams,
    setSearchParams,
    
    // Receipts
    receipts,
    setReceipts,
    selectedReceipt,
    setSelectedReceipt,
    expandedSkuGroups,
    setExpandedSkuGroups,
    showReceiptModal,
    setShowReceiptModal,
    isEditingInModal,
    setIsEditingInModal,
    editFormData,
    setEditFormData,
    showUnsavedChangesWarning,
    setShowUnsavedChangesWarning,
    showDeleteWarning,
    setShowDeleteWarning,
    deleteTargetId,
    setDeleteTargetId,
    staffMessage,
    setStaffMessage,
    staffImage,
    setStaffImage,
    isStaffMode,
    setIsStaffMode,
    activeTab,
    setActiveTab,
    
    // Loading states
    sendingMessage,
    setSendingMessage,
    loading,
    setLoading,
    searchQuery,
    setSearchQuery,
    receiptPeriodFilter,
    setReceiptPeriodFilter,
    receiptSelectedMonth,
    setReceiptSelectedMonth,
    isSavingReceipt,
    setIsSavingReceipt,
    isSendingSMS,
    setIsSendingSMS,
    sendingSMSReceiptId,
    setSendingSMSReceiptId,
    smsSentReceipts,
    setSmsSentReceipts,
    isDeletingReceipt,
    setIsDeletingReceipt,
    isSavingEdit,
    setIsSavingEdit,
    isBulkAdding,
    setIsBulkAdding,
    isTakingOver,
    setIsTakingOver,
    isReturningToAI,
    setIsReturningToAI,
    isMobileEditingDetails,
    setIsMobileEditingDetails,
    
    // PWA
    online,
    setOnline,
    showInstallPrompt,
    setShowInstallPrompt,
    isInstalled,
    setIsInstalled,
    
    // Scanner & Invoices
    showScanner,
    setShowScanner,
    invoices,
    setInvoices,
    activeView,
    setActiveView,
    tabVisibility,
    visibleTabCount,
    inventoryRefreshTrigger,
    setInventoryRefreshTrigger,
    selectedMobileInvoice,
    setSelectedMobileInvoice,
    isMobileChatOpen,
    setIsMobileChatOpen,
    
    // Partial Delivery
    showPartialDeliveryModal,
    setShowPartialDeliveryModal,
    partialDeliveryReceipt,
    setPartialDeliveryReceipt,
    selectedDeliveryItems,
    setSelectedDeliveryItems,
    isProcessingPartialDelivery,
    setIsProcessingPartialDelivery,
    isUndoingDelivery,
    setIsUndoingDelivery,
    selectedItemsForDelivery,
    setSelectedItemsForDelivery,
    isCreatingDeliveryFromReceipt,
    setIsCreatingDeliveryFromReceipt,
    
    // Store
    stores,
    setStores,
    currentStore,
    setCurrentStore,
    // SMS-AI emergency kill switch (store-level)
    smsAiPaused,
    togglingSmsAi,
    toggleSmsAiPaused,
    storeSettings,
    setStoreSettings,
    storeAddresses,
    setStoreAddresses,
    loadStoreAddresses,
    
    // Manufacturers
    manufacturers,
    loadManufacturers,
    createManufacturer,
    
    // Signatures
    showSignaturesDrawer,
    setShowSignaturesDrawer,
    signatures,
    setSignatures,
    loadingSignatures,
    setLoadingSignatures,
    
    // Inventory
    allInventoryUnits,
    setAllInventoryUnits,
    
    // Notifications
    notifications,
    setNotifications,
    unreadMessages,
    setUnreadMessages,
    totalUnread,
    setTotalUnread,
    notificationSound,
    setNotificationSound,
    browserNotifications,
    setBrowserNotifications,
    showStoreSettings,
    setShowStoreSettings,
    showSalesTaxExportModal,
    setShowSalesTaxExportModal,
    escalations,
    setEscalations,
    
    // Push Notifications
    pushSupported: pushNotifications.pushSupported,
    pushEnabled: pushNotifications.pushEnabled,
    pushLoading: pushNotifications.pushLoading,
    pushError: pushNotifications.pushError,
    togglePush: pushNotifications.togglePush,
    testPush: pushNotifications.testPush,
    
    // Auth
    session,
    setSession,
    authLoading,
    setAuthLoading,
    authEmail,
    setAuthEmail,
    authPassword,
    setAuthPassword,
    authError,
    setAuthError,
    authMode,
    setAuthMode,
    
    // Chat scroll
    userScrolledUp,
    setUserScrolledUp,
    
    // Create/Edit modal
    showModal,
    setShowModal,
    editingReceipt,
    setEditingReceipt,
    showBulkAddModal,
    setShowBulkAddModal,
    bulkSkuInput,
    setBulkSkuInput,
    bulkAddTarget,
    setBulkAddTarget,
    showBulkSetModal,
    setShowBulkSetModal,
    setName,
    setSetName,
    setPrice,
    setSetPrice,
    groupedItems,
    setGroupedItems,
    showGroupItemForm,
    setShowGroupItemForm,
    editingGroupItemIndex,
    setEditingGroupItemIndex,
    groupItemFormData,
    setGroupItemFormData,
    handleAddItemToGroup,
    handleAddInlineGroupRow,
    handleUpdateGroupItemInline,
    handleUpdateGroupItemFields,
    handleAddGroupItemsBulk,
    handleEditGroupItem,
    handleSaveGroupItem,
    handleRemoveGroupItem,
    handleCloseGroupItemForm,
    handleCloseGroupedItemsModal,
    expandedSets,
    setExpandedSets,
    
    // Payment History
    showPaymentForm,
    setShowPaymentForm,
    paymentFormData,
    setPaymentFormData,
    handleAddPayment,
    handleRemovePayment,
    
    formData,
    setFormData,
    
    // Refs
    notificationAudioRef,
    chatContainerRef,
    mobileChatContainerRef,
    lastMessageCountRef,
    initialScrollDoneRef,
    
    // Constants
    deliveryStatuses,
    
    // Computed values
    newOrdersReceipts,
    completedOrdersReceipts,
    activeReceipts,
    completedTabNotifications,
    stats,
    
    // Handler functions
    playNotificationSound,
    showBrowserNotification,
    addNotification,
    markReceiptAsRead,
    updateTotalUnread,
    requestNotificationPermission,
    handleChatScroll,
    loadReceipts,
    loadStoreSettings,
    loadPhoneAgentSettings,
    phoneAgentSettings,
    phoneAgentTabProps,
    loadEscalations,
    loadInventoryUnits,
    loadInvoices,
    loadReceiptDetails,
    normalizeStatus,
    getStatusColor,
    getStatusIcon,
    getItemReadinessByQuantity,
    // PERF: Precomputed readiness map keyed by receipt.id. Consumers should
    // prefer this over calling getItemReadinessByQuantity per-receipt in render.
    readinessByReceiptId,
    getItemStatusByInventory,
    getAllItemStatusesForReceipt,
    formatPhoneNumber,
    calculateTotals,
    explodeItems,
    getPaymentStatus,
    fetchSignatures,
    openSignaturesDrawer,
    handleAddItem,
    handleEditItem,
    handleSaveItemFromModal,
    handleCloseItemModal,
    showItemModal,
    setShowItemModal,
    editingItemIndex,
    setEditingItemIndex,
    itemFormData,
    setItemFormData,
    itemModalTarget,
    setItemModalTarget,
    handleBulkAddItems,
    handleAddSet,
    handleRemoveSet,
    handleEditSet,
    editingSetId,
    toggleSetExpansion,
    groupItemsForDisplay,
    getSetTotal,
    handleRemoveItem,
    handleItemChange,
    handleOpenModal,
    handleCloseModal,
    handleAttemptCloseModal,
    hasEditModalUnsavedChanges,
    confirmDiscardEditModalChanges,
    cancelDiscardEditModalChanges,
    showEditModalUnsavedWarning,
    setShowEditModalUnsavedWarning,
    handlePrintReceipt,
    handleShareReceipt,
    handleSaveReceipt,
    handleSendSMS,
    openPartialDeliveryModal,
    toggleDeliveryItem,
    handleUndoDelivery,
    handlePartialDelivery,
    handleMarkSingleItemDelivered,
    handleMarkUnitStatus,
    handleMarkItemReady,
    handleUndoReady,
    markingItemReady,
    // Per-item status-write busy map ({ [receiptItemId]: statusKey }) — feeds
    // the spinner inside the exact pill that was clicked on that row.
    itemStatusBusy,
    // True while the drawer is open but the full receipt (messages, units,
    // set fields) is still being fetched — drives in-drawer loading spinners.
    isLoadingReceiptDetails,
    toggleItemForDelivery,
    clearSelectedItemsForDelivery,
    handleCreateDeliveryFromReceipt,
    handleDeleteReceipt,
    handleAttemptDeleteReceipt,
    confirmDeleteReceipt,
    cancelDeleteReceipt,
    handleOpenReceipt,
    handleViewReceipt,
    handleCloseReceiptModal,
    handleAttemptCloseReceiptModal,
    hasUnsavedChanges,
    confirmDiscardChanges,
    cancelDiscardChanges,
    handleSaveEditInModal,
    handleCancelEditInModal,
    handleTakeOverChat,
    handleReturnToAI,
    handleToggleChatMode,
    togglingChatModeId,
    filterReceiptsBySearch,
    handleSendStaffMessage,
    simulateCustomerMessage,
    handleLogin,
    handleSignup,
    logoutConfirmOpen,
    isLoggingOut,
    openLogoutConfirm,
    confirmLogout,
    cancelLogout,
  };
};

export default useReceiptDashboard;